import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { AppContext } from '../../app';
import type { Director } from '../../director/Director';
import type { SurfaceDefinition } from '../../surface/Surface';
import type { SignedEnvelope } from '../../envelope/Envelope';
import { IncidentManager, type IncidentMeta, type GeoPoint } from '../../incidents/IncidentManager';
import { FederationManager } from '../../federation/FederationManager';
import { MeshManager } from '../../mesh/MeshManager';
import { MembershipManager } from '../../membership/MembershipManager';
import { CallManager } from '../../calls/CallManager';
import { t } from '../../i18n/strings';
import {
  QrAccumulator,
  type DecodeProgress,
  encodeBundle,
} from '../../sneakernet/QrCodec';
import type { CallHistoryRecord, FederatedIncidentRecord } from '../../storage/schema';
import {
  INCIDENT_INTAKE_FUNNEL_ID,
  IncidentRouter,
  IntakeValidationError,
} from '../../incidents/IncidentRouter';
import { InMemorySnapshotStorage, RecoveryManager } from '../../recovery/RecoveryManager';
import { ThreadManager, type MessageCacheRow } from '../../threads/ThreadManager';
import { getSocialGroupManager } from '../../social/SocialGroupManager';
import type {
  SocialGroupKind,
  SocialGroupRecord,
  SocialPostPayload,
  SocialVisibility,
} from '../../storage/schema';
import { getPersonalHelper } from '../../ai/PersonalHelper';
import type { ChatMessage as PersonalChatMessage, HelperState } from '../../ai/types';
import { getMascotManager } from '../../moderation/MascotManager';
import {
  MASCOT_CATEGORIES,
  defaultKeywordsFor,
  type MascotCategory,
} from '../../moderation/MascotClassifier';
import type {
  MascotAction,
  MascotSettings,
  MascotState,
  FilterLogEntry,
} from '../../moderation/MascotManager';
import { getNewsHeadlinesManager } from '../../news/NewsHeadlinesManager';
import type { NewsHeadlineRecord } from '../../storage/schema';
import { ShellPicker } from '../shells/ShellPicker';
import { useShell } from '../shells/ShellProvider';

// Module-scoped state — small bag of cross-surface params the director's
// gesture API doesn't carry. Persisted to localStorage so the selected
// incident survives reload.
const SELECTED_INCIDENT_KEY = 'alds-selected-incident';
function setSelectedIncident(incidentId: string, threadId: string): void {
  try {
    localStorage.setItem(SELECTED_INCIDENT_KEY, JSON.stringify({ incidentId, threadId }));
  } catch { /* noop */ }
}
function getSelectedIncident(): { incidentId: string; threadId: string } | null {
  try {
    const raw = localStorage.getItem(SELECTED_INCIDENT_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as { incidentId: string; threadId: string };
  } catch { return null; }
}

/**
 * Surface renderer.
 *
 * ALDS surface map (see docs/alds-surface-map.md):
 *   - hub                — role picker (caller / dispatcher / responder / supervisor)
 *   - env-intake         — caller's anonymous report funnel
 *   - env-board          — dispatcher's incident board
 *   - env-roster         — responder's assignments + on-shift toggle
 *   - env-audit          — supervisor / public transparency view
 *   - env-chat           — backing primitive: an incident's coordination thread
 *   - env-vault          — backing primitive: evidence storage
 *   - env-voice          — reserved for v2 voice/video work
 *
 * Transients: incident-report, triage, assign-responder, responder-accept,
 * responder-status, attach-evidence, request-backup, share-incident-out,
 * compose-message, establish-call.
 */

export interface SurfaceProps {
  ctx: AppContext;
  director: Director;
  signAndStore: (surfaceId: string, contentType: string, payload: unknown)
    => Promise<SignedEnvelope>;
}

const ROLE_STORAGE_KEY = 'alds-role';
type Role = 'caller' | 'dispatcher' | 'responder' | 'supervisor';

const ROLE_TO_SURFACE: Record<Role, string> = {
  caller: 'env-intake',
  dispatcher: 'env-board',
  responder: 'env-roster',
  supervisor: 'env-audit',
};

export function renderSurface(def: SurfaceDefinition, props: SurfaceProps): JSX.Element {
  switch (def.id) {
    case 'hub':                return <Hub {...props} />;
    case 'env-intake':         return <IntakeEnv {...props} />;
    case 'env-board':          return <BoardEnv {...props} />;
    case 'env-roster':         return <RosterEnv {...props} />;
    case 'env-audit':          return <AuditEnv {...props} />;
    case 'env-chat':           return <ChatEnv {...props} />;
    case 'env-voice':          return <VoiceEnv {...props} />;
    case 'env-vault':          return <VaultEnv {...props} />;
    case 'incident-report':    return <IncidentReport {...props} />;
    case 'triage':             return <Triage {...props} />;
    case 'assign-responder':   return <AssignResponder {...props} />;
    case 'responder-accept':   return <ResponderAccept {...props} />;
    case 'responder-status':   return <ResponderStatus {...props} />;
    case 'attach-evidence':    return <AttachEvidence {...props} />;
    case 'request-backup':     return <RequestBackup {...props} />;
    case 'share-incident-out': return <ShareIncidentOut {...props} />;
    case 'mesh-invite-generate': return <MeshInviteGenerate {...props} />;
    case 'mesh-invite-redeem':   return <MeshInviteRedeem {...props} />;
    case 'sneakernet':           return <Sneakernet {...props} />;
    case 'qr-handoff':           return <QrHandoff {...props} />;
    case 'compose-message':    return <ComposeMessage {...props} />;
    case 'establish-call':     return <EstablishCall {...props} />;
    case 'call-incoming':      return <CallIncoming {...props} />;
    case 'call-active':        return <CallActive {...props} />;
    case 'call-history':       return <CallHistoryView {...props} />;
    case 'media-controls':     return <MediaControlsBar {...props} />;
    case 'treaty-builder':     return <TreatyBuilder {...props} />;
    case 'bulletin-config':    return <BulletinConfig {...props} />;
    case 'bulletin-published': return <BulletinPublished {...props} />;
    case 'plugin-picker':      return <PluginPicker {...props} />;
    case 'key-storage-config': return <KeyStorageConfig {...props} />;
    case 'network-config':     return <NetworkConfigSurface {...props} />;
    case 'env-social':         return <SocialEnv {...props} />;
    case 'social-feed':        return <SocialFeed {...props} />;
    case 'social-channel':     return <SocialChannel {...props} />;
    case 'social-broadcast':   return <SocialBroadcast {...props} />;
    case 'social-gallery':     return <SocialGallery {...props} />;
    case 'social-stories':     return <SocialStories {...props} />;
    case 'social-compose':     return <SocialCompose {...props} />;
    case 'social-group-config': return <SocialGroupConfig {...props} />;
    case 'social-dm':          return <SocialDm {...props} />;
    case 'contact-picker':     return <ContactPicker {...props} />;
    case 'group-call-incoming': return <GroupCallIncoming {...props} />;
    case 'group-call-active':  return <GroupCallActive {...props} />;
    case 'call-roster':        return <CallRoster {...props} />;
    case 'plugin-marketplace': return <PluginMarketplace {...props} />;
    case 'audit-anchor':       return <AuditAnchor {...props} />;
    case 'federated-search':   return <FederatedSearch {...props} />;
    case 'sass-chats':         return <SASSChats {...props} />;
    case 'sass-more':          return <SASSMore {...props} />;
    case 'sass-audit-verify':  return <SASSAuditVerify {...props} />;
    case 'sass-prepublish-vote': return <SASSPrepublishVote {...props} />;
    case 'sass-ble-status':      return <SASSBleStatus {...props} />;
    case 'sass-sms-subscribers': return <SASSSmsSubscribers {...props} />;
    case 'sass-key-recovery-setup':   return <SASSKeyRecoverySetup {...props} />;
    case 'sass-key-recovery-restore': return <SASSKeyRecoveryRestore {...props} />;
    case 'personal-ai-chat':          return <PersonalAIChat {...props} />;
    case 'sass-surface-map':          return <SASSSurfaceMap {...props} />;
    case 'sass-mascot':               return <SASSMascot {...props} />;
    case 'news-paper':                return <NewsPaper {...props} />;
    case 'sass-shell-chooser':        return <SASSShellChooser {...props} />;
    case 'shell-home':                return <ShellHome {...props} />;
    default:
      return <div className="surface-q t-serif">Unknown surface · {def.id}</div>;
  }
}

function SASSShellChooser({ ctx, director }: SurfaceProps): JSX.Element {
  return (
    <EnvFrame title="choose your shell" glyph="◫" color="var(--phos)" director={director}>
      <ShellPicker ctx={ctx} />
    </EnvFrame>
  );
}

/**
 * Generic shell-home dispatch. Renders the active shell's Home
 * component. Falls back to the hub role-picker if the active shell
 * doesn't override (which is the adult-hub no-op default).
 */
function ShellHome({ ctx, director, signAndStore }: SurfaceProps): JSX.Element {
  const { shell } = useShell();
  if (shell.Home) {
    const Home = shell.Home;
    return <Home ctx={ctx} />;
  }
  // Fallback: route to hub the same way SurfaceStack's role-init does.
  return <Hub ctx={ctx} director={director} signAndStore={signAndStore} />;
}

// ─── HUB — role picker ────────────────────────────────────

const ROLE_CARDS: { role: Role; glyph: string; color: string }[] = [
  { role: 'caller',     glyph: '☎', color: 'var(--phos)'  },
  { role: 'dispatcher', glyph: '◆', color: 'var(--amber)' },
  { role: 'responder',  glyph: '◉', color: 'var(--blue)'  },
  { role: 'supervisor', glyph: '◈', color: 'var(--pink)'  },
];

const FIRST_RUN_KEY = 'alds-onboarding-seen-v1';

function Hub({ director }: SurfaceProps): JSX.Element {
  const pickRole = (role: Role) => {
    try { localStorage.setItem(ROLE_STORAGE_KEY, role); } catch { /* noop */ }
    director.bridge(ROLE_TO_SURFACE[role], `assume ${role} role`);
  };
  // First-run callout — appears once per device. Explains the four tabs
  // visible at the bottom of every screen. Dismissed on first tap.
  const [showCallout, setShowCallout] = useState<boolean>(() => {
    try { return localStorage.getItem(FIRST_RUN_KEY) === null; }
    catch { return false; }
  });
  const dismissCallout = () => {
    try { localStorage.setItem(FIRST_RUN_KEY, new Date().toISOString()); } catch { /* noop */ }
    setShowCallout(false);
  };
  return (
    <>
      {showCallout && (
        <div className="onboarding-callout schematic-frame">
          <div className="onboarding-row">
            <div className="onboarding-text">
              <span className="t-meta env-sublabel">welcome</span>
              <span> · use the tabs at the bottom: </span>
              <strong>✦ Chats</strong> <strong>◐ Feed</strong> <strong>◆ Dispatch</strong> <strong>≡ More</strong>
            </div>
            <button className="pill onboarding-dismiss" onClick={dismissCallout} aria-label="dismiss welcome">✕</button>
          </div>
        </div>
      )}
      <h2 className="surface-q t-serif">{t('hub.title')}</h2>
      <p className="surface-sub">{t('hub.subtitle')}</p>
      <div className="hub-grid" aria-live="polite">
        {ROLE_CARDS.map((r) => (
          <div
            key={r.role}
            className="env-card"
            role="button"
            tabIndex={0}
            aria-label={t(`role.${r.role}`)}
            style={{ ['--env-color' as string]: r.color } as React.CSSProperties}
            onClick={() => pickRole(r.role)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') pickRole(r.role); }}
          >
            <span className="env-glyph" aria-hidden="true">{r.glyph}</span>
            <div className="env-label">{t(`role.${r.role}`)}</div>
            <div className="env-desc">{t(`role.${r.role}.desc`)}</div>
          </div>
        ))}
      </div>
      <div className="t-meta env-sublabel" style={{ marginTop: 24 }}>{t('hub.meshSectionTitle')}</div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button className="pill" onClick={() => director.spawn('mesh-invite-generate', 'invite')}>{t('hub.inviteNeighbor')}</button>
        <button className="pill" onClick={() => director.spawn('mesh-invite-redeem', 'redeem')}>{t('hub.redeemInvite')}</button>
        <button className="pill" onClick={() => director.spawn('sneakernet', 'bundle')}>{t('hub.sneakernetBundle')}</button>
        <button className="pill" onClick={() => director.spawn('network-config', 'network config')}>network config</button>
      </div>
      <SurfaceStyles />
      <style>{`
        .onboarding-callout {
          padding: 8px 12px;
          margin-bottom: 10px;
          border-color: var(--phos);
          background: rgba(120, 220, 140, 0.05);
        }
        .onboarding-row {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .onboarding-text {
          flex: 1;
          min-width: 0;
          font-size: 11px;
          line-height: 1.4;
          color: var(--ink);
        }
        .onboarding-text strong {
          color: var(--phos);
          margin-right: 6px;
          font-weight: normal;
          letter-spacing: 0.04em;
        }
        .onboarding-dismiss {
          flex-shrink: 0;
          padding: 4px 8px !important;
          font-size: 12px !important;
        }
        @media (max-width: 600px) {
          .onboarding-text { font-size: 10px; }
        }
      `}</style>
    </>
  );
}

// ─── env-intake — caller report flow ─────────────────────

function IntakeEnv({ director }: SurfaceProps): JSX.Element {
  return (
    <EnvFrame title={t('intake.title')} glyph="☎" color="var(--phos)" director={director}>
      <p className="surface-sub" style={{ marginBottom: 12 }}>
        {t('intake.subtitle')}
      </p>
      <div className="t-meta env-sublabel">your reports · 0 this session</div>
      <div
        className="item-row"
        role="button"
        tabIndex={0}
        aria-label={t('intake.newReport')}
        style={{ ['--env-color' as string]: 'var(--phos)' } as React.CSSProperties}
        onClick={() => director.push('incident-report', 'new report')}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') director.push('incident-report', 'new report'); }}
      >
        <div>
          <div className="item-main">{t('intake.newReport')}</div>
          <div className="item-meta">{t('intake.emptyHint')}</div>
        </div>
        <div className="item-meta" aria-hidden="true">→</div>
      </div>
      <SurfaceStyles />
    </EnvFrame>
  );
}

// ─── env-board — dispatcher incident list ────────────────

function BoardEnv({ ctx, director }: SurfaceProps): JSX.Element {
  const [tab, setTab] = useState<'mine' | 'federated'>('mine');
  const incidents = useIncidents(ctx);
  const federated = useFederatedIncidents(ctx);
  return (
    <EnvFrame title="Incident Board" glyph="◆" color="var(--amber)" director={director}>
      <div className="pill-row" role="tablist" aria-label="board scope">
        <button
          className={`pill ${tab === 'mine' ? 'pill-on' : ''}`}
          role="tab"
          aria-selected={tab === 'mine'}
          onClick={() => setTab('mine')}
        >mine · {incidents.length}</button>
        <button
          className={`pill ${tab === 'federated' ? 'pill-on' : ''}`}
          role="tab"
          aria-selected={tab === 'federated'}
          onClick={() => setTab('federated')}
        >federated · {federated.length}</button>
      </div>

      {tab === 'mine' && (
        <>
          <div className="t-meta env-sublabel" style={{ marginTop: 12 }}>open + active · {incidents.length}</div>
          {incidents.length === 0 && (
            <div className="schematic-frame" style={{ padding: 14, marginTop: 12 }}>
              <div className="t-serif" style={{ fontSize: 15, marginBottom: 6 }}>You&apos;re ready to dispatch</div>
              <div className="surface-sub" style={{ marginBottom: 10 }}>
                Reports from neighbors will appear here. As dispatcher you can triage
                each one, assign it to a responder, or escalate to the supervisor.
              </div>
              <div className="t-meta" style={{ color: 'var(--ink-dim)' }}>
                The board updates every 2 seconds. Federated reports from neighboring
                neighborhoods show up under the &quot;federated&quot; tab.
              </div>
            </div>
          )}
          {incidents.map((inc) => (
            <div
              key={inc.incidentId}
              className="item-row"
              style={{ ['--env-color' as string]: 'var(--amber)' } as React.CSSProperties}
              onClick={() => {
                setSelectedIncident(inc.incidentId, inc.threadId);
                director.spawn('triage', `triage ${inc.incidentId.slice(0, 8)}`);
              }}
            >
              <div>
                <div className="item-main">
                  <span className={`sev-pill sev-${inc.confirmedSeverity ?? inc.proposedSeverity}`} />
                  {inc.category} · {inc.incidentId.slice(0, 8)}
                  {inc.membershipProof && (
                    <span
                      className="enc-badge"
                      style={{
                        marginLeft: 8,
                        borderColor: inc.membershipProof.verified ? 'var(--phos)' : 'var(--amber)',
                        color: inc.membershipProof.verified ? 'var(--phos)' : 'var(--amber)',
                      }}
                      title={inc.membershipProof.verified ? 'verified neighborhood member' : 'membership proof failed verification'}
                    >
                      {inc.membershipProof.verified ? '✓ VERIFIED' : '? UNVERIFIED'}
                    </span>
                  )}
                </div>
                <div className="item-meta">
                  {inc.status} · {ago(inc.createdAt)}
                </div>
              </div>
              <div className="item-meta">→ triage</div>
            </div>
          ))}
        </>
      )}

      {tab === 'federated' && (
        <>
          <div className="t-meta env-sublabel" style={{ marginTop: 12 }}>forwarded from federated neighborhoods</div>
          {federated.length === 0 && (
            <div className="empty-state t-meta">none — link a neighborhood from env-audit to start receiving</div>
          )}
          {federated.map((f) => (
            <div
              key={f.incidentId}
              className="item-row"
              style={{ ['--env-color' as string]: 'var(--blue)' } as React.CSSProperties}
            >
              <div>
                <div className="item-main">
                  <span className="enc-badge" style={{ marginRight: 8 }}>FED</span>
                  {f.incidentId.slice(0, 8)} · from {f.sourceNeighborhoodId.slice(0, 10)}…
                </div>
                <div className="item-meta">
                  {ago(f.forwardedAt)}{f.declined ? ' · declined' : ''}
                </div>
              </div>
              <div className="item-meta">
                {f.declined ? '—' : 'incoming'}
              </div>
            </div>
          ))}
        </>
      )}
      <SurfaceStyles />
    </EnvFrame>
  );
}

// ─── env-roster — responder view ─────────────────────────

function RosterEnv({ ctx, director }: SurfaceProps): JSX.Element {
  const [onShift, setOnShift] = useState(false);
  const [pending, setPending] = useState<{ incidentId: string; assignedAt: string; meta?: IncidentMeta }[]>([]);
  const [active, setActive] = useState<{ incidentId: string; acceptedAt?: string; meta?: IncidentMeta }[]>([]);

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      const im = new IncidentManager(ctx);
      const [list, myPub, pend, acc] = await Promise.all([
        im.listOnShift(),
        im.ownResponderPubkey(),
        im.listMyAssignments({ status: 'pending' }),
        im.listMyAssignments({ status: 'accepted' }),
      ]);
      if (cancelled) return;
      setOnShift(list.some((r) => r.pubkey === myPub));
      const metas = await Promise.all([...pend, ...acc].map((a) => im.getIncident(a.incidentId)));
      const metaById = new Map(metas.filter(Boolean).map((m) => [m!.incidentId, m!]));
      setPending(pend.map((p) => {
        const meta = metaById.get(p.incidentId);
        return { incidentId: p.incidentId, assignedAt: p.assignedAt, ...(meta ? { meta } : {}) };
      }));
      setActive(acc.map((a) => {
        const meta = metaById.get(a.incidentId);
        return { incidentId: a.incidentId, ...(a.acceptedAt !== undefined ? { acceptedAt: a.acceptedAt } : {}), ...(meta ? { meta } : {}) };
      }));
    };
    void tick();
    const id = setInterval(() => void tick(), 1500);
    return () => { cancelled = true; clearInterval(id); };
  }, [ctx]);

  const toggle = async () => {
    const im = new IncidentManager(ctx);
    await im.setOwnShift(!onShift);
    setOnShift((s) => !s);
  };

  return (
    <EnvFrame title="My Assignments" glyph="◉" color="var(--blue)" director={director}>
      <div className="t-meta env-sublabel">shift status</div>
      <div
        className="item-row"
        style={{ ['--env-color' as string]: 'var(--blue)' } as React.CSSProperties}
        onClick={() => void toggle()}
      >
        <div>
          <div className="item-main">
            <span className={`status-dot status-${onShift ? 'online' : 'offline'}`} />
            {onShift ? 'on shift' : 'off shift'}
          </div>
          <div className="item-meta">tap to toggle</div>
        </div>
      </div>

      <div className="t-meta env-sublabel" style={{ marginTop: 18 }}>active assignments · {active.length}</div>
      {active.length === 0 && (
        <div className="surface-sub" style={{ padding: '8px 0' }}>
          You don&apos;t have any active dispatches yet. {onShift
            ? 'When dispatch assigns one to you, it shows here with a thread for live coordination.'
            : 'Toggle on-shift above to start receiving assignments.'}
        </div>
      )}
      {active.map((a) => (
        <div
          key={a.incidentId}
          className="item-row"
          style={{ ['--env-color' as string]: 'var(--phos)' } as React.CSSProperties}
          onClick={() => {
            if (a.meta) setSelectedIncident(a.incidentId, a.meta.threadId);
            director.bridge('env-chat', `incident ${a.incidentId.slice(0, 8)}`);
          }}
        >
          <div>
            <div className="item-main">
              {a.meta && <span className={`sev-pill sev-${a.meta.confirmedSeverity ?? a.meta.proposedSeverity}`} />}
              {a.meta?.category ?? 'incident'} · {a.incidentId.slice(0, 8)}
            </div>
            <div className="item-meta">accepted {a.acceptedAt ? ago(a.acceptedAt) : '—'}</div>
          </div>
          <div className="item-meta">→ thread</div>
        </div>
      ))}

      <div className="t-meta env-sublabel" style={{ marginTop: 18 }}>pending dispatches · {pending.length}</div>
      {pending.length === 0 && <div className="empty-state t-meta">no pending</div>}
      {pending.map((p) => (
        <div
          key={p.incidentId}
          className="item-row"
          style={{ ['--env-color' as string]: 'var(--amber)' } as React.CSSProperties}
          onClick={() => {
            if (p.meta) setSelectedIncident(p.incidentId, p.meta.threadId);
            director.spawn('responder-accept', `accept ${p.incidentId.slice(0, 8)}`);
          }}
        >
          <div>
            <div className="item-main">
              {p.meta && <span className={`sev-pill sev-${p.meta.confirmedSeverity ?? p.meta.proposedSeverity}`} />}
              {p.meta?.category ?? 'incident'} · {p.incidentId.slice(0, 8)}
            </div>
            <div className="item-meta">{ago(p.assignedAt)} · pending</div>
          </div>
          <div className="item-meta">→ accept</div>
        </div>
      ))}
      <SurfaceStyles />
    </EnvFrame>
  );
}

// ─── env-audit — supervisor / transparency ───────────────

function AuditEnv({ ctx, director }: SurfaceProps): JSX.Element {
  const [recent, setRecent] = useState<{ occurredAt: string; action: string; details: Record<string, unknown> }[]>([]);
  const [counts, setCounts] = useState({ envelopes: 0, incidents: 0 });
  const [exportStatus, setExportStatus] = useState<'idle' | 'exporting' | 'copied' | 'error'>('idle');
  const [exportPreview, setExportPreview] = useState('');
  const [exportError, setExportError] = useState('');

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      const db = ctx.storage.db;
      const audits = await db.audit_log.orderBy('seq').reverse().limit(15).toArray();
      if (cancelled) return;
      setRecent(audits.map((r) => ({
        occurredAt: r.occurredAt,
        action: r.action,
        details: r.details,
      })));
      const [env, inc] = await Promise.all([
        db.envelopes.count(),
        db.incident_meta.count(),
      ]);
      if (cancelled) return;
      setCounts({ envelopes: env, incidents: inc });
    };
    void tick();
    const id = setInterval(() => void tick(), 2000);
    return () => { cancelled = true; clearInterval(id); };
  }, [ctx]);

  const exportLog = async () => {
    setExportStatus('exporting'); setExportError('');
    try {
      const im = new IncidentManager(ctx);
      const log = await im.exportDayLog();
      const json = JSON.stringify(log, null, 2);
      try {
        if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(json);
        }
      } catch { /* clipboard may be unavailable; preview still works */ }
      setExportPreview(json.slice(0, 600) + (json.length > 600 ? '\n…' : ''));
      setExportStatus('copied');
    } catch (e) {
      setExportStatus('error');
      setExportError(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <EnvFrame title="Audit & Transparency" glyph="◈" color="var(--pink)" director={director}>
      <div className="t-meta env-sublabel">storage</div>
      <div className="row"><span className="key">envelopes</span><span className="val">{counts.envelopes}</span></div>
      <div className="row"><span className="key">incidents</span><span className="val">{counts.incidents}</span></div>

      <div style={{ marginTop: 14, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button className="cta" onClick={() => void exportLog()} disabled={exportStatus === 'exporting'}>
          {exportStatus === 'exporting' ? 'exporting…'
           : exportStatus === 'copied' ? 'copied ✓'
           : 'export day log (redacted)'}
        </button>
        <button className="cta secondary" onClick={() => director.spawn('sneakernet', 'bundle')}>sneakernet bundle</button>
        <button className="cta secondary" onClick={() => director.spawn('mesh-invite-generate', 'invite')}>invite a peer</button>
      </div>

      {exportStatus === 'copied' && (
        <div className="receipt-mini">
          <div className="t-meta">redacted JSON preview (first 600 chars; full copy on clipboard)</div>
          <pre style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--ink)', whiteSpace: 'pre-wrap', wordBreak: 'break-word', marginTop: 6, maxHeight: 200, overflow: 'auto' }}>
            {exportPreview}
          </pre>
        </div>
      )}
      {exportStatus === 'error' && (
        <div className="receipt-mini" style={{ borderColor: 'var(--amber)' }}>
          <div className="t-meta" style={{ color: 'var(--amber)' }}>export failed</div>
          <div style={{ marginTop: 4, fontSize: 11 }}>{exportError}</div>
        </div>
      )}

      <div className="t-meta env-sublabel" style={{ marginTop: 18 }}>recent signed actions · {recent.length}</div>
      {recent.length === 0 && <div className="empty-state t-meta">no audit entries yet</div>}
      {recent.map((r, i) => (
        <div
          key={i}
          className="item-row"
          style={{ ['--env-color' as string]: 'var(--pink)' } as React.CSSProperties}
        >
          <div>
            <div className="item-main">{r.action}</div>
            <div className="item-meta">{r.occurredAt.slice(11, 19)}</div>
          </div>
          <div className="item-meta">signed ✓</div>
        </div>
      ))}
      <SurfaceStyles />
    </EnvFrame>
  );
}

// ─── env-chat / env-vault / env-voice — backing primitives ────

function ChatEnv({ ctx, director }: SurfaceProps): JSX.Element {
  const selected = getSelectedIncident();
  const [messages, setMessages] = useState<MessageCacheRow[]>([]);
  const [incident, setIncident] = useState<IncidentMeta | undefined>(undefined);
  const presenceMap = usePresenceMap(ctx);

  useEffect(() => {
    if (!selected) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const tm = new ThreadManager(ctx);
        const msgs = await tm.getMessages({ threadId: selected.threadId, limit: 100 });
        const im = new IncidentManager(ctx);
        const inc = await im.getIncident(selected.incidentId);
        if (cancelled) return;
        setMessages(msgs);
        setIncident(inc);
      } catch {
        // thread may not exist if the row was deleted; keep last state
      }
    };
    void tick();
    const id = setInterval(() => void tick(), 1500);
    return () => { cancelled = true; clearInterval(id); };
  }, [ctx, selected?.threadId, selected?.incidentId]);

  if (!selected) {
    return (
      <EnvFrame title="Incident Coordination" glyph="✦" color="var(--phos)" director={director}>
        <div className="empty-state t-meta">no incident selected — pick one from the board</div>
        <SurfaceStyles />
      </EnvFrame>
    );
  }

  return (
    <EnvFrame title={`Incident · ${selected.incidentId.slice(0, 8)}`} glyph="✦" color="var(--phos)" director={director}>
      {incident && (
        <>
          <div className="row">
            <span className="key">reporter</span>
            <span className="val">
              <PresenceDot pubkey={incident.reporterPubkey} presenceMap={presenceMap} />
              {incident.reporterPubkey.slice(0, 12)}…
            </span>
          </div>
          <div className="row"><span className="key">category</span><span className="val">{incident.category}</span></div>
          <div className="row"><span className="key">severity</span><span className="val">{incident.confirmedSeverity ?? incident.proposedSeverity}{incident.confirmedSeverity ? ' (confirmed)' : ' (proposed)'}</span></div>
          <div className="row"><span className="key">status</span><span className="val">{incident.status}</span></div>
          {incident.location && (
            <div className="row"><span className="key">location</span><span className="val">{incident.location.lat.toFixed(4)}, {incident.location.lng.toFixed(4)} ±{Math.round(incident.location.accuracyM)}m</span></div>
          )}
          {incident.narrative && (
            <div className="row" style={{ marginTop: 6 }}><span className="key">narrative</span><span className="val" style={{ maxWidth: 360, textAlign: 'right' }}>{incident.narrative}</span></div>
          )}
        </>
      )}

      <div className="t-meta env-sublabel" style={{ marginTop: 18 }}>thread · {messages.length} message{messages.length === 1 ? '' : 's'}</div>
      {messages.length === 0 && (
        <div className="empty-state t-meta">no messages on this thread yet</div>
      )}
      {messages.map((m) => (
        <div
          key={m.hash}
          className="item-row"
          style={{ ['--env-color' as string]: 'var(--phos)' } as React.CSSProperties}
        >
          <div>
            <div className="item-main">
              {m.kind === 'manifest-link' ? (
                <><span className="enc-badge" style={{ marginRight: 8 }}>📎 PHOTO</span>{m.text || 'evidence'}</>
              ) : (m.text || `(${m.kind})`)}
            </div>
            <div className="item-meta">{m.senderPubkey.slice(0, 8)}… · {ago(m.sentAt)}</div>
          </div>
        </div>
      ))}

      <div style={{ marginTop: 16, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button className="cta" onClick={() => director.spawn('compose-message', `to ${selected.threadId.slice(0, 8)}`)}>
          + message
        </button>
        <button className="cta secondary" onClick={() => director.spawn('responder-status', 'status update')}>
          update status
        </button>
        <button className="cta secondary" onClick={() => director.spawn('attach-evidence', 'photo')}>
          attach photo
        </button>
        <button
          className="cta secondary"
          onClick={() => void initiateCall(ctx, director, selected, 'audio')}
        >◉ call</button>
        <button
          className="cta secondary"
          onClick={() => void initiateCall(ctx, director, selected, 'video')}
        >◆ video call</button>
      </div>
      <SurfaceStyles />
    </EnvFrame>
  );
}

/**
 * Phase 19.3.3: start a call from env-chat. Peer pubkey resolves from the
 * incident's reporter (when own user is dispatcher/responder) or — if own
 * user IS the reporter — the first responder assignment. Spawns the
 * `call-active` surface bound to the new callId.
 */
async function initiateCall(
  ctx: AppContext,
  director: Director,
  selected: { incidentId: string; threadId: string },
  kind: 'audio' | 'video',
): Promise<void> {
  try {
    const im = new IncidentManager(ctx);
    const incident = await im.getIncident(selected.incidentId);
    if (incident === undefined) {
      // eslint-disable-next-line no-alert
      window.alert('Incident not found.');
      return;
    }
    const ownPubkey = await im.ownResponderPubkey();
    // Resolve peer: prefer an assigned responder when own = reporter,
    // else the reporter when own is dispatcher/responder.
    let peerPubkey: string | undefined;
    if (incident.reporterPubkey !== ownPubkey) {
      peerPubkey = incident.reporterPubkey;
    } else {
      const allAssignments = await im.listMyAssignments();
      const forThis = allAssignments.find((a) => a.incidentId === selected.incidentId);
      if (forThis !== undefined && forThis.responderPubkey !== ownPubkey) {
        peerPubkey = forThis.responderPubkey;
      }
    }
    if (peerPubkey === undefined) {
      // eslint-disable-next-line no-alert
      window.alert('No peer to call yet — assign a responder first.');
      return;
    }
    await initiateCallToPeer(ctx, director, { peerPubkey, threadId: selected.threadId, kind });
  } catch (err) {
    // eslint-disable-next-line no-alert
    window.alert(`call failed: ${err instanceof Error ? err.message : String(err)}`);
  }
}

/**
 * Phase 58: generalized call-initiation. Used by social-dm, contact-picker,
 * env-voice hub, and the existing incident flow (via `initiateCall`).
 *
 * `threadId` is best-effort routing context — for incident calls it's the
 * coordination thread; for DM-initiated calls it's the synthetic DM group
 * id. Receivers correlate on `callId`; thread-sync ring delivery is opt-in
 * (the WakeupNotifier push relay carries the actual ring signal).
 */
async function initiateCallToPeer(
  ctx: AppContext,
  director: Director,
  args: { peerPubkey: string; threadId: string; kind: 'audio' | 'video' },
): Promise<void> {
  // Phase 58.b (v11): ensure there's a real MLS thread to fan the ring
  // envelope out on, so the callee's ThreadInbox can dispatch it. For
  // peer-to-peer calls (contact-picker, social-dm) this means falling back
  // to / creating a DM group between the two parties. For incident calls
  // (initiateCall flow) the threadId is already the coordination thread,
  // so we leave it as-is.
  let threadId = args.threadId;
  if (args.threadId.startsWith('call-')) {
    try {
      const { getSocialGroupManager } = await import('../../social/SocialGroupManager');
      const dm = await getSocialGroupManager(ctx).getOrCreateDmGroup(args.peerPubkey);
      threadId = dm.mlsGroupId;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('initiateCallToPeer: getOrCreateDmGroup failed, falling back to synthetic thread:', err);
    }
  }
  const { getCallManager } = await import('../../calls/CallManager');
  const cm = getCallManager(ctx);
  const r = await cm.ring({ threadId, peerPubkey: args.peerPubkey, kind: args.kind });
  try { localStorage.setItem('alds-pending-call', r.callId); } catch { /* noop */ }
  const controller = cm.getController(r.callId);
  if (controller !== undefined) {
    try { await controller.placeCall({ withVideo: args.kind === 'video' }); }
    catch (err) {
      // eslint-disable-next-line no-console
      console.warn('placeCall failed:', err);
    }
  }
  director.bridge('call-active', `call ${r.callId.slice(0, 8)}`);
}

function VoiceEnv({ ctx, director }: SurfaceProps): JSX.Element {
  const [recent, setRecent] = useState<CallHistoryRecord[]>([]);
  const [contacts, setContacts] = useState<Array<{ pubkey: string; alias?: string }>>([]);

  useEffect(() => {
    void (async () => {
      const cm = new CallManager(ctx);
      setRecent(await cm.listCallHistory({ limit: 6 }));
      const chatRows = await ctx.storage.db.contacts.toArray();
      setContacts(chatRows.slice(0, 8).map((c) => ({
        pubkey: c.pubkey,
        ...(c.alias !== undefined ? { alias: c.alias } : {}),
      })));
    })().catch(() => { /* surface stays empty */ });
  }, [ctx]);

  const startCall = (kind: 'audio' | 'video'): void => {
    try {
      localStorage.setItem('alds-contact-picker-intent', kind === 'video' ? 'call-target-video' : 'call-target-audio');
    } catch { /* noop */ }
    director.push('contact-picker', kind === 'video' ? 'pick a contact to video-call' : 'pick a contact to call');
  };

  const callContact = (peer: string, kind: 'audio' | 'video'): void => {
    void initiateCallToPeer(ctx, director, { peerPubkey: peer, threadId: `call-${peer}`, kind })
      .catch((err) => {
        // eslint-disable-next-line no-alert
        window.alert(`call failed: ${err instanceof Error ? err.message : String(err)}`);
      });
  };

  const missedCount = recent.filter((r) => r.disposition === 'missed' || r.disposition === 'declined').length;

  return (
    <EnvFrame title="Voice / calls" glyph="◉" color="var(--phos)" director={director}>
      <div style={{ display: 'flex', gap: 8, padding: 12 }}>
        <button
          className="cta"
          data-testid="env-voice-start-audio"
          onClick={() => startCall('audio')}
        >
          ◉ start a call
        </button>
        <button
          className="cta secondary"
          data-testid="env-voice-start-video"
          onClick={() => startCall('video')}
        >
          ◆ start video
        </button>
        <button
          className="cta secondary"
          onClick={() => director.push('call-history', 'all recent calls')}
        >
          all recent →
        </button>
      </div>

      <div style={{ padding: '0 12px 12px' }}>
        <div className="env-sublabel t-meta" style={{ marginBottom: 6 }}>
          recent calls {missedCount > 0 && <span style={{ color: 'var(--amber)' }}>· {missedCount} missed</span>}
        </div>
        {recent.length === 0 ? (
          <div className="empty-state t-meta">no calls yet — start one above</div>
        ) : recent.map((r) => (
          <div key={r.callId} className="item-row" style={{ ['--env-color' as string]: 'var(--phos)' } as React.CSSProperties}>
            <div>
              <div className="item-main">
                {r.direction === 'incoming' ? '◀' : '▶'} {r.callId.slice(0, 8)} · {r.disposition}
              </div>
              <div className="item-meta">
                {ago(r.startedAt)}{r.durationSeconds ? ` · ${r.durationSeconds}s` : ''} · {r.mediaTypes.join(',')}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div style={{ padding: '0 12px 16px' }}>
        <div className="env-sublabel t-meta" style={{ marginBottom: 6 }}>contacts</div>
        {contacts.length === 0 ? (
          <div className="empty-state t-meta">no contacts yet — invite a peer via Hub → mesh invite</div>
        ) : contacts.map((c) => (
          <div key={c.pubkey} className="item-row" style={{ ['--env-color' as string]: 'var(--phos)' } as React.CSSProperties}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="item-main">{c.alias ?? `${c.pubkey.slice(0, 16)}…`}</div>
              <div className="item-meta">{c.pubkey.slice(0, 32)}…</div>
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <button
                className="pill"
                aria-label={`audio call ${c.alias ?? c.pubkey.slice(0, 8)}`}
                onClick={() => callContact(c.pubkey, 'audio')}
              >
                ◉
              </button>
              <button
                className="pill"
                aria-label={`video call ${c.alias ?? c.pubkey.slice(0, 8)}`}
                onClick={() => callContact(c.pubkey, 'video')}
              >
                ◆
              </button>
            </div>
          </div>
        ))}
      </div>

      <SurfaceStyles />
    </EnvFrame>
  );
}

function VaultEnv({ director }: SurfaceProps): JSX.Element {
  return (
    <EnvFrame title="Evidence Vault" glyph="◈" color="var(--pink)" director={director}>
      <div className="t-meta env-sublabel">attached evidence · 0 files</div>
      <div className="empty-state t-meta">attach photos to an incident from the thread surface</div>
      <SurfaceStyles />
    </EnvFrame>
  );
}

// ─── TRANSIENT: incident-report ────────────────────────────

function IncidentReport({ ctx, director }: SurfaceProps): JSX.Element {
  const [category, setCategory] = useState<string>('');
  const [severity, setSeverity] = useState<string>('');
  const [narrative, setNarrative] = useState('');
  const [location, setLocation] = useState<GeoPoint | null>(null);
  const [geoStatus, setGeoStatus] = useState<'idle' | 'requesting' | 'denied' | 'unavailable'>('idle');
  const [status, setStatus] = useState<'idle' | 'submitting' | 'done' | 'error'>('idle');
  const [resultId, setResultId] = useState('');
  const [errMsg, setErrMsg] = useState('');
  const [attestMembership, setAttestMembership] = useState(true);

  const canSubmit = category !== '' && severity !== '' && status === 'idle';

  const captureLocation = () => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      setGeoStatus('unavailable');
      return;
    }
    setGeoStatus('requesting');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocation({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracyM: pos.coords.accuracy,
          fixAt: new Date(pos.timestamp).toISOString(),
        });
        setGeoStatus('idle');
      },
      () => setGeoStatus('denied'),
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 60_000 },
    );
  };

  const submit = async () => {
    setStatus('submitting');
    setErrMsg('');
    try {
      const router = new IncidentRouter(ctx);
      await router.ensureFunnelRegistered();
      await router.funnels.submitAnswer({
        funnelId: INCIDENT_INTAKE_FUNNEL_ID, surfaceId: 'category', answer: category,
      });
      await router.funnels.submitAnswer({
        funnelId: INCIDENT_INTAKE_FUNNEL_ID, surfaceId: 'proposedSeverity', answer: severity,
      });
      if (location) {
        await router.funnels.submitAnswer({
          funnelId: INCIDENT_INTAKE_FUNNEL_ID, surfaceId: 'location', answer: location,
        });
      }
      const trimmed = narrative.trim();
      if (trimmed !== '') {
        await router.funnels.submitAnswer({
          funnelId: INCIDENT_INTAKE_FUNNEL_ID, surfaceId: 'narrative', answer: trimmed,
        });
      }

      // Optional Phase 11: attach a membership proof so the dispatcher
      // sees a "✓ VERIFIED" badge on the board row.
      let membershipProof: { nullifier: string; verified: boolean; impl: 'plaintext' | 'semaphore' } | undefined;
      if (attestMembership) {
        try {
          const mesh = new MeshManager(ctx);
          const neighborhoodId = await mesh.neighborhoodId();
          const mm = new MembershipManager(ctx);
          // Auto-bootstrap: ensure our own commitment is in the local group.
          const me = await mm.ownIdentity();
          if (!(await mm.isMember(neighborhoodId, me.commitment))) {
            await mm.addMember(neighborhoodId, me.commitment);
          }
          // Use the incident's category+severity as the bound message so the
          // proof can't be replayed against a different report.
          const message = `${category}|${severity}`;
          // External nullifier = same neighborhoodId per-day window. For v1
          // we use just neighborhoodId; rate-limiting by time window is
          // Phase 11.5 work.
          const proof = await mm.generateProofForIncident({
            neighborhoodId,
            externalNullifier: neighborhoodId,
            message,
          });
          const verify = await mm.verifyProofAgainst({
            neighborhoodId,
            proof,
            externalNullifier: neighborhoodId,
            message,
          });
          membershipProof = {
            nullifier: proof.nullifier,
            verified: verify.ok,
            impl: proof.impl,
          };
        } catch (memErr) {
          // Membership-proof generation isn't fatal — incident still goes through.
          // eslint-disable-next-line no-console
          console.warn('membership proof skipped:', memErr);
        }
      }

      const result = await router.completeIntake(membershipProof ? { membershipProof } : {});
      setResultId(result.incidentId);
      setStatus('done');
      setTimeout(() => director.pop('report submitted'), 1200);
    } catch (e) {
      setStatus('error');
      setErrMsg(e instanceof IntakeValidationError ? e.message : (e instanceof Error ? e.message : String(e)));
    }
  };

  return (
    <>
      <h2 className="surface-q t-serif">New incident report</h2>
      <p className="surface-sub">
        Anonymous. Submitted under a fresh per-incident sub-key — no link to your other reports.
      </p>

      <div className="t-meta env-sublabel">What is happening?</div>
      <div className="pill-row">
        {['medical', 'safety', 'utility', 'property', 'welfare-check', 'other'].map((c) => (
          <button
            key={c}
            className={`pill ${category === c ? 'pill-on' : ''}`}
            onClick={() => setCategory(c)}
          >{c}</button>
        ))}
      </div>

      <div className="t-meta env-sublabel" style={{ marginTop: 16 }}>Severity (your read)</div>
      <div className="pill-row">
        {['life-threat', 'urgent', 'assistance', 'info'].map((s) => (
          <button
            key={s}
            className={`pill ${severity === s ? 'pill-on' : ''}`}
            onClick={() => setSeverity(s)}
          >{s}</button>
        ))}
      </div>

      <div className="t-meta env-sublabel" style={{ marginTop: 16 }}>Location (optional)</div>
      {location ? (
        <div className="row">
          <span className="key">captured</span>
          <span className="val">
            {location.lat.toFixed(4)}, {location.lng.toFixed(4)} ±{Math.round(location.accuracyM)}m
            <button className="pill" style={{ marginLeft: 10 }} onClick={() => setLocation(null)} aria-label="clear captured location">clear</button>
          </span>
        </div>
      ) : (
        <ManualGeoPicker
          onPick={(p) => setLocation(p)}
          geoStatus={geoStatus}
          onUseGps={captureLocation}
        />
      )}

      <div className="t-meta env-sublabel" style={{ marginTop: 16 }}>Narrative (optional, no PII)</div>
      <textarea
        value={narrative}
        onChange={(e) => setNarrative(e.target.value)}
        placeholder="brief description…"
        rows={3}
        style={{ width: '100%', resize: 'vertical', minHeight: 72 }}
      />

      <div style={{ marginTop: 14, display: 'flex', gap: 10, alignItems: 'center' }}>
        <input
          id="attest-toggle"
          type="checkbox"
          checked={attestMembership}
          onChange={(e) => setAttestMembership(e.target.checked)}
        />
        <label htmlFor="attest-toggle" className="item-meta" style={{ cursor: 'pointer' }}>
          attach membership proof (✓ VERIFIED badge on the board)
        </label>
      </div>

      <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
        <button className="cta" disabled={!canSubmit} onClick={() => void submit()}>
          {status === 'submitting' ? 'submitting…'
           : status === 'done' ? 'submitted ✓'
           : 'submit to neighborhood'}
        </button>
        <button className="cta secondary" onClick={() => director.pop('cancel')}>cancel</button>
      </div>

      {status === 'done' && (
        <div className="receipt-mini">
          <div className="t-meta">incident id</div>
          <div className="receipt-hash">{resultId}</div>
        </div>
      )}
      {status === 'error' && (
        <div className="receipt-mini" style={{ borderColor: 'var(--amber)' }}>
          <div className="t-meta" style={{ color: 'var(--amber)' }}>submission failed</div>
          <div style={{ marginTop: 4, fontSize: 11, color: 'var(--ink)' }}>{errMsg || 'see console for details'}</div>
        </div>
      )}
      <SurfaceStyles />
    </>
  );
}

// ─── TRANSIENT: triage ────────────────────────────────────

function Triage({ ctx, director }: SurfaceProps): JSX.Element {
  const selected = getSelectedIncident();
  const [incident, setIncident] = useState<IncidentMeta | undefined>();
  const [overrideSeverity, setOverrideSeverity] = useState<string>('');
  const [status, setStatus] = useState<'idle' | 'working' | 'error'>('idle');
  const [errMsg, setErrMsg] = useState('');

  useEffect(() => {
    if (!selected) return;
    void (async () => {
      const im = new IncidentManager(ctx);
      setIncident(await im.getIncident(selected.incidentId));
    })();
  }, [ctx, selected?.incidentId]);

  if (!selected) {
    return (
      <>
        <h2 className="surface-q t-serif">Triage</h2>
        <p className="surface-sub">no incident selected — back to the board</p>
        <button className="cta secondary" onClick={() => director.pop('cancel')}>back</button>
        <SurfaceStyles />
      </>
    );
  }

  const baseSeverity = incident?.confirmedSeverity ?? incident?.proposedSeverity;
  const pendingSeverity = overrideSeverity || baseSeverity || '';

  const maybeConfirmSeverity = async (im: IncidentManager) => {
    if (overrideSeverity !== '' && overrideSeverity !== baseSeverity) {
      await im.confirmSeverity(selected.incidentId, overrideSeverity as 'life-threat' | 'urgent' | 'assistance' | 'info');
    }
  };

  const decide = async (decision: 'assign' | 'hold' | 'cancel') => {
    setStatus('working'); setErrMsg('');
    try {
      const im = new IncidentManager(ctx);
      await maybeConfirmSeverity(im);
      if (decision === 'hold') {
        if (incident && incident.status === 'open') await im.updateStatus(selected.incidentId, 'triaged');
        director.pop('held');
      } else if (decision === 'cancel') {
        await im.updateStatus(selected.incidentId, 'cancelled');
        director.pop('cancelled');
      } else { // assign
        if (incident && incident.status === 'open') await im.updateStatus(selected.incidentId, 'triaged');
        director.spawn('assign-responder', 'pick responder');
      }
    } catch (e) {
      setStatus('error');
      setErrMsg(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <>
      <h2 className="surface-q t-serif">Triage</h2>
      <p className="surface-sub">Confirm severity if needed, then assign / hold / cancel. Each decision is audited.</p>

      {incident && (
        <>
          <div className="row"><span className="key">category</span><span className="val">{incident.category}</span></div>
          <div className="row"><span className="key">status</span><span className="val">{incident.status}</span></div>
          <div className="row"><span className="key">proposed</span><span className="val">{incident.proposedSeverity}</span></div>
          {incident.narrative && (
            <div className="row" style={{ marginTop: 6 }}>
              <span className="key">narrative</span>
              <span className="val" style={{ maxWidth: 360, textAlign: 'right' }}>{incident.narrative}</span>
            </div>
          )}
        </>
      )}

      <div className="t-meta env-sublabel" style={{ marginTop: 16 }}>Confirm severity (override if needed)</div>
      <div className="pill-row">
        {['life-threat', 'urgent', 'assistance', 'info'].map((s) => (
          <button
            key={s}
            className={`pill ${pendingSeverity === s ? 'pill-on' : ''}`}
            onClick={() => setOverrideSeverity(s)}
          >{s}</button>
        ))}
      </div>

      <div style={{ marginTop: 18, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button className="cta" disabled={status === 'working'} onClick={() => void decide('assign')}>
          assign responder →
        </button>
        <button className="cta secondary" disabled={status === 'working'} onClick={() => void decide('hold')}>hold</button>
        <button className="cta secondary" disabled={status === 'working'} onClick={() => void decide('cancel')}>cancel incident</button>
        <button className="cta secondary" onClick={() => director.bridge('env-chat', `incident ${selected.incidentId.slice(0, 8)}`)}>
          view thread
        </button>
        <button className="cta secondary" onClick={() => director.pop('back')}>back</button>
      </div>

      {status === 'error' && (
        <div className="receipt-mini" style={{ borderColor: 'var(--amber)' }}>
          <div className="t-meta" style={{ color: 'var(--amber)' }}>triage failed</div>
          <div style={{ marginTop: 4, fontSize: 11 }}>{errMsg}</div>
        </div>
      )}
      <SurfaceStyles />
    </>
  );
}

function AssignResponder({ ctx, director }: SurfaceProps): JSX.Element {
  const selected = getSelectedIncident();
  const [roster, setRoster] = useState<{ pubkey: string; lastActive: string }[]>([]);
  const [myPubkey, setMyPubkey] = useState('');
  const [status, setStatus] = useState<'idle' | 'assigning' | 'done' | 'error'>('idle');
  const [errMsg, setErrMsg] = useState('');

  useEffect(() => {
    void (async () => {
      const im = new IncidentManager(ctx);
      const [list, mine] = await Promise.all([
        im.listOnShift(),
        im.ownResponderPubkey(),
      ]);
      setRoster(list.map((r) => ({ pubkey: r.pubkey, lastActive: r.lastActive })));
      setMyPubkey(mine);
    })();
  }, [ctx]);

  if (!selected) {
    return (
      <>
        <h2 className="surface-q t-serif">Assign responder</h2>
        <p className="surface-sub">no incident selected — back to board</p>
        <button className="cta secondary" onClick={() => director.pop('cancel')}>back</button>
        <SurfaceStyles />
      </>
    );
  }

  const pick = async (pubkey: string) => {
    setStatus('assigning'); setErrMsg('');
    try {
      const im = new IncidentManager(ctx);
      await im.assignResponder(selected.incidentId, pubkey);
      setStatus('done');
      setTimeout(() => director.pop('assigned'), 700);
    } catch (e) {
      setStatus('error');
      setErrMsg(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <>
      <h2 className="surface-q t-serif">Pick a responder</h2>
      <p className="surface-sub">On-shift roster for this neighborhood. Click to assign.</p>

      {roster.length === 0 && (
        <div className="empty-state t-meta">no on-shift responders — try toggling yourself on-shift in env-roster</div>
      )}

      {roster.map((r) => (
        <div
          key={r.pubkey}
          className="item-row"
          style={{ ['--env-color' as string]: 'var(--amber)' } as React.CSSProperties}
          onClick={() => void pick(r.pubkey)}
        >
          <div>
            <div className="item-main">
              <span className="status-dot status-online" />
              {r.pubkey === myPubkey ? 'me' : `${r.pubkey.slice(0, 10)}…`}
            </div>
            <div className="item-meta">active {ago(r.lastActive)}</div>
          </div>
          <div className="item-meta">→ assign</div>
        </div>
      ))}

      <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
        <button className="cta secondary" onClick={() => director.pop('back')}>back</button>
      </div>

      {status === 'done' && (
        <div className="receipt-mini"><div className="t-meta">assigned ✓</div></div>
      )}
      {status === 'error' && (
        <div className="receipt-mini" style={{ borderColor: 'var(--amber)' }}>
          <div className="t-meta" style={{ color: 'var(--amber)' }}>assignment failed</div>
          <div style={{ marginTop: 4, fontSize: 11 }}>{errMsg}</div>
        </div>
      )}
      <SurfaceStyles />
    </>
  );
}

function ResponderAccept({ ctx, director }: SurfaceProps): JSX.Element {
  const selected = getSelectedIncident();
  const [incident, setIncident] = useState<IncidentMeta | undefined>();
  const [status, setStatus] = useState<'idle' | 'working' | 'error'>('idle');
  const [errMsg, setErrMsg] = useState('');

  useEffect(() => {
    if (!selected) return;
    void (async () => {
      const im = new IncidentManager(ctx);
      setIncident(await im.getIncident(selected.incidentId));
    })();
  }, [ctx, selected?.incidentId]);

  if (!selected) {
    return (
      <>
        <h2 className="surface-q t-serif">Accept dispatch?</h2>
        <p className="surface-sub">no assignment selected</p>
        <button className="cta secondary" onClick={() => director.pop('cancel')}>back</button>
        <SurfaceStyles />
      </>
    );
  }

  const decide = async (accept: boolean) => {
    setStatus('working'); setErrMsg('');
    try {
      const im = new IncidentManager(ctx);
      if (accept) await im.acceptAssignment(selected.incidentId);
      else await im.declineAssignment(selected.incidentId);
      director.pop(accept ? 'accepted' : 'declined');
    } catch (e) {
      setStatus('error');
      setErrMsg(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <>
      <h2 className="surface-q t-serif">Accept dispatch?</h2>
      <p className="surface-sub">An incident needs help. Accepting also moves it to <span className="t-phos">dispatched</span>.</p>
      {incident && (
        <>
          <div className="row"><span className="key">category</span><span className="val">{incident.category}</span></div>
          <div className="row"><span className="key">severity</span><span className="val">{incident.confirmedSeverity ?? incident.proposedSeverity}</span></div>
          {incident.location && (
            <div className="row"><span className="key">location</span><span className="val">{incident.location.lat.toFixed(4)}, {incident.location.lng.toFixed(4)}</span></div>
          )}
          {incident.narrative && (
            <div className="row" style={{ marginTop: 6 }}>
              <span className="key">narrative</span>
              <span className="val" style={{ maxWidth: 360, textAlign: 'right' }}>{incident.narrative}</span>
            </div>
          )}
        </>
      )}
      <div style={{ marginTop: 18, display: 'flex', gap: 8 }}>
        <button className="cta" disabled={status === 'working'} onClick={() => void decide(true)}>accept</button>
        <button className="cta secondary" disabled={status === 'working'} onClick={() => void decide(false)}>decline</button>
        <button className="cta secondary" onClick={() => director.pop('cancel')}>cancel</button>
      </div>
      {status === 'error' && (
        <div className="receipt-mini" style={{ borderColor: 'var(--amber)' }}>
          <div className="t-meta" style={{ color: 'var(--amber)' }}>action failed</div>
          <div style={{ marginTop: 4, fontSize: 11 }}>{errMsg}</div>
        </div>
      )}
      <SurfaceStyles />
    </>
  );
}

function ResponderStatus({ ctx, director }: SurfaceProps): JSX.Element {
  const selected = getSelectedIncident();
  const [status, setStatus] = useState<'idle' | 'working' | 'error'>('idle');
  const [errMsg, setErrMsg] = useState('');

  if (!selected) {
    return (
      <>
        <h2 className="surface-q t-serif">Update status</h2>
        <p className="surface-sub">no incident selected</p>
        <button className="cta secondary" onClick={() => director.pop('cancel')}>back</button>
        <SurfaceStyles />
      </>
    );
  }

  const setIncidentStatus = async (s: 'on-scene' | 'resolved' | 'cancelled') => {
    setStatus('working'); setErrMsg('');
    try {
      const im = new IncidentManager(ctx);
      await im.updateStatus(selected.incidentId, s);
      director.pop(`status: ${s}`);
    } catch (e) {
      setStatus('error');
      setErrMsg(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <>
      <h2 className="surface-q t-serif">Update status</h2>
      <p className="surface-sub">Each transition writes a signed audit entry.</p>
      <div style={{ marginTop: 16, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button className="cta" disabled={status === 'working'} onClick={() => void setIncidentStatus('on-scene')}>on-scene</button>
        <button className="cta" disabled={status === 'working'} onClick={() => void setIncidentStatus('resolved')}>resolved</button>
        <button className="cta secondary" disabled={status === 'working'} onClick={() => void setIncidentStatus('cancelled')}>cancel incident</button>
        <button className="cta secondary" onClick={() => director.pop('cancel')}>back</button>
      </div>
      {status === 'error' && (
        <div className="receipt-mini" style={{ borderColor: 'var(--amber)' }}>
          <div className="t-meta" style={{ color: 'var(--amber)' }}>status update failed</div>
          <div style={{ marginTop: 4, fontSize: 11 }}>{errMsg}</div>
        </div>
      )}
      <SurfaceStyles />
    </>
  );
}

function AttachEvidence({ ctx, director }: SurfaceProps): JSX.Element {
  const selected = getSelectedIncident();
  const [file, setFile] = useState<File | null>(null);
  const [status, setStatus] = useState<'idle' | 'reading' | 'storing' | 'done' | 'error'>('idle');
  const [errMsg, setErrMsg] = useState('');
  const [result, setResult] = useState<{ manifestId: string; chunkCount: number } | null>(null);

  if (!selected) {
    return (
      <>
        <h2 className="surface-q t-serif">Attach photo</h2>
        <p className="surface-sub">no incident selected</p>
        <button className="cta secondary" onClick={() => director.pop('cancel')}>back</button>
        <SurfaceStyles />
      </>
    );
  }

  const onPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    setFile(f ?? null);
    setStatus('idle');
    setErrMsg('');
    setResult(null);
  };

  const submit = async () => {
    if (!file) return;
    setStatus('reading'); setErrMsg('');
    try {
      const buf = new Uint8Array(await file.arrayBuffer());
      setStatus('storing');
      const im = new IncidentManager(ctx);
      const r = await im.attachEvidence({
        incidentId: selected.incidentId,
        filename: file.name,
        contentType: file.type || 'application/octet-stream',
        bytes: buf,
      });
      setResult(r);
      setStatus('done');
      setTimeout(() => director.pop('evidence attached'), 1200);
    } catch (e) {
      setStatus('error');
      setErrMsg(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <>
      <h2 className="surface-q t-serif">Attach photo</h2>
      <p className="surface-sub">
        Chunked + AES-GCM encrypted via Vault. Manifest goes on the incident thread;
        chunks live only with group members. Photos only in v1.
      </p>

      <div className="t-meta env-sublabel">choose file</div>
      <input
        type="file"
        accept="image/*"
        onChange={onPick}
        style={{ fontSize: 12 }}
        disabled={status === 'reading' || status === 'storing'}
      />
      {file && (
        <div className="row" style={{ marginTop: 8 }}>
          <span className="key">selected</span>
          <span className="val">{file.name} · {Math.round(file.size / 1024)} KB</span>
        </div>
      )}

      <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
        <button className="cta" disabled={!file || status !== 'idle'} onClick={() => void submit()}>
          {status === 'reading' ? 'reading…'
           : status === 'storing' ? 'encrypting…'
           : status === 'done' ? 'stored ✓'
           : 'encrypt & attach'}
        </button>
        <button className="cta secondary" onClick={() => director.pop('cancel')}>cancel</button>
      </div>

      {status === 'done' && result && (
        <div className="receipt-mini">
          <div className="t-meta">manifest id</div>
          <div className="receipt-hash">{result.manifestId}</div>
          <div className="t-meta" style={{ marginTop: 6 }}>chunks · {result.chunkCount}</div>
        </div>
      )}
      {status === 'error' && (
        <div className="receipt-mini" style={{ borderColor: 'var(--amber)' }}>
          <div className="t-meta" style={{ color: 'var(--amber)' }}>attach failed</div>
          <div style={{ marginTop: 4, fontSize: 11 }}>{errMsg}</div>
        </div>
      )}
      <SurfaceStyles />
    </>
  );
}

function RequestBackup({ ctx, director }: SurfaceProps): JSX.Element {
  const selected = getSelectedIncident();
  const [reason, setReason] = useState('');
  const [status, setStatus] = useState<'idle' | 'working' | 'done' | 'error'>('idle');
  const [errMsg, setErrMsg] = useState('');

  if (!selected) {
    return (
      <>
        <h2 className="surface-q t-serif">Request backup</h2>
        <p className="surface-sub">no incident selected</p>
        <button className="cta secondary" onClick={() => director.pop('cancel')}>back</button>
        <SurfaceStyles />
      </>
    );
  }

  const broadcast = async () => {
    setStatus('working'); setErrMsg('');
    try {
      const im = new IncidentManager(ctx);
      await im.requestBackup(selected.incidentId, reason.trim());
      setStatus('done');
      setTimeout(() => director.pop('backup requested'), 700);
    } catch (e) {
      setStatus('error');
      setErrMsg(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <>
      <h2 className="surface-q t-serif">Request backup</h2>
      <p className="surface-sub">
        Writes a signed audit entry tied to this incident. Network broadcast to nearby
        responders is Phase 6 wiring.
      </p>
      <div className="t-meta env-sublabel">why (optional)</div>
      <textarea
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="e.g. need two more hands, traffic control…"
        rows={3}
        style={{ width: '100%', resize: 'vertical', minHeight: 64 }}
      />
      <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
        <button className="cta" disabled={status === 'working'} onClick={() => void broadcast()}>
          {status === 'working' ? 'recording…' : status === 'done' ? 'recorded ✓' : 'broadcast'}
        </button>
        <button className="cta secondary" onClick={() => director.pop('cancel')}>cancel</button>
      </div>
      {status === 'error' && (
        <div className="receipt-mini" style={{ borderColor: 'var(--amber)' }}>
          <div className="t-meta" style={{ color: 'var(--amber)' }}>broadcast failed</div>
          <div style={{ marginTop: 4, fontSize: 11 }}>{errMsg}</div>
        </div>
      )}
      <SurfaceStyles />
    </>
  );
}

function ShareIncidentOut({ director }: SurfaceProps): JSX.Element {
  return (
    <>
      <h2 className="surface-q t-serif">Export incident (redacted)</h2>
      <p className="surface-sub">Strips caller pubkey + precise geo; outputs JSON the public bulletin can pin.</p>
      <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
        <button className="cta" onClick={() => director.pop('exported')}>export to clipboard</button>
        <button className="cta secondary" onClick={() => director.pop('cancel')}>cancel</button>
      </div>
      <SurfaceStyles />
    </>
  );
}

// ─── TRANSIENT: mesh invite generate + redeem (Phase 6) ────

function MeshInviteGenerate({ ctx, director }: SurfaceProps): JSX.Element {
  const [code, setCode] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [status, setStatus] = useState<'idle' | 'generating' | 'done' | 'error'>('idle');
  const [errMsg, setErrMsg] = useState('');

  const generate = async () => {
    setStatus('generating'); setErrMsg('');
    try {
      const mesh = new MeshManager(ctx);
      const result = await mesh.generateInviteCode();
      setCode(result.code);
      setExpiresAt(result.expiresAt);
      setStatus('done');
    } catch (e) {
      setStatus('error');
      setErrMsg(e instanceof Error ? e.message : String(e));
    }
  };

  const copy = async () => {
    if (!code) return;
    try {
      if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(code);
    } catch { /* clipboard may be denied */ }
  };

  return (
    <>
      <h2 className="surface-q t-serif">Invite a neighbor</h2>
      <p className="surface-sub">
        Signed peer invite. Shareable as text or QR. Default expiry 24 hours, single-use.
      </p>
      {!code && (
        <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
          <button className="cta" disabled={status === 'generating'} onClick={() => void generate()}>
            {status === 'generating' ? 'generating…' : 'generate invite'}
          </button>
          <button className="cta secondary" onClick={() => director.pop('cancel')}>cancel</button>
        </div>
      )}
      {code && (
        <>
          <div className="receipt-mini">
            <div className="t-meta">invite code (paste to a neighbor)</div>
            <div className="receipt-hash" style={{ whiteSpace: 'pre-wrap' }}>{code}</div>
            {expiresAt && (
              <div className="t-meta" style={{ marginTop: 6 }}>expires {new Date(expiresAt).toLocaleString()}</div>
            )}
          </div>
          <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
            <button className="cta" onClick={() => void copy()}>copy to clipboard</button>
            <button className="cta secondary" onClick={() => director.pop('done')}>done</button>
          </div>
        </>
      )}
      {status === 'error' && (
        <div className="receipt-mini" style={{ borderColor: 'var(--amber)' }}>
          <div className="t-meta" style={{ color: 'var(--amber)' }}>generate failed</div>
          <div style={{ marginTop: 4, fontSize: 11 }}>{errMsg}</div>
        </div>
      )}
      <SurfaceStyles />
    </>
  );
}

function MeshInviteRedeem({ ctx, director }: SurfaceProps): JSX.Element {
  const [code, setCode] = useState('');
  const [status, setStatus] = useState<'idle' | 'redeeming' | 'done' | 'error'>('idle');
  const [errMsg, setErrMsg] = useState('');
  const [result, setResult] = useState<{ peerId: string; multiaddrs: string[]; verified: boolean } | null>(null);

  const redeem = async () => {
    setStatus('redeeming'); setErrMsg('');
    try {
      const mesh = new MeshManager(ctx);
      const r = await mesh.redeemInviteCode({ code: code.trim() });
      setResult({ peerId: r.issuerPeerId, multiaddrs: r.multiaddrs, verified: r.signatureVerified });
      setStatus('done');
    } catch (e) {
      setStatus('error');
      setErrMsg(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <>
      <h2 className="surface-q t-serif">Redeem invite</h2>
      <p className="surface-sub">
        Paste an invite code from a neighbor to add their node to your peer book.
      </p>
      <textarea
        value={code}
        onChange={(e) => setCode(e.target.value)}
        placeholder="mesh:v1:…"
        rows={3}
        style={{ width: '100%', resize: 'vertical', minHeight: 72, fontFamily: 'var(--mono)', fontSize: 11 }}
        aria-label="paste mesh invite code"
      />
      <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
        <button className="cta" disabled={!code || status === 'redeeming'} onClick={() => void redeem()}>
          {status === 'redeeming' ? 'redeeming…' : status === 'done' ? 'redeemed ✓' : 'redeem'}
        </button>
        <button className="cta secondary" onClick={() => director.pop('cancel')}>close</button>
      </div>
      {result && (
        <div className="receipt-mini">
          <div className="t-meta">peer added</div>
          <div className="receipt-hash">{result.peerId}</div>
          <div className="t-meta" style={{ marginTop: 6 }}>
            {result.multiaddrs.length} multiaddr{result.multiaddrs.length === 1 ? '' : 's'}
            · signature {result.verified ? 'verified ✓' : 'not verified'}
          </div>
        </div>
      )}
      {status === 'error' && (
        <div className="receipt-mini" style={{ borderColor: 'var(--amber)' }}>
          <div className="t-meta" style={{ color: 'var(--amber)' }}>redeem failed</div>
          <div style={{ marginTop: 4, fontSize: 11 }}>{errMsg}</div>
        </div>
      )}
      <SurfaceStyles />
    </>
  );
}

// ─── TRANSIENT: qr-handoff (Phase 10) ─────────────────────────

const QR_PAYLOAD_KEY = 'alds-qr-payload';        // outgoing bundle, set by Sneakernet
const QR_RESULT_KEY = 'alds-qr-result';          // incoming bundle, written on scan complete
const QR_ACC_KEY = 'alds-qr-acc';                // partial scan state for resume

function QrHandoff({ director }: SurfaceProps): JSX.Element {
  const initialMode: 'show' | 'scan' = (() => {
    try { return (localStorage.getItem('alds-qr-mode') as 'show' | 'scan') ?? 'show'; }
    catch { return 'show'; }
  })();
  const [mode, setMode] = useState<'show' | 'scan'>(initialMode);

  const setQrMode = (m: 'show' | 'scan') => {
    setMode(m);
    try { localStorage.setItem('alds-qr-mode', m); } catch { /* noop */ }
  };

  return (
    <>
      <h2 className="surface-q t-serif">QR handoff</h2>
      <p className="surface-sub">
        Camera-to-camera transfer of a sneakernet bundle. No network — just
        light bouncing off screens.
      </p>
      <div className="pill-row" role="tablist" aria-label="qr mode">
        <button
          className={`pill ${mode === 'show' ? 'pill-on' : ''}`}
          role="tab"
          aria-selected={mode === 'show'}
          onClick={() => setQrMode('show')}
        >show codes</button>
        <button
          className={`pill ${mode === 'scan' ? 'pill-on' : ''}`}
          role="tab"
          aria-selected={mode === 'scan'}
          onClick={() => setQrMode('scan')}
        >scan codes</button>
      </div>

      {mode === 'show' ? <QrShow /> : <QrScan />}

      <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
        <button className="cta secondary" onClick={() => director.pop('cancel')}>close</button>
      </div>
      <SurfaceStyles />
    </>
  );
}

function QrShow(): JSX.Element {
  const [frames, setFrames] = useState<string[]>([]);
  const [bundleId, setBundleId] = useState('');
  const [idx, setIdx] = useState(0);
  const [auto, setAuto] = useState(true);
  const [imageDataUrl, setImageDataUrl] = useState<string>('');
  const [err, setErr] = useState('');

  useEffect(() => {
    const raw = (() => { try { return localStorage.getItem(QR_PAYLOAD_KEY) ?? ''; } catch { return ''; } })();
    if (!raw) {
      setErr('No outgoing bundle staged. Build one from the sneakernet surface, then re-open QR handoff.');
      return;
    }
    try {
      const bytes = new TextEncoder().encode(raw);
      const r = encodeBundle(bytes);
      setFrames(r.frames);
      setBundleId(r.bundleId);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }, []);

  useEffect(() => {
    if (frames.length === 0 || !auto) return;
    const id = setInterval(() => setIdx((i) => (i + 1) % frames.length), 2500);
    return () => clearInterval(id);
  }, [frames.length, auto]);

  useEffect(() => {
    if (frames.length === 0) return;
    void (async () => {
      try {
        const { default: QRCode } = await import('qrcode');
        const url = await QRCode.toDataURL(frames[idx]!, {
          errorCorrectionLevel: 'M',
          margin: 2,
          width: 360,
        });
        setImageDataUrl(url);
      } catch (e) {
        setErr(`QR render failed: ${e instanceof Error ? e.message : e}`);
      }
    })();
  }, [frames, idx]);

  if (err) {
    return (
      <div className="receipt-mini" style={{ borderColor: 'var(--amber)' }}>
        <div className="t-meta" style={{ color: 'var(--amber)' }}>show failed</div>
        <div style={{ marginTop: 4, fontSize: 11 }}>{err}</div>
      </div>
    );
  }
  if (frames.length === 0) {
    return <div className="empty-state t-meta" style={{ marginTop: 14 }}>loading…</div>;
  }

  return (
    <div style={{ marginTop: 14 }}>
      <div className="row"><span className="key">bundle</span><span className="val">{bundleId}</span></div>
      <div className="row"><span className="key">frame</span><span className="val">{idx + 1} / {frames.length}</span></div>
      <div style={{ display: 'flex', justifyContent: 'center', margin: '14px 0', background: '#fff', padding: 12 }}>
        {imageDataUrl
          ? <img src={imageDataUrl} alt={`QR frame ${idx + 1} of ${frames.length}`} width={360} height={360} />
          : <div style={{ width: 360, height: 360, color: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>rendering…</div>
        }
      </div>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
        <button className="pill" onClick={() => setIdx((i) => (i - 1 + frames.length) % frames.length)} aria-label="previous frame">◂ prev</button>
        <button className="pill" onClick={() => setIdx((i) => (i + 1) % frames.length)} aria-label="next frame">next ▸</button>
        <button className={`pill ${auto ? 'pill-on' : ''}`} onClick={() => setAuto((a) => !a)} aria-label="toggle auto-advance">
          {auto ? 'auto · on' : 'auto · off'}
        </button>
      </div>
    </div>
  );
}

function QrScan(): JSX.Element {
  const videoRef = React.useRef<HTMLVideoElement | null>(null);
  const [supported, setSupported] = useState<boolean | null>(null);
  const [progress, setProgress] = useState<DecodeProgress | null>(null);
  const [err, setErr] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    const w = globalThis as unknown as { BarcodeDetector?: unknown };
    setSupported(typeof w.BarcodeDetector === 'function');
  }, []);

  useEffect(() => {
    if (supported !== true) return;
    let stop = false;
    let stream: MediaStream | null = null;
    const acc = (() => {
      try {
        const saved = localStorage.getItem(QR_ACC_KEY) ?? '';
        return QrAccumulator.restore(saved);
      } catch { return new QrAccumulator(); }
    })();

    const persist = () => {
      try { localStorage.setItem(QR_ACC_KEY, acc.serialize()); } catch { /* noop */ }
    };
    persist();
    setProgress(acc.progress());

    (async () => {
      try {
        const W = globalThis as unknown as { BarcodeDetector: new (opts: { formats: string[] }) => { detect(src: CanvasImageSource): Promise<{ rawValue: string }[]> } };
        const detector = new W.BarcodeDetector({ formats: ['qr_code'] });
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }
        while (!stop) {
          if (!videoRef.current) break;
          try {
            const codes = await detector.detect(videoRef.current);
            for (const c of codes) {
              try { acc.add(c.rawValue); persist(); }
              catch { /* unrelated QR or duplicate; ignore */ }
            }
            const p = acc.progress();
            setProgress(p);
            if (p.complete) {
              const bundle = acc.finalize();
              try {
                localStorage.setItem(QR_RESULT_KEY, new TextDecoder().decode(bundle));
                localStorage.removeItem(QR_ACC_KEY);
              } catch { /* noop */ }
              setDone(true);
              break;
            }
          } catch (scanErr) {
            // Continue — scan errors are normal between frames
            void scanErr;
          }
          await new Promise((r) => setTimeout(r, 250));
        }
      } catch (e) {
        setErr(e instanceof Error ? e.message : String(e));
      } finally {
        if (stream) for (const t of stream.getTracks()) t.stop();
      }
    })();

    return () => { stop = true; };
  }, [supported]);

  if (supported === null) return <div className="empty-state t-meta" style={{ marginTop: 14 }}>checking browser support…</div>;
  if (supported === false) {
    return (
      <div className="receipt-mini" style={{ marginTop: 14, borderColor: 'var(--amber)' }}>
        <div className="t-meta" style={{ color: 'var(--amber)' }}>BarcodeDetector unavailable</div>
        <div style={{ marginTop: 4, fontSize: 11 }}>
          Chrome / Edge / Safari support QR scanning natively. Firefox needs a JS QR
          library — currently unsupported in v1. Use the JSON paste path in the
          sneakernet surface instead.
        </div>
      </div>
    );
  }

  return (
    <div style={{ marginTop: 14 }}>
      <video ref={videoRef} autoPlay playsInline muted style={{ width: '100%', maxWidth: 360, background: '#000', display: 'block', margin: '0 auto' }} />
      {progress && progress.totalFrames > 0 && (
        <div style={{ marginTop: 10 }}>
          <div className="row"><span className="key">bundle</span><span className="val">{progress.bundleId}</span></div>
          <div className="row"><span className="key">received</span><span className="val">{progress.received} / {progress.totalFrames}</span></div>
          {progress.missingFrames.length > 0 && progress.missingFrames.length <= 8 && (
            <div className="row"><span className="key">missing</span><span className="val">{progress.missingFrames.join(', ')}</span></div>
          )}
        </div>
      )}
      {done && (
        <div className="receipt-mini">
          <div className="t-meta">scan complete</div>
          <div style={{ marginTop: 4, fontSize: 11 }}>
            Bundle saved to clipboard buffer (localStorage <code>{QR_RESULT_KEY}</code>).
            Return to the sneakernet surface → import tab → paste to apply.
          </div>
        </div>
      )}
      {err && (
        <div className="receipt-mini" style={{ borderColor: 'var(--amber)' }}>
          <div className="t-meta" style={{ color: 'var(--amber)' }}>scan failed</div>
          <div style={{ marginTop: 4, fontSize: 11 }}>{err}</div>
        </div>
      )}
    </div>
  );
}

// ─── TRANSIENT: sneakernet bundle export/import (Phase 6) ───

function Sneakernet({ ctx, director }: SurfaceProps): JSX.Element {
  const [mode, setMode] = useState<'export' | 'import'>('export');
  const [bundleText, setBundleText] = useState('');
  const [status, setStatus] = useState<'idle' | 'working' | 'done' | 'error'>('idle');
  const [errMsg, setErrMsg] = useState('');
  const [importResult, setImportResult] = useState<{ imported: number; skipped: number; errors: string[] } | null>(null);

  const doExport = async () => {
    setStatus('working'); setErrMsg('');
    try {
      const rm = new RecoveryManager(ctx, new InMemorySnapshotStorage());
      const r = await rm.exportQueueToBundle();
      const json = new TextDecoder().decode(r.bundleBytes);
      setBundleText(json);
      try {
        if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(json);
      } catch { /* clipboard denied; user can copy manually */ }
      setStatus('done');
    } catch (e) {
      setStatus('error');
      setErrMsg(e instanceof Error ? e.message : String(e));
    }
  };

  const doImport = async () => {
    setStatus('working'); setErrMsg('');
    try {
      const rm = new RecoveryManager(ctx, new InMemorySnapshotStorage());
      const bytes = new TextEncoder().encode(bundleText.trim());
      const r = await rm.importQueueFromBundle(bytes);
      setImportResult({ imported: r.imported, skipped: r.skipped, errors: r.errors });
      setStatus('done');
    } catch (e) {
      setStatus('error');
      setErrMsg(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <>
      <h2 className="surface-q t-serif">Sneakernet bundle</h2>
      <p className="surface-sub">
        Move signed envelopes between offline neighbors. Export your outbound queue as JSON;
        another node imports it. Phase 10: also show as QR for camera-to-camera handoff.
      </p>
      <div className="pill-row" role="tablist" aria-label="bundle direction">
        <button
          className={`pill ${mode === 'export' ? 'pill-on' : ''}`}
          role="tab"
          aria-selected={mode === 'export'}
          onClick={() => { setMode('export'); setStatus('idle'); setImportResult(null); setBundleText(''); }}
        >export</button>
        <button
          className={`pill ${mode === 'import' ? 'pill-on' : ''}`}
          role="tab"
          aria-selected={mode === 'import'}
          onClick={() => { setMode('import'); setStatus('idle'); setImportResult(null); setBundleText(''); }}
        >import</button>
      </div>

      {mode === 'export' && (
        <>
          <div style={{ marginTop: 14, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button className="cta" disabled={status === 'working'} onClick={() => void doExport()}>
              {status === 'working' ? 'building…' : status === 'done' ? 'copied ✓' : 'build bundle'}
            </button>
            {bundleText && (
              <button
                className="cta secondary"
                onClick={() => {
                  try {
                    localStorage.setItem('alds-qr-payload', bundleText);
                    localStorage.setItem('alds-qr-mode', 'show');
                  } catch { /* noop */ }
                  director.spawn('qr-handoff', 'show as QR');
                }}
              >show as QR</button>
            )}
            <button className="cta secondary" onClick={() => director.pop('cancel')}>close</button>
          </div>
          {bundleText && (
            <div className="receipt-mini">
              <div className="t-meta">JSON bundle (also copied to clipboard)</div>
              <pre style={{ fontFamily: 'var(--mono)', fontSize: 10, whiteSpace: 'pre-wrap', wordBreak: 'break-all', maxHeight: 220, overflow: 'auto', marginTop: 6 }}>
                {bundleText.slice(0, 1200)}{bundleText.length > 1200 ? '\n…' : ''}
              </pre>
            </div>
          )}
        </>
      )}
      {mode === 'import' && (
        <>
          <div className="t-meta env-sublabel" style={{ marginTop: 14 }}>paste a bundle</div>
          <textarea
            value={bundleText}
            onChange={(e) => setBundleText(e.target.value)}
            placeholder='{"kind":"mesh-queue-export",…}'
            rows={6}
            style={{ width: '100%', resize: 'vertical', minHeight: 110, fontFamily: 'var(--mono)', fontSize: 11 }}
            aria-label="paste sneakernet bundle JSON"
          />
          <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button className="cta" disabled={!bundleText || status === 'working'} onClick={() => void doImport()}>
              {status === 'working' ? 'importing…' : status === 'done' ? 'imported ✓' : 'import'}
            </button>
            <button
              className="cta secondary"
              onClick={() => {
                try {
                  // Try pulling a freshly-scanned bundle into the textarea.
                  const scanned = localStorage.getItem('alds-qr-result');
                  if (scanned) { setBundleText(scanned); localStorage.removeItem('alds-qr-result'); }
                  else {
                    localStorage.setItem('alds-qr-mode', 'scan');
                    director.spawn('qr-handoff', 'scan');
                  }
                } catch { /* noop */ }
              }}
            >scan QR</button>
            <button className="cta secondary" onClick={() => director.pop('cancel')}>close</button>
          </div>
          {importResult && (
            <div className="receipt-mini">
              <div className="t-meta">result</div>
              <div className="row"><span className="key">imported</span><span className="val">{importResult.imported}</span></div>
              <div className="row"><span className="key">skipped</span><span className="val">{importResult.skipped}</span></div>
              {importResult.errors.length > 0 && (
                <div className="row" style={{ marginTop: 6 }}>
                  <span className="key">errors</span>
                  <span className="val">{importResult.errors.slice(0, 3).join(', ')}</span>
                </div>
              )}
            </div>
          )}
        </>
      )}
      {status === 'error' && (
        <div className="receipt-mini" style={{ borderColor: 'var(--amber)' }}>
          <div className="t-meta" style={{ color: 'var(--amber)' }}>operation failed</div>
          <div style={{ marginTop: 4, fontSize: 11 }}>{errMsg}</div>
        </div>
      )}
      <SurfaceStyles />
    </>
  );
}

// ─── TRANSIENT: compose-message + establish-call (retained) ────

function ComposeMessage({ director, signAndStore }: SurfaceProps): JSX.Element {
  const [body, setBody] = useState('');
  const [status, setStatus] = useState<'idle' | 'signing' | 'signed' | 'error'>('idle');
  const [hash, setHash] = useState<string>('');

  const submit = async () => {
    setStatus('signing');
    try {
      const env = await signAndStore('compose-message', 'chat/message', { body });
      setHash(env.hash);
      setStatus('signed');
      setTimeout(() => director.pop('message sent'), 800);
    } catch {
      setStatus('error');
    }
  };

  return (
    <>
      <h2 className="surface-q t-serif">Append to thread</h2>
      <p className="surface-sub">Transient surface. Will sign with the current envelope and pop on completion.</p>
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="signed message body…"
        rows={4}
        style={{ width: '100%', resize: 'vertical', minHeight: 80 }}
      />
      <div style={{ marginTop: 14, display: 'flex', gap: 8 }}>
        <button className="cta" disabled={!body || status !== 'idle'} onClick={() => void submit()}>
          {status === 'signing' ? 'signing…' : status === 'signed' ? 'signed ✓' : 'sign & broadcast'}
        </button>
        <button className="cta secondary" onClick={() => director.pop('cancel')}>cancel</button>
      </div>
      {status === 'signed' && (
        <div className="receipt-mini">
          <div className="t-meta">envelope hash</div>
          <div className="receipt-hash">{hash.slice(0, 12)}…{hash.slice(-8)}</div>
        </div>
      )}
      <SurfaceStyles />
    </>
  );
}

function EstablishCall({ ctx, director }: SurfaceProps): JSX.Element {
  // Phase 58: real establish-call surface. Pre-ring confirmation — pulls
  // target peer from localStorage (`alds-establish-call-target`), lets the
  // user pick audio vs video, then rings via initiateCallToPeer.
  const peerPubkey = (() => {
    try { return localStorage.getItem('alds-establish-call-target') ?? ''; } catch { return ''; }
  })();
  const [status, setStatus] = useState<'idle' | 'ringing' | 'error'>('idle');
  const [error, setError] = useState<string>('');

  const ring = async (kind: 'audio' | 'video'): Promise<void> => {
    if (peerPubkey === '') return;
    setStatus('ringing');
    try {
      await initiateCallToPeer(ctx, director, {
        peerPubkey,
        threadId: `call-${peerPubkey}`,
        kind,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setStatus('error');
    }
  };

  if (peerPubkey === '') {
    return (
      <>
        <h2 className="surface-q t-serif">Start a call</h2>
        <p className="surface-sub">Pick a contact first.</p>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 16 }}>
          <button
            className="cta"
            onClick={() => {
              try { localStorage.setItem('alds-contact-picker-intent', 'call-target-audio'); } catch { /* noop */ }
              director.push('contact-picker', 'pick a contact to call');
            }}
          >
            ☰ pick contact
          </button>
          <button className="cta secondary" onClick={() => director.pop('back')}>back</button>
        </div>
        <SurfaceStyles />
      </>
    );
  }

  return (
    <>
      <h2 className="surface-q t-serif">Call {peerPubkey.slice(0, 12)}…</h2>
      <p className="surface-sub">Pick audio or video. The other side will be rung via push relay.</p>
      <div style={{ textAlign: 'center', padding: 24 }}>
        <div style={{ fontSize: 56, color: 'var(--phos)', textShadow: '0 0 24px var(--phos)', marginBottom: 12 }}>◉</div>
        <div style={{ fontSize: 12, marginBottom: 6 }}>{status === 'ringing' ? 'ringing…' : 'ready'}</div>
      </div>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
        <button
          className="cta"
          data-testid="establish-call-ring-audio"
          disabled={status === 'ringing'}
          onClick={() => { void ring('audio'); }}
        >
          ◉ audio
        </button>
        <button
          className="cta secondary"
          data-testid="establish-call-ring-video"
          disabled={status === 'ringing'}
          onClick={() => { void ring('video'); }}
        >
          ◆ video
        </button>
        <button className="cta secondary" onClick={() => director.pop('back')}>back</button>
      </div>
      {status === 'error' && (
        <div className="receipt-mini" style={{ marginTop: 12, borderColor: 'var(--amber)' }}>
          <div className="t-meta" style={{ color: 'var(--amber)' }}>call failed: {error}</div>
        </div>
      )}
      <SurfaceStyles />
    </>
  );
}

// ─── ENV FRAME helper ─────────────────────────────────────

function EnvFrame({
  title, glyph, color, director, children,
}: {
  title: string;
  glyph: string;
  color: string;
  director: Director;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <>
      <div className="env-toolbar">
        <div className="env-toolbar-title">
          <span style={{ color, marginRight: 8 }}>{glyph}</span>
          {title}
        </div>
        <button className="cta secondary" onClick={() => director.bridge('hub', '← hub (switch role)')}>
          ◂ hub
        </button>
      </div>
      <div style={{ flex: 1, overflowY: 'auto' }}>{children}</div>
    </>
  );
}

// ─── Phase 8 call surfaces (v2 voice/video) ───────────────

function CallIncoming({ ctx, director }: SurfaceProps): JSX.Element {
  // Wired against CallManager; in v1 of Phase 8 the actual ring delivery
  // is stubbed, so this surface is reached via cm.registerIncoming() in
  // future inbox handler work.
  const selected = getSelectedIncident();
  const [status, setStatus] = useState<'idle' | 'working' | 'error'>('idle');
  const [callId] = useState<string>(() => {
    try { return localStorage.getItem('alds-pending-call') ?? ''; } catch { return ''; }
  });
  const accept = async () => {
    if (!callId) return director.pop('no-call');
    setStatus('working');
    try {
      const cm = new CallManager(ctx);
      await cm.accept(callId);
      director.bridge('call-active', `call ${callId.slice(0, 8)}`);
    } catch { setStatus('error'); }
  };
  const decline = async () => {
    if (!callId) return director.pop('no-call');
    try {
      const cm = new CallManager(ctx);
      await cm.decline(callId);
    } finally { director.pop('declined'); }
  };
  return (
    <>
      <h2 className="surface-q t-serif">Incoming call</h2>
      <p className="surface-sub">
        Phase 8 wiring is partial — media routing requires the libp2p
        signaling stream which lands in build-plan steps 2–4.
      </p>
      {selected && <div className="row"><span className="key">on incident</span><span className="val">{selected.incidentId.slice(0, 8)}</span></div>}
      <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
        <button className="cta" onClick={() => void accept()} disabled={status === 'working'}>accept</button>
        <button className="cta secondary" onClick={() => void decline()}>decline</button>
      </div>
      {status === 'error' && <div className="receipt-mini" style={{ borderColor: 'var(--amber)' }}><div className="t-meta">accept failed</div></div>}
      <SurfaceStyles />
    </>
  );
}

function CallActive({ ctx, director }: SurfaceProps): JSX.Element {
  // Persistent surface — stays up for the duration of the call. Phase 19.3
  // wires this to the live CallController so remote media flows through
  // and mute/video/end actually drive the underlying engine.
  const [callId] = useState<string>(() => {
    try { return localStorage.getItem('alds-pending-call') ?? ''; } catch { return ''; }
  });
  const [status, setStatus] = useState<'connecting' | 'connected' | 'reconnecting' | 'ended' | 'error'>('connecting');
  const [audioMuted, setAudioMuted] = useState(false);
  const [videoOn, setVideoOn] = useState(false);
  const remoteVideoRef = useRef<HTMLVideoElement | null>(null);
  const remoteAudioRef = useRef<HTMLAudioElement | null>(null);

  // Phase 54.b: auto-flip presence to busy + "on a call" while this surface
  // is mounted. Restore to 'available' on unmount. The PresenceManager is a
  // singleton shared with the bootstrap heartbeat timer, so this state shows
  // up to peers on the next heartbeat (≤30s) without an explicit re-emit.
  useEffect(() => {
    let cancelled = false;
    let restored = false;
    void (async () => {
      try {
        const { getPresenceManager } = await import('../../mesh/PresenceManager');
        if (cancelled) return;
        const pm = getPresenceManager(ctx);
        pm.setOwnStatus('busy');
        pm.setOwnActivity('on a call');
      } catch { /* swallow — surface still renders */ }
    })();
    return () => {
      cancelled = true;
      if (restored) return;
      restored = true;
      void (async () => {
        try {
          const { getPresenceManager } = await import('../../mesh/PresenceManager');
          const pm = getPresenceManager(ctx);
          pm.setOwnStatus('available');
          pm.setOwnActivity(undefined);
        } catch { /* swallow */ }
      })();
    };
  }, [ctx]);

  // Subscribe to the live CallController for this callId.
  useEffect(() => {
    if (!callId) return;
    let unsubscribe: (() => void) | undefined;
    let cancelled = false;
    void (async () => {
      const { getCallManager } = await import('../../calls/CallManager');
      const cm = getCallManager(ctx);
      const controller = cm.getController(callId);
      if (controller === undefined) return;
      if (cancelled) return;
      // Initial state from the session.
      if (controller.session.state === 'connected') setStatus('connected');
      unsubscribe = controller.on((e) => {
        if (e.kind === 'state') {
          if (e.to === 'connected') setStatus('connected');
          else if (e.to === 'closed') setStatus('ended');
        } else if (e.kind === 'reconnect-attempt') {
          setStatus('reconnecting');
        } else if (e.kind === 'terminal') {
          setStatus('ended');
          setTimeout(() => director.bridge('hub', 'call ended'), 600);
        } else if (e.kind === 'remote-track' && e.stream !== undefined) {
          if (e.trackKind === 'video' && remoteVideoRef.current !== null) {
            remoteVideoRef.current.srcObject = e.stream;
            setVideoOn(true);
          } else if (e.trackKind === 'audio' && remoteAudioRef.current !== null) {
            remoteAudioRef.current.srcObject = e.stream;
          }
        }
      });
    })();
    return () => {
      cancelled = true;
      if (unsubscribe !== undefined) unsubscribe();
    };
  }, [ctx, callId, director]);

  const toggleMute = async () => {
    const { getCallManager } = await import('../../calls/CallManager');
    const controller = getCallManager(ctx).getController(callId);
    if (controller !== undefined) await controller.setMuted('audio', !audioMuted);
    setAudioMuted((m) => !m);
  };
  const toggleVideo = async () => {
    const { getCallManager } = await import('../../calls/CallManager');
    const controller = getCallManager(ctx).getController(callId);
    if (controller === undefined) { setVideoOn((v) => !v); return; }
    if (videoOn) await controller.removeVideo();
    else await controller.addVideo();
    setVideoOn((v) => !v);
  };
  const end = async () => {
    if (!callId) { director.pop('no-call'); return; }
    try {
      const { getCallManager } = await import('../../calls/CallManager');
      const cm = getCallManager(ctx);
      const controller = cm.getController(callId);
      if (controller !== undefined) await controller.end('user-ended');
      await cm.endCall(callId);
      setStatus('ended');
      setTimeout(() => director.bridge('hub', 'call ended'), 600);
    } catch {
      setStatus('error');
    }
  };
  return (
    <EnvFrame title="Call · active" glyph="◉" color="var(--phos)" director={director}>
      <div className="row"><span className="key">call</span><span className="val">{callId ? callId.slice(0, 12) + '…' : '(none)'}</span></div>
      <div className="row"><span className="key">status</span><span className="val">{status}</span></div>
      <div className="row"><span className="key">audio</span><span className="val">{audioMuted ? 'muted' : 'open'}</span></div>
      <div className="row"><span className="key">video</span><span className="val">{videoOn ? 'on' : 'off'}</span></div>
      <video
        ref={remoteVideoRef}
        data-testid="call-remote-video"
        autoPlay
        playsInline
        style={{
          width: '100%',
          marginTop: 12,
          background: '#000',
          borderRadius: 4,
          display: videoOn ? 'block' : 'none',
        }}
      />
      <audio
        ref={remoteAudioRef}
        data-testid="call-remote-audio"
        autoPlay
        style={{ display: 'none' }}
      />
      <p className="surface-sub" style={{ marginTop: 12 }}>
        Media routes through CallController + MediaEngine. Remote tracks
        attach to the &lt;video&gt; element above when video is on.
      </p>
      <div style={{ marginTop: 16, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button className="cta secondary" onClick={toggleMute}>
          {audioMuted ? 'unmute' : 'mute'}
        </button>
        <button className="cta secondary" onClick={toggleVideo}>
          {videoOn ? 'stop video' : 'start video'}
        </button>
        <button className="cta" onClick={() => void end()}>end call</button>
        <button className="cta secondary" onClick={() => director.spawn('call-history', 'recent calls')}>recent calls</button>
      </div>
      <SurfaceStyles />
    </EnvFrame>
  );
}

function MediaControlsBar({ ctx, director }: SurfaceProps): JSX.Element {
  // Ambient floating bar — visible during active calls regardless of the
  // surface stack underneath. Mirrors the controls on CallActive so the
  // user can mute/end without leaving whatever surface they're on.
  const [callId] = useState<string>(() => {
    try { return localStorage.getItem('alds-pending-call') ?? ''; } catch { return ''; }
  });
  const [audioMuted, setAudioMuted] = useState(false);
  if (!callId) return <></>;
  const end = async () => {
    try {
      const cm = new CallManager(ctx);
      await cm.endCall(callId);
    } catch { /* best-effort */ }
    director.bridge('hub', 'call ended');
  };
  return (
    <div
      data-testid="media-controls-bar"
      style={{
        position: 'fixed',
        bottom: 16,
        left: '50%',
        transform: 'translateX(-50%)',
        background: 'rgba(0,0,0,0.85)',
        padding: '8px 12px',
        borderRadius: 24,
        display: 'flex',
        gap: 8,
        zIndex: 9999,
      }}
    >
      <button className="cta secondary" onClick={() => setAudioMuted((m) => !m)}>
        {audioMuted ? '◇ muted' : '◆ live'}
      </button>
      <button className="cta secondary" onClick={() => director.bridge('call-active', 'show call')}>
        ◉ {callId.slice(0, 6)}
      </button>
      <button className="cta" onClick={() => void end()}>end</button>
    </div>
  );
}

function CallHistoryView({ ctx, director }: SurfaceProps): JSX.Element {
  const [rows, setRows] = useState<CallHistoryRecord[]>([]);
  useEffect(() => {
    void (async () => {
      const cm = new CallManager(ctx);
      setRows(await cm.listCallHistory({ limit: 25 }));
    })();
  }, [ctx]);

  const startCall = (kind: 'audio' | 'video'): void => {
    try {
      localStorage.setItem('alds-contact-picker-intent', kind === 'video' ? 'call-target-video' : 'call-target-audio');
    } catch { /* noop */ }
    director.push('contact-picker', kind === 'video' ? 'pick a contact to video-call' : 'pick a contact to call');
  };

  return (
    <>
      <h2 className="surface-q t-serif">Recent calls</h2>
      <p className="surface-sub">Most recent first. Disposition matches the CallSession terminal state.</p>
      <div style={{ display: 'flex', gap: 8, margin: '8px 0 12px' }}>
        <button
          className="cta"
          data-testid="call-history-start-audio"
          onClick={() => startCall('audio')}
        >
          ◉ start a call
        </button>
        <button
          className="cta secondary"
          data-testid="call-history-start-video"
          onClick={() => startCall('video')}
        >
          ◆ start video call
        </button>
      </div>
      {rows.length === 0 && <div className="empty-state t-meta">no calls yet — tap "start a call" to ring a contact</div>}
      {rows.map((r) => (
        <div
          key={r.callId}
          className="item-row"
          style={{ ['--env-color' as string]: 'var(--phos)' } as React.CSSProperties}
        >
          <div>
            <div className="item-main">
              {r.direction === 'incoming' ? '◀' : '▶'} {r.callId.slice(0, 8)} · {r.disposition}
            </div>
            <div className="item-meta">
              {r.threadId?.slice(0, 8) ?? '—'} · {ago(r.startedAt)}{r.durationSeconds ? ` · ${r.durationSeconds}s` : ''}
            </div>
          </div>
          <div className="item-meta">{r.mediaTypes.join(',')}</div>
        </div>
      ))}
      <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
        <button className="cta secondary" onClick={() => director.pop('back')}>back</button>
      </div>
      <SurfaceStyles />
    </>
  );
}

// ─── ManualGeoPicker — GPS button + drop-pin fallback ─────

function ManualGeoPicker({
  onPick, geoStatus, onUseGps,
}: {
  onPick: (p: GeoPoint) => void;
  geoStatus: 'idle' | 'requesting' | 'denied' | 'unavailable';
  onUseGps: () => void;
}): JSX.Element {
  const [showManual, setShowManual] = useState(false);
  const [latText, setLatText] = useState('');
  const [lngText, setLngText] = useState('');
  const [err, setErr] = useState('');

  const submit = () => {
    const lat = parseFloat(latText);
    const lng = parseFloat(lngText);
    if (Number.isNaN(lat) || Math.abs(lat) > 90) { setErr('lat must be in [-90, 90]'); return; }
    if (Number.isNaN(lng) || Math.abs(lng) > 180) { setErr('lng must be in [-180, 180]'); return; }
    setErr('');
    onPick({
      lat, lng,
      accuracyM: 100,  // manual drop = ~100m default
      fixAt: new Date().toISOString(),
    });
    setShowManual(false);
    setLatText(''); setLngText('');
  };

  if (!showManual) {
    return (
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <button className="pill" onClick={onUseGps} disabled={geoStatus === 'requesting'} aria-label="use device GPS for location">
          {geoStatus === 'requesting' ? 'requesting…' : 'use my GPS'}
        </button>
        <button className="pill" onClick={() => setShowManual(true)} aria-label="enter location manually">
          drop pin manually
        </button>
        <span className="item-meta">
          {geoStatus === 'denied' && 'GPS denied — use manual pin instead'}
          {geoStatus === 'unavailable' && 'no GPS available'}
          {geoStatus === 'idle' && 'coarsened to ~50m before storage'}
        </span>
      </div>
    );
  }

  return (
    <div className="manual-geo">
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <label className="item-meta" htmlFor="lat-input">lat</label>
        <input
          id="lat-input"
          type="number"
          step="0.0001"
          value={latText}
          onChange={(e) => setLatText(e.target.value)}
          placeholder="47.6062"
          style={{ width: 110 }}
        />
        <label className="item-meta" htmlFor="lng-input">lng</label>
        <input
          id="lng-input"
          type="number"
          step="0.0001"
          value={lngText}
          onChange={(e) => setLngText(e.target.value)}
          placeholder="-122.3321"
          style={{ width: 110 }}
        />
        <button className="pill pill-on" onClick={submit}>set</button>
        <button className="pill" onClick={() => { setShowManual(false); setErr(''); }}>cancel</button>
      </div>
      {err && <div className="item-meta" style={{ color: 'var(--amber)', marginTop: 6 }}>{err}</div>}
    </div>
  );
}

// ─── Phase 14: TreatyBuilder transient ────────────────────

interface TreatyRuleDraft {
  field: string;
  action: 'drop' | 'coarsenGeo' | 'hash' | 'truncate';
  maxLen?: number;
}

function TreatyBuilder({ ctx, director }: SurfaceProps): JSX.Element {
  const [remoteId, setRemoteId] = useState('');
  const [hopTtl, setHopTtl] = useState(2);
  const [autoForward, setAutoForward] = useState(false);
  const [rules, setRules] = useState<TreatyRuleDraft[]>([]);
  const [status, setStatus] = useState<'idle' | 'working' | 'done' | 'error'>('idle');
  const [err, setErr] = useState('');

  const addRule = () => setRules((rs) => [...rs, { field: '', action: 'drop' }]);
  const updateRule = (i: number, patch: Partial<TreatyRuleDraft>) =>
    setRules((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const removeRule = (i: number) => setRules((rs) => rs.filter((_, idx) => idx !== i));

  const sign = async () => {
    if (!remoteId.trim()) { setErr('remoteNeighborhoodId required'); setStatus('error'); return; }
    setStatus('working');
    setErr('');
    try {
      const fm = new FederationManager(ctx);
      const { MeshManager } = await import('../../mesh/MeshManager');
      const mm = new MeshManager(ctx);
      const localId = await mm.neighborhoodId();
      const redactionRules = rules
        .filter((r) => r.field.trim().length > 0)
        .map((r) => ({
          field: r.field.trim(),
          action: r.action,
          ...(r.maxLen !== undefined ? { params: { maxLen: r.maxLen } } : {}),
        }));
      await fm.linkNeighborhood({
        localNeighborhoodId: localId,
        remoteNeighborhoodId: remoteId.trim(),
        treaty: { signedAt: new Date().toISOString() },
        hopTtl,
        autoForward,
        ...(redactionRules.length > 0 ? { redactionRules } : {}),
      });
      setStatus('done');
      setTimeout(() => director.pop('treaty signed'), 800);
    } catch (e) {
      setStatus('error');
      setErr(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <>
      <h2 className="surface-q t-serif">Treaty builder · multi-hop federation</h2>
      <p className="surface-sub">
        Sign a federation treaty with another neighborhood. Hop TTL caps how
        far an incident can travel; redaction rules strip sensitive fields
        before forward.
      </p>

      <div style={{ marginTop: 12 }}>
        <label htmlFor="tb-remote" className="key">remote neighborhood id</label>
        <input
          id="tb-remote"
          type="text"
          value={remoteId}
          onChange={(e) => setRemoteId(e.target.value)}
          placeholder="16-char hex"
          style={{ width: '100%', padding: 6, marginTop: 4 }}
        />
      </div>

      <div className="row" style={{ marginTop: 8 }}>
        <label htmlFor="tb-ttl" className="key">hop ttl</label>
        <input
          id="tb-ttl"
          type="number"
          min={1}
          max={5}
          value={hopTtl}
          onChange={(e) => setHopTtl(Math.max(1, Math.min(5, Number(e.target.value) || 1)))}
          style={{ width: 60 }}
        />
      </div>

      <div className="row" style={{ marginTop: 8 }}>
        <label htmlFor="tb-auto" className="key">auto-forward inbound</label>
        <input
          id="tb-auto"
          type="checkbox"
          checked={autoForward}
          onChange={(e) => setAutoForward(e.target.checked)}
        />
      </div>

      <h3 className="t-meta" style={{ marginTop: 16 }}>Redaction rules</h3>
      {rules.length === 0 && <div className="t-meta" style={{ color: 'var(--ink-dim)' }}>none — full payload forwarded</div>}
      {rules.map((r, i) => (
        <div key={i} style={{ display: 'flex', gap: 6, marginTop: 6, alignItems: 'center' }}>
          <input
            type="text"
            value={r.field}
            onChange={(e) => updateRule(i, { field: e.target.value })}
            placeholder="field (e.g. narrative)"
            style={{ flex: 1, padding: 4 }}
          />
          <select
            value={r.action}
            onChange={(e) => updateRule(i, { action: e.target.value as TreatyRuleDraft['action'] })}
          >
            <option value="drop">drop</option>
            <option value="hash">hash</option>
            <option value="truncate">truncate</option>
            <option value="coarsenGeo">coarsenGeo</option>
          </select>
          {r.action === 'truncate' && (
            <input
              type="number"
              placeholder="maxLen"
              value={r.maxLen ?? ''}
              onChange={(e) => {
                const v = Number(e.target.value);
                updateRule(i, v > 0 ? { maxLen: v } : {});
              }}
              style={{ width: 60, padding: 4 }}
            />
          )}
          <button className="pill" onClick={() => removeRule(i)}>×</button>
        </div>
      ))}
      <button className="pill" onClick={addRule} style={{ marginTop: 8 }}>+ add rule</button>

      <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
        <button className="cta" onClick={() => void sign()} disabled={status === 'working'}>
          sign + link
        </button>
        <button className="cta secondary" onClick={() => director.pop('cancelled')}>cancel</button>
      </div>
      {status === 'done' && <div className="receipt-mini"><div className="t-meta">treaty signed ✓</div></div>}
      {status === 'error' && (
        <div className="receipt-mini" style={{ borderColor: 'var(--amber)' }}>
          <div className="t-meta">error: {err}</div>
        </div>
      )}
      <SurfaceStyles />
    </>
  );
}

// ─── Phase 19.3: NetworkConfig — operator pastes bootstrap + TURN ───

function NetworkConfigSurface({ director }: SurfaceProps): JSX.Element {
  const [bootstrapPeers, setBootstrapPeers] = useState('');
  const [turnUrl, setTurnUrl] = useState('');
  const [turnSecret, setTurnSecret] = useState('');
  const [turnRealm, setTurnRealm] = useState('');
  const [defaultSensitivity, setDefaultSensitivity] = useState<'low' | 'medium' | 'high'>('low');
  const [pushRelayUrl, setPushRelayUrl] = useState('');
  const [pushVapidPublic, setPushVapidPublic] = useState('');
  const [status, setStatus] = useState<'idle' | 'saved'>('idle');

  useEffect(() => {
    void (async () => {
      const { loadNetworkConfig } = await import('../../calls/NetworkConfig');
      const cfg = loadNetworkConfig();
      setBootstrapPeers(cfg.bootstrapPeers);
      setTurnUrl(cfg.turnUrl);
      setTurnSecret(cfg.turnSecret);
      setTurnRealm(cfg.turnRealm);
      setDefaultSensitivity(cfg.defaultSensitivity);
      setPushRelayUrl(cfg.pushRelayUrl);
      setPushVapidPublic(cfg.pushVapidPublic);
    })();
  }, []);

  const save = async () => {
    const { saveNetworkConfig } = await import('../../calls/NetworkConfig');
    saveNetworkConfig({
      bootstrapPeers: bootstrapPeers.trim(),
      turnUrl: turnUrl.trim(),
      turnSecret: turnSecret.trim(),
      turnRealm: turnRealm.trim(),
      defaultSensitivity,
      pushRelayUrl: pushRelayUrl.trim(),
      pushVapidPublic: pushVapidPublic.trim(),
    });
    setStatus('saved');
    setTimeout(() => setStatus('idle'), 1500);
  };

  const autoFillPushFromRelay = async () => {
    if (!pushRelayUrl.trim()) return;
    try {
      // Probe the relay's /.well-known/vapid-public to auto-fill the key.
      // The relay URL is typically `<base>/push`; the metadata lives at
      // the base with `/vapid-public`.
      const base = pushRelayUrl.trim().replace(/\/push\/?$/, '');
      const res = await fetch(`${base}/vapid-public`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json() as { publicKey?: string };
      if (typeof data.publicKey === 'string') setPushVapidPublic(data.publicKey);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('autoFillPushFromRelay failed:', err);
    }
  };

  return (
    <>
      <h2 className="surface-q t-serif">Network config</h2>
      <p className="surface-sub">
        Operator-supplied infrastructure: bootstrap peer multiaddrs (for
        cross-NAT mesh discovery) and TURN credentials (for high-sensitivity
        voice calls). Stored locally; never transmitted.
      </p>

      <h3 className="t-meta" style={{ marginTop: 14 }}>libp2p bootstrap peers</h3>
      <textarea
        value={bootstrapPeers}
        onChange={(e) => setBootstrapPeers(e.target.value)}
        rows={3}
        placeholder="/dns4/alds-bootstrap.bizlaunch.now/tcp/443/wss/p2p/12D3KooW..."
        style={{ width: '100%', resize: 'vertical', fontFamily: 'var(--mono)', fontSize: 11 }}
      />
      <div className="t-meta" style={{ color: 'var(--ink-dim)', fontSize: 9, marginTop: 4 }}>
        one multiaddr per line · lines starting with # are ignored
      </div>

      <h3 className="t-meta" style={{ marginTop: 16 }}>TURN server (for sensitivity ≥ medium)</h3>
      <label htmlFor="turn-url" className="t-meta">url</label>
      <input
        id="turn-url"
        type="text"
        value={turnUrl}
        onChange={(e) => setTurnUrl(e.target.value)}
        placeholder="turn:alds-turn.bizlaunch.now:3478"
        style={{ width: '100%' }}
      />
      <label htmlFor="turn-realm" className="t-meta" style={{ marginTop: 8, display: 'block' }}>realm</label>
      <input
        id="turn-realm"
        type="text"
        value={turnRealm}
        onChange={(e) => setTurnRealm(e.target.value)}
        placeholder="alds.bizlaunch.now"
        style={{ width: '100%' }}
      />
      <label htmlFor="turn-secret" className="t-meta" style={{ marginTop: 8, display: 'block' }}>shared secret</label>
      <input
        id="turn-secret"
        type="password"
        value={turnSecret}
        onChange={(e) => setTurnSecret(e.target.value)}
        placeholder="(hex string from your operator)"
        style={{ width: '100%' }}
      />

      <h3 className="t-meta" style={{ marginTop: 16 }}>default call sensitivity</h3>
      <div className="pill-row">
        {(['low', 'medium', 'high'] as const).map((s) => (
          <button
            key={s}
            className={`pill ${defaultSensitivity === s ? 'pill-on' : ''}`}
            onClick={() => setDefaultSensitivity(s)}
          >{s}</button>
        ))}
      </div>
      <div className="t-meta" style={{ color: 'var(--ink-dim)', fontSize: 9, marginTop: 4 }}>
        low = direct + public STUN · medium = host + operator STUN · high = TURN relay only
      </div>

      <h3 className="t-meta" style={{ marginTop: 16 }}>Web Push relay (Phase 12.5)</h3>
      <label htmlFor="push-relay-url" className="t-meta">relay url</label>
      <input
        id="push-relay-url"
        type="text"
        value={pushRelayUrl}
        onChange={(e) => setPushRelayUrl(e.target.value)}
        placeholder="https://alds-push.bizlaunch.now/push"
        style={{ width: '100%' }}
      />
      <label htmlFor="push-vapid" className="t-meta" style={{ marginTop: 8, display: 'block' }}>VAPID public key</label>
      <input
        id="push-vapid"
        type="text"
        value={pushVapidPublic}
        onChange={(e) => setPushVapidPublic(e.target.value)}
        placeholder="(base64url public key from relay)"
        style={{ width: '100%', fontFamily: 'var(--mono)', fontSize: 11 }}
      />
      <div style={{ marginTop: 6, display: 'flex', gap: 6 }}>
        <button
          className="pill"
          onClick={() => void autoFillPushFromRelay()}
          disabled={!pushRelayUrl.trim()}
        >fetch VAPID from relay</button>
      </div>
      <div className="t-meta" style={{ color: 'var(--ink-dim)', fontSize: 9, marginTop: 4 }}>
        relay forwards encrypted pushes to FCM/APNs without seeing sender or plaintext.
        VAPID public key gets handed to PushManager.subscribe() at subscription time.
      </div>

      <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
        <button className="cta" onClick={() => void save()}>save</button>
        <button className="cta secondary" onClick={() => director.pop('cancel')}>back</button>
      </div>
      {status === 'saved' && (
        <div className="receipt-mini"><div className="t-meta">saved · applies to next call</div></div>
      )}
      <SurfaceStyles />
    </>
  );
}

// ─── Phase 18: KeyStorageConfig ────────────────────────────

function KeyStorageConfig({ ctx, director }: SurfaceProps): JSX.Element {
  const [available, setAvailable] = useState<Array<{ kind: string; available: boolean }>>([]);
  const [current, setCurrent] = useState<{
    id: string; storageKind: string; attestedAt: string; label?: string;
    rotatedFromAttestationId?: string;
  } | undefined>(undefined);
  const [history, setHistory] = useState<Array<{
    id: string; storageKind: string; attestedAt: string; label?: string;
    rotatedFromAttestationId?: string;
  }>>([]);
  const [label, setLabel] = useState('');
  const [status, setStatus] = useState<'idle' | 'working' | 'done' | 'error'>('idle');
  const [err, setErr] = useState('');

  const refresh = async () => {
    const { KeyStorageManager } = await import('../../identity/KeyStorageManager');
    const mgr = new KeyStorageManager(ctx);
    setAvailable(await mgr.detectAvailable());
    setCurrent(await mgr.getCurrentAttestation());
    setHistory(await mgr.listAttestations());
  };

  useEffect(() => { void refresh(); }, [ctx]);

  const select = async (kind: 'secure-enclave' | 'software-keyed') => {
    setStatus('working');
    setErr('');
    try {
      const { KeyStorageManager } = await import('../../identity/KeyStorageManager');
      const mgr = new KeyStorageManager(ctx);
      await mgr.selectStorage({ kind, ...(label.trim() !== '' ? { label: label.trim() } : {}) });
      setLabel('');
      setStatus('done');
      await refresh();
    } catch (e) {
      setStatus('error');
      setErr(e instanceof Error ? e.message : String(e));
    }
  };

  const rotate = async () => {
    setStatus('working');
    setErr('');
    try {
      const { KeyStorageManager } = await import('../../identity/KeyStorageManager');
      const mgr = new KeyStorageManager(ctx);
      await mgr.rotateKey(label.trim() !== '' ? { label: label.trim() } : {});
      setLabel('');
      setStatus('done');
      await refresh();
    } catch (e) {
      setStatus('error');
      setErr(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <>
      <h2 className="surface-q t-serif">Key storage</h2>
      <p className="surface-sub">
        Decide which backend protects your identity keys. Secure-enclave
        binds them to your device's hardware (TouchID, Windows Hello,
        StrongBox). Software-keyed is the legacy path — keys in
        IndexedDB protected only by your password.
      </p>

      <h3 className="t-meta" style={{ marginTop: 16 }}>Available backends</h3>
      {available.map((b) => (
        <div className="row" key={b.kind}>
          <span className="key">{b.kind}</span>
          <span className="val" style={{ color: b.available ? 'var(--phos)' : 'var(--ink-dim)' }}>
            {b.available ? 'available' : 'unavailable'}
          </span>
        </div>
      ))}

      <h3 className="t-meta" style={{ marginTop: 16 }}>Current</h3>
      {current === undefined && <div className="t-meta" style={{ color: 'var(--ink-dim)' }}>not set</div>}
      {current !== undefined && (
        <div className="receipt-mini">
          <div className="row"><span className="key">backend</span><span className="val">{current.storageKind}</span></div>
          {current.label && <div className="row"><span className="key">label</span><span className="val">{current.label}</span></div>}
          <div className="row"><span className="key">attested</span><span className="val">{ago(current.attestedAt)}</span></div>
          {current.rotatedFromAttestationId && (
            <div className="row"><span className="key">rotated from</span><span className="val">{current.rotatedFromAttestationId.slice(0, 8)}…</span></div>
          )}
        </div>
      )}

      <h3 className="t-meta" style={{ marginTop: 16 }}>Pick or rotate</h3>
      <input
        type="text" placeholder="optional label (e.g. 'MacBook TouchID')"
        value={label} onChange={(e) => setLabel(e.target.value)}
        style={{ width: '100%', padding: 6 }}
      />
      <div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button className="pill" onClick={() => void select('secure-enclave')} disabled={status === 'working'}>use secure-enclave</button>
        <button className="pill" onClick={() => void select('software-keyed')} disabled={status === 'working'}>use software-keyed</button>
        <button className="pill" onClick={() => void rotate()} disabled={status === 'working' || current === undefined}>rotate</button>
      </div>

      {history.length > 1 && (
        <>
          <h3 className="t-meta" style={{ marginTop: 16 }}>History</h3>
          {history.map((h) => (
            <div key={h.id} className="item-row" style={{ ['--env-color' as string]: 'var(--rule)' } as React.CSSProperties}>
              <div>
                <div className="item-main">{h.storageKind}{h.label ? ` · ${h.label}` : ''}</div>
                <div className="item-meta">{ago(h.attestedAt)}{h.rotatedFromAttestationId ? ' · rotated from prior' : ''}</div>
              </div>
            </div>
          ))}
        </>
      )}

      <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
        <button className="cta secondary" onClick={() => director.pop('back')}>back</button>
      </div>
      {status === 'error' && (
        <div className="receipt-mini" style={{ borderColor: 'var(--amber)' }}>
          <div className="t-meta">error: {err}</div>
        </div>
      )}
      <SurfaceStyles />
    </>
  );
}

// ─── Phase 17: PluginPicker ────────────────────────────────

function PluginPicker({ ctx, director }: SurfaceProps): JSX.Element {
  const [installed, setInstalled] = useState<Array<{ id: string; name: string; status: string; version: string }>>([]);
  const [activeId, setActiveId] = useState<string | undefined>(undefined);
  const [customJson, setCustomJson] = useState('');
  const [status, setStatus] = useState<'idle' | 'working' | 'done' | 'error'>('idle');
  const [err, setErr] = useState('');

  const refresh = async () => {
    const { PluginManager } = await import('../../plugins/PluginManager');
    const pm = new PluginManager(ctx);
    const rows = await pm.listInstalled();
    const active = await pm.getActive();
    setInstalled(rows.map((r) => {
      let parsedName = r.id;
      try {
        parsedName = (JSON.parse(r.manifestJson) as { name?: string }).name ?? r.id;
      } catch { /* keep id */ }
      return { id: r.id, name: parsedName, status: r.status, version: r.version };
    }));
    setActiveId(active?.id);
  };

  useEffect(() => { void refresh(); }, [ctx]);

  const installBuiltin = async (key: 'cert-volunteer' | 'wildland-fire' | 'college-community' | 'mutual-aid-basic') => {
    setStatus('working');
    setErr('');
    try {
      const { PluginManager } = await import('../../plugins/PluginManager');
      const { BUILTIN_PLUGINS } = await import('../../plugins/builtins');
      const manifest = BUILTIN_PLUGINS.find((m) => m.id === key);
      if (!manifest) throw new Error(`unknown builtin ${key}`);
      const pm = new PluginManager(ctx);
      await pm.installBuiltin(manifest, { activate: true });
      setStatus('done');
      await refresh();
    } catch (e) {
      setStatus('error');
      setErr(e instanceof Error ? e.message : String(e));
    }
  };

  const activate = async (id: string) => {
    const { PluginManager } = await import('../../plugins/PluginManager');
    const pm = new PluginManager(ctx);
    await pm.activate(id);
    await refresh();
  };

  const deactivate = async (id: string) => {
    const { PluginManager } = await import('../../plugins/PluginManager');
    const pm = new PluginManager(ctx);
    await pm.deactivate(id);
    await refresh();
  };

  const uninstall = async (id: string) => {
    const { PluginManager } = await import('../../plugins/PluginManager');
    const pm = new PluginManager(ctx);
    await pm.uninstall(id);
    await refresh();
  };

  const installCustom = async () => {
    setStatus('working');
    setErr('');
    try {
      const { PluginManager } = await import('../../plugins/PluginManager');
      const manifest = JSON.parse(customJson);
      const pm = new PluginManager(ctx);
      await pm.install({ manifest, activate: true });
      setCustomJson('');
      setStatus('done');
      await refresh();
    } catch (e) {
      setStatus('error');
      setErr(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <>
      <h2 className="surface-q t-serif">Jurisdiction plugins</h2>
      <p className="surface-sub">
        Pick a plugin that matches your community. Determines the intake
        categories the caller sees. Built-in plugins are read-only manifests;
        custom plugins can be installed by pasting signed JSON.
      </p>

      <h3 className="t-meta" style={{ marginTop: 16 }}>Installed</h3>
      {installed.length === 0 && <div className="t-meta" style={{ color: 'var(--ink-dim)' }}>none — using built-in default (mutual-aid-basic)</div>}
      {installed.map((p) => (
        <div key={p.id} className="item-row" style={{ ['--env-color' as string]: p.id === activeId ? 'var(--phos)' : 'var(--rule)' } as React.CSSProperties}>
          <div>
            <div className="item-main">
              {p.name}
              {p.id === activeId && <span className="pill pill-on" style={{ marginLeft: 8 }}>ACTIVE</span>}
            </div>
            <div className="item-meta">{p.id} · v{p.version} · {p.status}</div>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            {p.id !== activeId && <button className="pill" onClick={() => void activate(p.id)}>activate</button>}
            {p.id === activeId && <button className="pill" onClick={() => void deactivate(p.id)}>deactivate</button>}
            <button className="pill" onClick={() => void uninstall(p.id)}>uninstall</button>
          </div>
        </div>
      ))}

      <h3 className="t-meta" style={{ marginTop: 16 }}>Built-in plugins</h3>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        <button className="pill" onClick={() => void installBuiltin('mutual-aid-basic')}>+ mutual-aid-basic</button>
        <button className="pill" onClick={() => void installBuiltin('cert-volunteer')}>+ CERT volunteer</button>
        <button className="pill" onClick={() => void installBuiltin('wildland-fire')}>+ wildland fire</button>
        <button className="pill" onClick={() => void installBuiltin('college-community')}>+ college community</button>
      </div>

      <h3 className="t-meta" style={{ marginTop: 16 }}>Custom manifest (JSON)</h3>
      <textarea
        value={customJson}
        onChange={(e) => setCustomJson(e.target.value)}
        placeholder='{"id":"my-plugin","name":"...","author":"...","version":"1.0.0","categories":[...]}'
        style={{ width: '100%', height: 120, padding: 6, fontFamily: 'var(--mono)', fontSize: 11 }}
      />
      <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
        <button className="cta" onClick={() => void installCustom()} disabled={status === 'working' || customJson.trim() === ''}>install + activate</button>
        <button className="cta secondary" onClick={() => director.pop('back')}>back</button>
      </div>
      {status === 'error' && (
        <div className="receipt-mini" style={{ borderColor: 'var(--amber)' }}>
          <div className="t-meta">error: {err}</div>
        </div>
      )}
      <SurfaceStyles />
    </>
  );
}

// ─── Phase 15: BulletinConfig + BulletinPublished ─────────

function BulletinConfig({ ctx, director }: SurfaceProps): JSX.Element {
  const [label, setLabel] = useState('');
  const [url, setUrl] = useState('');
  const [authKind, setAuthKind] = useState<'pinata' | 'web3-storage' | 'ipfs-cluster' | 'public-gateway'>('pinata');
  const [token, setToken] = useState('');
  const [status, setStatus] = useState<'idle' | 'working' | 'done' | 'error'>('idle');
  const [err, setErr] = useState('');
  const [endpoints, setEndpoints] = useState<Array<{ id: string; label: string; url: string; authKind: string }>>([]);

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      const { PinningManager } = await import('../../bulletins/PinningManager');
      const pm = new PinningManager(ctx);
      const list = await pm.listEndpoints();
      if (!cancelled) setEndpoints(list);
    };
    void tick();
    return () => { cancelled = true; };
  }, [ctx]);

  const refresh = async () => {
    const { PinningManager } = await import('../../bulletins/PinningManager');
    const pm = new PinningManager(ctx);
    setEndpoints(await pm.listEndpoints());
  };

  const add = async () => {
    if (!label.trim() || !url.trim()) { setErr('label + url required'); setStatus('error'); return; }
    setStatus('working');
    setErr('');
    try {
      const { PinningManager } = await import('../../bulletins/PinningManager');
      const pm = new PinningManager(ctx);
      await pm.addEndpoint({ label: label.trim(), url: url.trim(), authKind });
      setLabel(''); setUrl('');
      setStatus('done');
      await refresh();
    } catch (e) {
      setStatus('error');
      setErr(e instanceof Error ? e.message : String(e));
    }
  };

  const publish = async (endpointId: string) => {
    setStatus('working');
    setErr('');
    try {
      const { PinningManager } = await import('../../bulletins/PinningManager');
      const pm = new PinningManager(ctx);
      const r = await pm.publishBulletin({
        endpointId,
        ...(token.trim() !== '' ? { token: token.trim() } : {}),
      });
      setStatus('done');
      setErr(`published cid: ${r.cid}`);
    } catch (e) {
      setStatus('error');
      setErr(e instanceof Error ? e.message : String(e));
    }
  };

  const remove = async (endpointId: string) => {
    const { PinningManager } = await import('../../bulletins/PinningManager');
    const pm = new PinningManager(ctx);
    await pm.removeEndpoint(endpointId);
    await refresh();
  };

  return (
    <>
      <h2 className="surface-q t-serif">Bulletin pinning · configure endpoints</h2>
      <p className="surface-sub">
        Add one or more IPFS pinning services. Publishing a bulletin POSTs
        the redacted day-log to the selected endpoint and records the CID.
      </p>

      <h3 className="t-meta" style={{ marginTop: 16 }}>Configured endpoints</h3>
      {endpoints.length === 0 && <div className="t-meta" style={{ color: 'var(--ink-dim)' }}>none</div>}
      {endpoints.map((ep) => (
        <div key={ep.id} className="item-row" style={{ ['--env-color' as string]: 'var(--phos)' } as React.CSSProperties}>
          <div>
            <div className="item-main">{ep.label}</div>
            <div className="item-meta">{ep.authKind} · {ep.url}</div>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button className="pill" onClick={() => void publish(ep.id)}>publish now</button>
            <button className="pill" onClick={() => void remove(ep.id)}>remove</button>
          </div>
        </div>
      ))}

      <h3 className="t-meta" style={{ marginTop: 16 }}>Add endpoint</h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 6 }}>
        <input
          type="text" placeholder="label (e.g. 'primary pinata')"
          value={label} onChange={(e) => setLabel(e.target.value)}
          style={{ padding: 6 }}
        />
        <input
          type="text" placeholder="url (e.g. https://api.pinata.cloud)"
          value={url} onChange={(e) => setUrl(e.target.value)}
          style={{ padding: 6 }}
        />
        <select value={authKind} onChange={(e) => setAuthKind(e.target.value as 'pinata' | 'web3-storage' | 'ipfs-cluster' | 'public-gateway')} style={{ padding: 6 }}>
          <option value="pinata">pinata</option>
          <option value="web3-storage">web3.storage</option>
          <option value="ipfs-cluster">ipfs-cluster</option>
          <option value="public-gateway">public-gateway (read-only)</option>
        </select>
        <input
          type="password" placeholder="pin token (for publish; never stored)"
          value={token} onChange={(e) => setToken(e.target.value)}
          style={{ padding: 6 }}
        />
      </div>
      <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
        <button className="cta" onClick={() => void add()} disabled={status === 'working'}>add endpoint</button>
        <button className="cta secondary" onClick={() => director.spawn('bulletin-published', 'published bulletins')}>history</button>
        <button className="cta secondary" onClick={() => director.pop('back')}>back</button>
      </div>
      {status === 'done' && err && <div className="receipt-mini"><div className="t-meta">{err}</div></div>}
      {status === 'error' && (
        <div className="receipt-mini" style={{ borderColor: 'var(--amber)' }}>
          <div className="t-meta">error: {err}</div>
        </div>
      )}
      <SurfaceStyles />
    </>
  );
}

function BulletinPublished({ ctx, director }: SurfaceProps): JSX.Element {
  const [rows, setRows] = useState<Array<{
    cid: string; endpointId: string; windowStart: string; windowEnd: string;
    publishedAt: string; byteSize: number; redactionVersion: number;
  }>>([]);

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      const { PinningManager } = await import('../../bulletins/PinningManager');
      const pm = new PinningManager(ctx);
      const list = await pm.listBulletins({ limit: 50 });
      if (!cancelled) setRows(list);
    };
    void tick();
    const id = setInterval(() => void tick(), 5000);
    return () => { cancelled = true; clearInterval(id); };
  }, [ctx]);

  return (
    <>
      <h2 className="surface-q t-serif">Published bulletins</h2>
      <p className="surface-sub">
        Most recent first. CIDs are content-addressed: anyone with the CID
        can fetch + verify the bulletin independently via any IPFS gateway.
      </p>
      {rows.length === 0 && <div className="empty-state t-meta">none published yet</div>}
      {rows.map((r) => (
        <div
          key={r.cid}
          className="item-row"
          style={{ ['--env-color' as string]: 'var(--phos)' } as React.CSSProperties}
        >
          <div>
            <div className="item-main">{r.cid.slice(0, 24)}…</div>
            <div className="item-meta">
              window {r.windowStart.slice(0, 10)} → {r.windowEnd.slice(0, 10)} · {r.byteSize}b · v{r.redactionVersion}
            </div>
            <div className="item-meta">published {ago(r.publishedAt)}</div>
          </div>
          <button
            className="pill"
            onClick={() => {
              const url = `https://ipfs.io/ipfs/${r.cid}`;
              try { void navigator.clipboard.writeText(url); }
              catch { /* clipboard unavailable */ }
            }}
          >copy URL</button>
        </div>
      ))}
      <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
        <button className="cta secondary" onClick={() => director.pop('back')}>back</button>
      </div>
      <SurfaceStyles />
    </>
  );
}

// ─── helpers ──────────────────────────────────────────────

function useFederatedIncidents(ctx: AppContext): FederatedIncidentRecord[] {
  const [list, setList] = useState<FederatedIncidentRecord[]>([]);
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      const fm = new FederationManager(ctx);
      const rows = await fm.listFederatedIncidents();
      if (!cancelled) setList(rows);
    };
    void tick();
    const id = setInterval(() => void tick(), 2000);
    return () => { cancelled = true; clearInterval(id); };
  }, [ctx]);
  return list;
}

function useIncidents(ctx: AppContext): IncidentMeta[] {
  const [list, setList] = useState<IncidentMeta[]>([]);
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      const im = new IncidentManager(ctx);
      const incidents = await im.listIncidents({ limit: 50 });
      if (!cancelled) setList(incidents);
    };
    void tick();
    const id = setInterval(() => void tick(), 2000);
    return () => { cancelled = true; clearInterval(id); };
  }, [ctx]);
  return list;
}

function ago(iso: string): string {
  const seconds = Math.max(0, Math.floor((Date.now() - Date.parse(iso)) / 1000));
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}

/**
 * Phase 53.b: format a hex pubkey as a human-verifiable fingerprint.
 * Takes the first 128 bits (32 hex chars) and chunks them into 8 groups of
 * 4 separated by hyphens. 128 bits is collision-resistant enough for an
 * adversary that has to forge an Ed25519 keypair AND match the prefix the
 * user reads aloud, while still being short enough to dictate over a call.
 */
function formatFingerprint(pubkeyHex: string): string {
  const clean = pubkeyHex.toLowerCase().replace(/[^0-9a-f]/g, '');
  const prefix = clean.slice(0, 32).padEnd(32, '0');
  const groups: string[] = [];
  for (let i = 0; i < prefix.length; i += 4) groups.push(prefix.slice(i, i + 4));
  return groups.join('-');
}

// ─── shared scoped styles ─────────────────────────────────

function SurfaceStyles(): JSX.Element {
  return (
    <style>{`
      .surface-q { font-size: 30px; line-height: 1.1; margin: 0 0 8px; }
      .surface-sub { font-size: 11px; color: var(--ink-dim); line-height: 1.6; margin: 0 0 24px; max-width: 480px; }
      @media (max-width: 600px) {
        .surface-q { font-size: 22px; margin: 0 0 6px; }
        .surface-sub { font-size: 11px; margin: 0 0 16px; }
      }

      .hub-grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 12px;
      }
      /* U1.1 — Phase U1 mobile fit: KEEP the 2×2 grid on phones so all
       * four role cards fit above the fold. Cards shrink to compensate
       * for the narrower column width. Previously the grid collapsed to
       * 1×4 at 700px which pushed Responder + Supervisor off-screen on
       * every mobile viewport. The collapse to 1-column now only fires
       * on tiny devices (<360px wide). */
      @media (max-width: 700px) {
        .hub-grid { gap: 10px; }
        .env-card { padding: 12px 10px; }
        .env-glyph { font-size: 28px; margin-bottom: 4px; }
        .env-label { font-size: 15px; }
        .env-desc { font-size: 10px; line-height: 1.3; }
      }
      @media (max-width: 360px) { .hub-grid { grid-template-columns: 1fr; } }
      .env-card {
        padding: 20px;
        border: 1px solid var(--rule);
        background: rgba(22, 28, 32, 0.4);
        cursor: pointer;
        transition: all 250ms var(--surface-ease);
        position: relative;
        overflow: hidden;
      }
      .env-card:hover {
        background: rgba(22, 28, 32, 0.8);
        transform: translateY(-2px);
      }
      .env-card::after {
        content: '';
        position: absolute;
        inset: 0;
        background: linear-gradient(135deg, var(--env-color), transparent 60%);
        opacity: 0;
        transition: opacity 250ms ease;
        pointer-events: none;
        mix-blend-mode: overlay;
      }
      .env-card:hover::after { opacity: 0.15; }
      .env-glyph {
        font-family: var(--serif);
        font-size: 42px;
        line-height: 1;
        color: var(--env-color);
        text-shadow: 0 0 24px var(--env-color);
        margin-bottom: 14px;
        display: block;
      }
      .env-label { font-family: var(--serif); font-size: 20px; margin-bottom: 6px; }
      .env-desc { font-size: 10px; color: var(--ink-dim); line-height: 1.6; }

      .env-toolbar {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding-bottom: 14px;
        margin-bottom: 14px;
        border-bottom: 1px dashed var(--rule);
      }
      .env-toolbar-title { font-family: var(--serif); font-size: 18px; }
      .env-sublabel { margin-bottom: 10px; }

      .item-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 12px 14px;
        border: 1px solid var(--rule);
        margin-bottom: 4px;
        font-size: 11px;
        cursor: pointer;
        transition: all 180ms ease;
      }
      .item-row:hover { border-color: var(--env-color); padding-left: 18px; }
      .item-main { color: var(--ink); }
      .item-meta { font-size: 9px; color: var(--ink-dim); letter-spacing: 0.15em; text-transform: uppercase; }

      .empty-state {
        padding: 14px 18px;
        border: 1px dashed var(--rule);
        color: var(--ink-dim);
        font-style: italic;
      }

      .status-dot {
        display: inline-block;
        width: 6px; height: 6px;
        border-radius: 50%;
        margin-right: 8px;
      }
      .status-online { background: var(--phos); box-shadow: 0 0 6px var(--phos); }
      .status-offline { background: var(--ink-dim); }
      .status-in-call { background: var(--amber); box-shadow: 0 0 6px var(--amber); }

      .sev-pill {
        display: inline-block;
        width: 8px; height: 8px;
        margin-right: 10px;
        vertical-align: middle;
      }
      .sev-life-threat { background: #ff5b6b; box-shadow: 0 0 6px #ff5b6b; }
      .sev-urgent      { background: var(--amber); box-shadow: 0 0 6px var(--amber); }
      .sev-assistance  { background: var(--phos); box-shadow: 0 0 6px var(--phos); }
      .sev-info        { background: var(--blue); }

      .pill-row { display: flex; flex-wrap: wrap; gap: 6px; }
      .pill {
        padding: 5px 10px;
        font-size: 10px;
        letter-spacing: 0.15em;
        text-transform: uppercase;
        border: 1px solid var(--rule);
        background: transparent;
        color: var(--ink-dim);
        cursor: pointer;
      }
      .pill:hover { border-color: var(--phos); color: var(--phos); }
      .pill-on { border-color: var(--phos); color: var(--phos); background: rgba(127, 255, 159, 0.06); }

      .row { display: flex; justify-content: space-between; padding: 4px 0; }
      .row .key {
        color: var(--ink-dim);
        font-size: 10px;
        letter-spacing: 0.15em;
        text-transform: uppercase;
      }
      .row .val { color: var(--phos); font-size: 11px; }

      .receipt-mini {
        margin-top: 16px;
        padding: 10px 12px;
        border: 1px solid var(--phos);
        background: rgba(127, 255, 159, 0.04);
      }
      .receipt-hash {
        font-family: var(--mono);
        font-size: 11px;
        color: var(--phos);
        margin-top: 4px;
        word-break: break-all;
      }
    `}</style>
  );
}

// ─── Phase 27.1 — ADSCS surfaces (env-social, social-feed, group config) ──

function SocialEnv({ ctx, director }: SurfaceProps): JSX.Element {
  const [groups, setGroups] = useState<SocialGroupRecord[]>([]);
  const [unreadMentions, setUnreadMentions] = useState<number>(0);
  const [unreadByGroup, setUnreadByGroup] = useState<Record<string, number>>({});
  const [mentionsList, setMentionsList] = useState<Array<{ id: string; messageHash: string; groupId: string; receivedAt: string }>>([]);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [searchHits, setSearchHits] = useState<Array<{ groupId: string; groupName: string; messageHash: string; snippet: string; matchedAt: string }>>([]);
  const [showMentions, setShowMentions] = useState<boolean>(false);

  const refresh = async (): Promise<void> => {
    const mgr = getSocialGroupManager(ctx);
    // Pull pending hydration before we count.
    await mgr.ingestIncomingMentions().catch(() => { /* swallow */ });
    const [gs, ms, byGroup] = await Promise.all([
      mgr.listGroups(),
      mgr.listMyMentions({ onlyUnread: true }),
      mgr.getUnreadCountsByGroup(),
    ]);
    setGroups(gs);
    setUnreadMentions(ms.length);
    setMentionsList(ms.map((m) => ({ id: m.id, messageHash: m.messageHash, groupId: m.groupId, receivedAt: m.receivedAt })));
    setUnreadByGroup(byGroup);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try { await refresh(); }
      finally { if (cancelled) { /* noop */ } }
    })();
    return () => { cancelled = true; };
  /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [ctx]);

  const runSearch = async (): Promise<void> => {
    if (searchQuery.trim().length === 0) { setSearchHits([]); return; }
    try {
      const hits = await getSocialGroupManager(ctx).search({ query: searchQuery.trim() });
      setSearchHits(hits);
    } catch {
      setSearchHits([]);
    }
  };

  const openGroup = (groupId: string, name: string): void => {
    try { localStorage.setItem('alds-selected-social-group', groupId); } catch { /* noop */ }
    director.push('social-feed', name);
  };

  return (
    <EnvFrame title="Social" glyph="◐" color="var(--blue)" director={director}>
      <div className="env-toolbar" style={{ borderBottom: 'none', marginBottom: 8 }}>
        <div className="t-meta env-sublabel">
          {groups.length} group{groups.length === 1 ? '' : 's'}
          {' · '}
          <span
            role={unreadMentions > 0 ? 'button' : undefined}
            tabIndex={unreadMentions > 0 ? 0 : -1}
            style={unreadMentions > 0 ? { cursor: 'pointer', color: 'var(--amber)' } : undefined}
            onClick={() => { if (unreadMentions > 0) setShowMentions((v) => !v); }}
            onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && unreadMentions > 0) setShowMentions((v) => !v); }}
          >
            {unreadMentions} unread mention{unreadMentions === 1 ? '' : 's'}
          </span>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="pill" onClick={() => director.bridge('social-channel', 'channel view')}>channel</button>
          <button className="pill" onClick={() => director.push('social-broadcast', 'broadcast')}>broadcast</button>
          <button className="pill" onClick={() => director.push('social-gallery', 'gallery')}>gallery</button>
          <button className="pill" onClick={() => director.push('social-stories', 'stories')}>stories</button>
          <button className="pill" onClick={() => director.push('social-compose', 'rich compose')}>✎ compose</button>
          <button className="pill" onClick={() => director.push('social-group-config', 'new group')}>+ new group</button>
        </div>
      </div>

      {/* Inline mentions panel */}
      {showMentions && mentionsList.length > 0 && (
        <div style={{ padding: '0 12px 8px', borderBottom: '1px solid var(--phos)' }}>
          <div className="t-meta env-sublabel" style={{ marginBottom: 4 }}>unread mentions</div>
          {mentionsList.map((m) => {
            const groupName = groups.find((g) => g.id === m.groupId)?.name ?? m.groupId.slice(0, 8);
            return (
              <div
                key={m.id}
                role="button"
                tabIndex={0}
                onClick={() => {
                  getSocialGroupManager(ctx).markMentionRead(m.id).catch(() => { /* noop */ });
                  openGroup(m.groupId, groupName);
                }}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') openGroup(m.groupId, groupName); }}
                style={{ padding: '4px 0', cursor: 'pointer', fontFamily: 'var(--mono)', fontSize: 12 }}
              >
                @ in #{groupName} · {new Date(m.receivedAt).toLocaleString()}
              </div>
            );
          })}
        </div>
      )}

      {/* Search bar */}
      <div style={{ padding: '0 12px 8px', display: 'flex', gap: 6 }}>
        <input
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { runSearch().catch(() => { /* swallow */ }); } }}
          placeholder="Search posts…"
          style={{ flex: 1, background: 'transparent', color: 'var(--phos)', border: '1px solid var(--phos)', padding: 4, fontFamily: 'var(--mono)', fontSize: 12 }}
        />
        <button className="pill" onClick={() => { runSearch().catch(() => { /* swallow */ }); }}>find</button>
        {searchHits.length > 0 && (
          <button className="pill" onClick={() => { setSearchHits([]); setSearchQuery(''); }}>clear</button>
        )}
      </div>
      {searchHits.length > 0 && (
        <div style={{ padding: '0 12px 8px', borderBottom: '1px solid var(--phos)' }}>
          <div className="t-meta env-sublabel">{searchHits.length} hit{searchHits.length === 1 ? '' : 's'}</div>
          {searchHits.map((h) => (
            <div
              key={h.messageHash}
              role="button"
              tabIndex={0}
              onClick={() => openGroup(h.groupId, h.groupName)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') openGroup(h.groupId, h.groupName); }}
              style={{ padding: '4px 0', cursor: 'pointer' }}
            >
              <div className="t-meta env-sublabel">#{h.groupName} · {new Date(h.matchedAt).toLocaleString()}</div>
              <div style={{ fontSize: 12 }}>{h.snippet}</div>
            </div>
          ))}
        </div>
      )}

      {groups.length === 0 ? (
        <div className="surface-sub" style={{ padding: 12 }}>
          No social groups yet. Create one to start a feed, channel, or broadcast.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {groups.map((g) => {
            const unread = unreadByGroup[g.id] ?? 0;
            return (
              <div
                key={g.id}
                className="item-row"
                role="button"
                tabIndex={0}
                style={{ ['--env-color' as string]: 'var(--blue)' } as React.CSSProperties}
                onClick={() => openGroup(g.id, g.name)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') openGroup(g.id, g.name); }}
              >
                <div>
                  <div className="item-main">
                    {g.name}
                    {unread > 0 && (
                      <span
                        style={{
                          marginLeft: 8,
                          padding: '0 6px',
                          background: 'var(--blue)',
                          color: 'var(--bg)',
                          borderRadius: 8,
                          fontSize: 10,
                          fontWeight: 700,
                        }}
                      >
                        {unread > 99 ? '99+' : unread}
                      </span>
                    )}
                  </div>
                  <div className="item-meta">
                    {g.kind} · {g.visibility} · {g.senderModel}
                    {g.federationLinkIds.length > 0 ? ` · 🔗 ${g.federationLinkIds.length} link${g.federationLinkIds.length === 1 ? '' : 's'}` : ''}
                    {g.description !== undefined ? ` · ${g.description}` : ''}
                  </div>
                </div>
                <div className="item-meta" aria-hidden="true">→</div>
              </div>
            );
          })}
        </div>
      )}
      <SurfaceStyles />
    </EnvFrame>
  );
}

type SocialMessage = { messageHash: string; senderPubkey: string; sentAt: string; payload: SocialPostPayload; replyCount?: number; hidden?: boolean };

const SLASH_HELP_TEXT = [
  '/me <text>     — emote-style action post',
  '/help          — show this help (not published)',
].join('\n');

function applySlashCommand(raw: string): { kind: 'publish'; text: string } | { kind: 'help' } | { kind: 'noop' } {
  const trimmed = raw.trim();
  if (trimmed.length === 0) return { kind: 'noop' };
  if (trimmed === '/help') return { kind: 'help' };
  if (trimmed.startsWith('/me ')) {
    const rest = trimmed.slice(4).trim();
    if (rest.length === 0) return { kind: 'noop' };
    return { kind: 'publish', text: `* ${rest}` };  // leading `*` = emote marker
  }
  return { kind: 'publish', text: trimmed };
}

function SocialFeed({ ctx, director }: SurfaceProps): JSX.Element {
  const groupId = (() => {
    try { return localStorage.getItem('alds-selected-social-group') ?? ''; } catch { return ''; }
  })();
  const [group, setGroup] = useState<SocialGroupRecord | undefined>(undefined);
  const [isMod, setIsMod] = useState<boolean>(false);
  const [showHidden, setShowHidden] = useState<boolean>(false);
  const [messages, setMessages] = useState<SocialMessage[]>([]);
  const [repliesByHash, setRepliesByHash] = useState<Record<string, SocialMessage[]>>({});
  const [pollTallies, setPollTallies] = useState<Record<string, { counts: number[]; total: number; options: string[] }>>({});
  const [composer, setComposer] = useState<string>('');
  const [replyTarget, setReplyTarget] = useState<SocialMessage | undefined>(undefined);
  const [helpVisible, setHelpVisible] = useState<boolean>(false);
  const [status, setStatus] = useState<'idle' | 'sending' | 'error'>('idle');
  const [lastError, setLastError] = useState<string>('');

  const refresh = async (): Promise<void> => {
    if (groupId === '') return;
    const mgr = getSocialGroupManager(ctx);
    const mod = await mgr.isModerator(groupId);
    setIsMod(mod);
    const [g, ms] = await Promise.all([
      mgr.getGroup(groupId),
      mgr.listMessages({ groupId, includeReplyCount: true, includeHidden: mod && showHidden }),
    ]);
    setGroup(g);
    setMessages(ms);
    // Fetch replies for each top-level post that has any.
    const replies: Record<string, SocialMessage[]> = {};
    await Promise.all(
      ms
        .filter((m) => (m.replyCount ?? 0) > 0)
        .map(async (m) => {
          replies[m.messageHash] = await mgr.listReplies({ groupId, parentHash: m.messageHash, includeHidden: mod && showHidden });
        }),
    );
    setRepliesByHash(replies);
    // Tally any polls.
    const tallies: Record<string, { counts: number[]; total: number; options: string[] }> = {};
    await Promise.all(
      ms
        .filter((m) => (m.payload.pollOptions?.length ?? 0) >= 2)
        .map(async (m) => {
          try {
            tallies[m.messageHash] = await mgr.tallyPoll({ groupId, pollHash: m.messageHash });
          } catch { /* skip */ }
        }),
    );
    setPollTallies(tallies);
    // Clear unread badge on this group + hydrate any incoming mentions.
    await Promise.all([
      mgr.markRead(groupId).catch(() => { /* swallow */ }),
      mgr.ingestIncomingMentions({ groupId }).catch(() => { /* swallow */ }),
    ]);
  };

  const vote = async (pollHash: string, choice: number): Promise<void> => {
    try {
      await getSocialGroupManager(ctx).votePoll({ groupId, pollHash, choice });
      await refresh();
    } catch (err) {
      setLastError(err instanceof Error ? err.message : String(err));
    }
  };

  useEffect(() => { refresh().catch(() => { /* swallow */ }); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [groupId, showHidden]);

  const hide = async (targetHash: string): Promise<void> => {
    try { await getSocialGroupManager(ctx).hidePost({ groupId, targetHash }); await refresh(); }
    catch (err) { setLastError(err instanceof Error ? err.message : String(err)); }
  };
  const restore = async (targetHash: string): Promise<void> => {
    try { await getSocialGroupManager(ctx).restorePost({ groupId, targetHash }); await refresh(); }
    catch (err) { setLastError(err instanceof Error ? err.message : String(err)); }
  };
  const voteHide = async (targetHash: string): Promise<void> => {
    try { await getSocialGroupManager(ctx).voteHide({ groupId, targetHash }); await refresh(); }
    catch (err) { setLastError(err instanceof Error ? err.message : String(err)); }
  };

  const send = async (): Promise<void> => {
    const action = applySlashCommand(composer);
    if (action.kind === 'noop') return;
    if (action.kind === 'help') {
      setHelpVisible(true);
      setComposer('');
      return;
    }
    setHelpVisible(false);
    setStatus('sending');
    try {
      await getSocialGroupManager(ctx).publish({
        groupId,
        text: action.text,
        ...(replyTarget !== undefined && { parentHash: replyTarget.messageHash }),
      });
      setComposer('');
      setReplyTarget(undefined);
      setStatus('idle');
      await refresh();
    } catch (err) {
      setStatus('error');
      setLastError(err instanceof Error ? err.message : String(err));
    }
  };

  if (groupId === '') {
    return (
      <EnvFrame title="Social Feed" glyph="◐" color="var(--blue)" director={director}>
        <div className="surface-sub" style={{ padding: 12 }}>
          No group selected. Go back and pick one from Social.
        </div>
      </EnvFrame>
    );
  }

  const renderPost = (m: SocialMessage, depth: number): JSX.Element => {
    const isEmote = m.payload.text !== undefined && m.payload.text.startsWith('* ');
    const isHidden = m.hidden === true;
    const sealed = m.payload.sealedSender === true;
    return (
      <div
        key={m.messageHash}
        className="receipt-mini"
        style={{
          marginBottom: 8,
          marginLeft: depth * 16,
          opacity: isHidden ? 0.55 : 1,
          borderStyle: isHidden ? 'dashed' : 'solid',
        }}
      >
        <div className="t-meta env-sublabel">
          {sealed ? <span style={{ color: 'var(--blue)' }}>👤 anonymous member</span> : <>{m.senderPubkey.slice(0, 12)}…</>}
          {' · '}{new Date(m.sentAt).toLocaleString()}
          {isHidden && <span style={{ marginLeft: 8, color: 'var(--pink)' }}>· hidden by moderator</span>}
        </div>
        {isHidden && !isMod ? (
          <div className="surface-sub" style={{ marginTop: 4, fontStyle: 'italic' }}>
            Post hidden by moderator.
          </div>
        ) : (
        <div
          style={{
            marginTop: 4,
            whiteSpace: 'pre-wrap',
            fontStyle: isEmote ? 'italic' : 'normal',
            color: isEmote ? 'var(--amber)' : undefined,
          }}
        >
          {isEmote ? m.payload.text!.slice(2) : (m.payload.text ?? '')}
        </div>
        )}
        {m.payload.tags !== undefined && m.payload.tags.length > 0 && (
          <div className="t-meta env-sublabel" style={{ marginTop: 4 }}>
            {m.payload.tags.map((t) => `#${t}`).join(' ')}
          </div>
        )}
        {m.payload.linkCardUrl !== undefined && (
          <div className="t-meta env-sublabel" style={{ marginTop: 4, color: 'var(--blue)' }}>
            🔗 {m.payload.linkCardUrl}
          </div>
        )}
        {(m.payload.mediaManifestIds?.length ?? 0) > 0 && (() => {
          const ids = m.payload.mediaManifestIds!;
          const voiceNotes = ids.filter((id) => id.startsWith('audio:') || id.startsWith('voice:'));
          const otherMedia = ids.filter((id) => !id.startsWith('audio:') && !id.startsWith('voice:'));
          return (
            <>
              {voiceNotes.map((vId) => (
                <VoiceNotePlayer key={vId} ctx={ctx} manifestRef={vId} groupId={groupId} />
              ))}
              {otherMedia.length > 0 && (
                <div className="t-meta env-sublabel" style={{ marginTop: 4 }}>
                  ▦ {otherMedia.length} attachment{otherMedia.length === 1 ? '' : 's'}
                </div>
              )}
            </>
          );
        })()}
        {(m.payload.pollOptions?.length ?? 0) >= 2 && (
          <div style={{ marginTop: 6 }}>
            {m.payload.pollOptions!.map((opt, i) => {
              const tally = pollTallies[m.messageHash];
              const count = tally?.counts[i] ?? 0;
              const total = tally?.total ?? 0;
              const pct = total > 0 ? Math.round((count / total) * 100) : 0;
              return (
                <div key={i} style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <button
                    className="pill"
                    style={{ flex: 1, textAlign: 'left' }}
                    onClick={() => { vote(m.messageHash, i).catch(() => { /* swallow */ }); }}
                  >
                    {opt}
                  </button>
                  <span className="t-meta env-sublabel" style={{ minWidth: 70, textAlign: 'right' }}>
                    {count} · {pct}%
                  </span>
                </div>
              );
            })}
            <div className="t-meta env-sublabel" style={{ marginTop: 4 }}>
              {pollTallies[m.messageHash]?.total ?? 0} vote{(pollTallies[m.messageHash]?.total ?? 0) === 1 ? '' : 's'}
            </div>
          </div>
        )}
        {depth === 0 && (
          <div style={{ display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
            <button
              className="pill"
              onClick={() => { setReplyTarget(m); setHelpVisible(false); }}
            >
              ↳ reply{(m.replyCount ?? 0) > 0 ? ` (${m.replyCount})` : ''}
            </button>
            {isMod && !isHidden && (
              <button className="pill" onClick={() => { hide(m.messageHash).catch(() => { /* swallow */ }); }}>
                ⊘ hide
              </button>
            )}
            {isMod && isHidden && (
              <button className="pill" onClick={() => { restore(m.messageHash).catch(() => { /* swallow */ }); }}>
                ↺ restore
              </button>
            )}
            {!isMod && group?.moderation === 'community-vote' && !isHidden && (
              <button className="pill" onClick={() => { voteHide(m.messageHash).catch(() => { /* swallow */ }); }}>
                ⊘ vote hide
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <EnvFrame title={group?.name ?? 'Loading…'} glyph="◐" color="var(--blue)" director={director}>
      {group !== undefined && (
        <div className="t-meta env-sublabel" style={{ padding: '4px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{group.kind} · {group.presentationStyle === group.kind ? group.kind : `→ ${group.presentationStyle}`} · {group.visibility} · {group.moderation}</span>
          {isMod && (
            <label style={{ fontSize: 11, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={showHidden}
                onChange={(e) => setShowHidden(e.target.checked)}
                style={{ marginRight: 4 }}
              />
              show hidden
            </label>
          )}
        </div>
      )}
      <div style={{ flex: 1, overflowY: 'auto', padding: 12 }}>
        {messages.length === 0 ? (
          <div className="surface-sub">No posts yet. Be the first.</div>
        ) : (
          messages.map((m) => (
            <React.Fragment key={m.messageHash}>
              {renderPost(m, 0)}
              {repliesByHash[m.messageHash]?.map((r) => renderPost(r, 1))}
            </React.Fragment>
          ))
        )}
      </div>
      {replyTarget !== undefined && (
        <div className="t-meta env-sublabel" style={{ padding: '4px 12px', borderTop: '1px solid var(--phos)' }}>
          Replying to {replyTarget.senderPubkey.slice(0, 12)}…
          <button className="pill" style={{ marginLeft: 8 }} onClick={() => setReplyTarget(undefined)}>cancel</button>
        </div>
      )}
      {helpVisible && (
        <pre className="t-meta env-sublabel" style={{ padding: '4px 12px', whiteSpace: 'pre-wrap' }}>{SLASH_HELP_TEXT}</pre>
      )}
      <div style={{ borderTop: '1px solid var(--phos)', padding: 8, display: 'flex', gap: 8 }}>
        <textarea
          rows={2}
          value={composer}
          onChange={(e) => setComposer(e.target.value)}
          placeholder={replyTarget !== undefined ? 'Reply…' : 'Compose… (/help for commands)'}
          style={{ flex: 1, background: 'transparent', color: 'var(--phos)', border: '1px solid var(--phos)', padding: 6, fontFamily: 'var(--mono)' }}
        />
        <button className="cta" disabled={status === 'sending' || composer.trim().length === 0} onClick={() => { send().catch(() => { /* swallow */ }); }}>
          {status === 'sending' ? 'sending…' : (replyTarget !== undefined ? 'reply' : 'post')}
        </button>
      </div>
      {status === 'error' && (
        <div className="t-meta" style={{ color: 'var(--pink)', padding: '4px 12px' }}>{lastError}</div>
      )}
      <SurfaceStyles />
    </EnvFrame>
  );
}

function SocialGroupConfig({ ctx, director }: SurfaceProps): JSX.Element {
  const [name, setName] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [kind, setKind] = useState<SocialGroupKind>('feed');
  const [visibility, setVisibility] = useState<SocialVisibility>('private');
  const [status, setStatus] = useState<'idle' | 'saving' | 'error'>('idle');
  const [lastError, setLastError] = useState<string>('');
  const [availableLinks, setAvailableLinks] = useState<Array<{ id: string; label: string }>>([]);
  const [selectedLinks, setSelectedLinks] = useState<Set<string>>(new Set());

  useEffect(() => {
    (async () => {
      const links = await ctx.storage.db.federation_links.toArray();
      setAvailableLinks(
        links
          .filter((l) => l.status === 'active')
          .map((l) => ({ id: l.id, label: `${l.localNeighborhoodId} ↔ ${l.remoteNeighborhoodId}` })),
      );
    })().catch(() => { /* noop */ });
  }, [ctx]);

  const create = async (): Promise<void> => {
    if (name.trim().length === 0) { setStatus('error'); setLastError('name required'); return; }
    setStatus('saving');
    try {
      const rec = await getSocialGroupManager(ctx).createGroup({
        name: name.trim(),
        kind,
        visibility,
        ...(description.trim().length > 0 ? { description: description.trim() } : {}),
      });
      // Phase 27.7: if links were selected, attach them.
      if (selectedLinks.size > 0) {
        await getSocialGroupManager(ctx).federateGroup(rec.id, [...selectedLinks]).catch(() => { /* swallow */ });
      }
      try { localStorage.setItem('alds-selected-social-group', rec.id); } catch { /* noop */ }
      director.bridge('social-feed', rec.name);
    } catch (err) {
      setStatus('error');
      setLastError(err instanceof Error ? err.message : String(err));
    }
  };

  const toggleLink = (id: string): void => {
    setSelectedLinks((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  return (
    <EnvFrame title="New social group" glyph="✚" color="var(--blue)" director={director}>
      <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <label className="t-meta env-sublabel">
          name
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            style={{ display: 'block', width: '100%', background: 'transparent', color: 'var(--phos)', border: '1px solid var(--phos)', padding: 6 }}
          />
        </label>
        <label className="t-meta env-sublabel">
          description (optional)
          <input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            style={{ display: 'block', width: '100%', background: 'transparent', color: 'var(--phos)', border: '1px solid var(--phos)', padding: 6 }}
          />
        </label>
        <label className="t-meta env-sublabel">
          presentation
          <select
            value={kind}
            onChange={(e) => setKind(e.target.value as SocialGroupKind)}
            style={{ display: 'block', width: '100%', background: 'transparent', color: 'var(--phos)', border: '1px solid var(--phos)', padding: 6 }}
          >
            <option value="feed">feed (Facebook-style)</option>
            <option value="timeline">timeline (Twitter-style)</option>
            <option value="gallery">gallery (Instagram-style)</option>
            <option value="broadcast">broadcast (Telegram-style)</option>
            <option value="channel">channel (Slack-style)</option>
            <option value="dm">dm</option>
          </select>
        </label>
        <label className="t-meta env-sublabel">
          visibility
          <select
            value={visibility}
            onChange={(e) => setVisibility(e.target.value as SocialVisibility)}
            style={{ display: 'block', width: '100%', background: 'transparent', color: 'var(--phos)', border: '1px solid var(--phos)', padding: 6 }}
          >
            <option value="private">private (invite-only)</option>
            <option value="federated">federated (neighborhood mesh)</option>
            <option value="public-bulletin">public bulletin</option>
          </select>
        </label>
        {availableLinks.length > 0 && (
          <div className="t-meta env-sublabel">
            federate with (optional)
            <div style={{ marginTop: 4, padding: 6, border: '1px solid var(--phos)', maxHeight: 120, overflowY: 'auto' }}>
              {availableLinks.map((l) => (
                <label key={l.id} style={{ display: 'block', fontSize: 11, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={selectedLinks.has(l.id)}
                    onChange={() => toggleLink(l.id)}
                    style={{ marginRight: 6 }}
                  />
                  🔗 {l.label}
                </label>
              ))}
            </div>
          </div>
        )}
        <button className="cta" disabled={status === 'saving'} onClick={() => { create().catch(() => { /* swallow */ }); }}>
          {status === 'saving' ? 'creating…' : 'create group'}
        </button>
        {status === 'error' && (
          <div className="t-meta" style={{ color: 'var(--pink)' }}>{lastError}</div>
        )}
      </div>
      <SurfaceStyles />
    </EnvFrame>
  );
}

// ─── Phase 27.2 — Slack-style channel surface ────────────────────────────

function SocialChannel({ ctx, director }: SurfaceProps): JSX.Element {
  const [groups, setGroups] = useState<SocialGroupRecord[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<string>(() => {
    try { return localStorage.getItem('alds-selected-social-group') ?? ''; } catch { return ''; }
  });
  const [messages, setMessages] = useState<SocialMessage[]>([]);
  const [threadParent, setThreadParent] = useState<SocialMessage | undefined>(undefined);
  const [threadReplies, setThreadReplies] = useState<SocialMessage[]>([]);
  const [composer, setComposer] = useState<string>('');
  const [threadComposer, setThreadComposer] = useState<string>('');
  const [helpVisible, setHelpVisible] = useState<boolean>(false);
  const [lastError, setLastError] = useState<string>('');

  const refreshGroups = async (): Promise<void> => {
    const gs = await getSocialGroupManager(ctx).listGroups();
    setGroups(gs);
    if (selectedGroupId === '' && gs.length > 0) {
      setSelectedGroupId(gs[0]!.id);
    }
  };

  const refreshMessages = async (): Promise<void> => {
    if (selectedGroupId === '') return;
    const mgr = getSocialGroupManager(ctx);
    const ms = await mgr.listMessages({ groupId: selectedGroupId, includeReplyCount: true, order: 'asc' });
    setMessages(ms);
  };

  const refreshThread = async (): Promise<void> => {
    if (threadParent === undefined || selectedGroupId === '') { setThreadReplies([]); return; }
    const replies = await getSocialGroupManager(ctx).listReplies({
      groupId: selectedGroupId,
      parentHash: threadParent.messageHash,
    });
    setThreadReplies(replies);
  };

  useEffect(() => { refreshGroups().catch(() => { /* swallow */ }); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);
  useEffect(() => {
    refreshMessages().catch(() => { /* swallow */ });
    setThreadParent(undefined);
    setThreadReplies([]);
    try { localStorage.setItem('alds-selected-social-group', selectedGroupId); } catch { /* noop */ }
  /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [selectedGroupId]);
  useEffect(() => { refreshThread().catch(() => { /* swallow */ }); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [threadParent?.messageHash]);

  const sendTop = async (): Promise<void> => {
    const action = applySlashCommand(composer);
    if (action.kind === 'noop') return;
    if (action.kind === 'help') { setHelpVisible(true); setComposer(''); return; }
    setHelpVisible(false);
    try {
      await getSocialGroupManager(ctx).publish({ groupId: selectedGroupId, text: action.text });
      setComposer('');
      await refreshMessages();
    } catch (err) {
      setLastError(err instanceof Error ? err.message : String(err));
    }
  };

  const sendReply = async (): Promise<void> => {
    if (threadParent === undefined) return;
    const action = applySlashCommand(threadComposer);
    if (action.kind === 'noop') return;
    if (action.kind === 'help') { setHelpVisible(true); setThreadComposer(''); return; }
    setHelpVisible(false);
    try {
      await getSocialGroupManager(ctx).publish({
        groupId: selectedGroupId,
        text: action.text,
        parentHash: threadParent.messageHash,
      });
      setThreadComposer('');
      await Promise.all([refreshThread(), refreshMessages()]);
    } catch (err) {
      setLastError(err instanceof Error ? err.message : String(err));
    }
  };

  const selectedGroup = groups.find((g) => g.id === selectedGroupId);

  return (
    <EnvFrame title={selectedGroup?.name ?? 'Channels'} glyph="◆" color="var(--blue)" director={director}>
      <div style={{ display: 'flex', height: '100%' }}>
        {/* Sidebar */}
        <div style={{ width: 180, borderRight: '1px solid var(--phos)', overflowY: 'auto' }}>
          <div className="t-meta env-sublabel" style={{ padding: '6px 8px' }}>channels</div>
          {groups.length === 0 ? (
            <div className="surface-sub" style={{ padding: 8, fontSize: 11 }}>no groups</div>
          ) : groups.map((g) => (
            <div
              key={g.id}
              role="button"
              tabIndex={0}
              onClick={() => setSelectedGroupId(g.id)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setSelectedGroupId(g.id); }}
              style={{
                padding: '6px 8px',
                cursor: 'pointer',
                background: g.id === selectedGroupId ? 'rgba(127, 255, 159, 0.08)' : 'transparent',
                borderLeft: g.id === selectedGroupId ? '2px solid var(--phos)' : '2px solid transparent',
                fontFamily: 'var(--mono)',
                fontSize: 12,
              }}
            >
              #{g.name}
              <div className="t-meta env-sublabel" style={{ fontSize: 10 }}>{g.kind}</div>
            </div>
          ))}
          <div style={{ padding: 8 }}>
            <button className="pill" onClick={() => director.push('social-group-config', 'new group')}>
              + group
            </button>
          </div>
        </div>

        {/* Main message panel */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          {selectedGroupId === '' ? (
            <div className="surface-sub" style={{ padding: 12 }}>Pick a channel from the sidebar.</div>
          ) : (
            <>
              <div style={{ flex: 1, overflowY: 'auto', padding: 12 }}>
                {messages.length === 0 ? (
                  <div className="surface-sub">No messages yet.</div>
                ) : messages.map((m) => {
                  const isEmote = m.payload.text !== undefined && m.payload.text.startsWith('* ');
                  return (
                    <div
                      key={m.messageHash}
                      className="receipt-mini"
                      style={{ marginBottom: 6, cursor: 'pointer', borderColor: threadParent?.messageHash === m.messageHash ? 'var(--amber)' : undefined }}
                      onClick={() => setThreadParent(m)}
                    >
                      <div className="t-meta env-sublabel">
                        {m.senderPubkey.slice(0, 12)}… · {new Date(m.sentAt).toLocaleString()}
                      </div>
                      <div
                        style={{
                          marginTop: 4,
                          whiteSpace: 'pre-wrap',
                          fontStyle: isEmote ? 'italic' : 'normal',
                          color: isEmote ? 'var(--amber)' : undefined,
                        }}
                      >
                        {isEmote ? m.payload.text!.slice(2) : (m.payload.text ?? '')}
                      </div>
                      {(m.replyCount ?? 0) > 0 && (
                        <div className="t-meta env-sublabel" style={{ marginTop: 4 }}>
                          ↳ {m.replyCount} repl{m.replyCount === 1 ? 'y' : 'ies'}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              {helpVisible && (
                <pre className="t-meta env-sublabel" style={{ padding: '4px 12px', whiteSpace: 'pre-wrap' }}>{SLASH_HELP_TEXT}</pre>
              )}
              <div style={{ borderTop: '1px solid var(--phos)', padding: 8, display: 'flex', gap: 8 }}>
                <textarea
                  rows={2}
                  value={composer}
                  onChange={(e) => setComposer(e.target.value)}
                  placeholder="Message (/help)"
                  style={{ flex: 1, background: 'transparent', color: 'var(--phos)', border: '1px solid var(--phos)', padding: 6, fontFamily: 'var(--mono)' }}
                />
                <button className="cta" disabled={composer.trim().length === 0} onClick={() => { sendTop().catch(() => { /* swallow */ }); }}>
                  send
                </button>
              </div>
            </>
          )}
        </div>

        {/* Thread panel */}
        {threadParent !== undefined && (
          <div style={{ width: 280, borderLeft: '1px solid var(--phos)', display: 'flex', flexDirection: 'column' }}>
            <div className="t-meta env-sublabel" style={{ padding: '6px 8px', display: 'flex', justifyContent: 'space-between' }}>
              thread
              <button className="pill" onClick={() => setThreadParent(undefined)}>×</button>
            </div>
            <div className="receipt-mini" style={{ margin: 8 }}>
              <div className="t-meta env-sublabel">{threadParent.senderPubkey.slice(0, 12)}…</div>
              <div style={{ marginTop: 4, whiteSpace: 'pre-wrap' }}>{threadParent.payload.text ?? ''}</div>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: 8 }}>
              {threadReplies.length === 0 ? (
                <div className="surface-sub" style={{ fontSize: 11 }}>No replies yet.</div>
              ) : threadReplies.map((r) => (
                <div key={r.messageHash} className="receipt-mini" style={{ marginBottom: 4 }}>
                  <div className="t-meta env-sublabel">{r.senderPubkey.slice(0, 12)}…</div>
                  <div style={{ marginTop: 4, whiteSpace: 'pre-wrap', fontSize: 12 }}>{r.payload.text ?? ''}</div>
                </div>
              ))}
            </div>
            <div style={{ borderTop: '1px solid var(--phos)', padding: 6, display: 'flex', gap: 4 }}>
              <textarea
                rows={2}
                value={threadComposer}
                onChange={(e) => setThreadComposer(e.target.value)}
                placeholder="Reply…"
                style={{ flex: 1, background: 'transparent', color: 'var(--phos)', border: '1px solid var(--phos)', padding: 4, fontFamily: 'var(--mono)', fontSize: 12 }}
              />
              <button className="cta" disabled={threadComposer.trim().length === 0} onClick={() => { sendReply().catch(() => { /* swallow */ }); }}>
                reply
              </button>
            </div>
          </div>
        )}
      </div>
      {lastError !== '' && (
        <div className="t-meta" style={{ color: 'var(--pink)', padding: '4px 12px' }}>{lastError}</div>
      )}
      <SurfaceStyles />
    </EnvFrame>
  );
}

// ─── Phase 27.3 — broadcast / gallery / stories surfaces ──────────────────

function useSelectedSocialGroupId(): string {
  return (() => {
    try { return localStorage.getItem('alds-selected-social-group') ?? ''; } catch { return ''; }
  })();
}

function SocialBroadcast({ ctx, director }: SurfaceProps): JSX.Element {
  const groupId = useSelectedSocialGroupId();
  const [group, setGroup] = useState<SocialGroupRecord | undefined>(undefined);
  const [messages, setMessages] = useState<SocialMessage[]>([]);
  const [canPost, setCanPost] = useState<boolean>(false);
  const [composer, setComposer] = useState<string>('');
  const [error, setError] = useState<string>('');

  const refresh = async (): Promise<void> => {
    if (groupId === '') return;
    const mgr = getSocialGroupManager(ctx);
    const [g, ms, mod] = await Promise.all([
      mgr.getGroup(groupId),
      mgr.listMessages({ groupId, includeReplyCount: true }),
      mgr.isModerator(groupId),
    ]);
    setGroup(g);
    setMessages(ms);
    setCanPost(mod);
  };

  useEffect(() => { refresh().catch(() => { /* swallow */ }); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [groupId]);

  const send = async (): Promise<void> => {
    if (composer.trim().length === 0) return;
    try {
      await getSocialGroupManager(ctx).publish({ groupId, text: composer.trim() });
      setComposer('');
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  if (groupId === '') {
    return (
      <EnvFrame title="Broadcast" glyph="📢" color="var(--amber)" director={director}>
        <div className="surface-sub" style={{ padding: 12 }}>No group selected.</div>
      </EnvFrame>
    );
  }

  return (
    <EnvFrame title={group?.name ?? 'Broadcast'} glyph="📢" color="var(--amber)" director={director}>
      <div className="t-meta env-sublabel" style={{ padding: '4px 12px' }}>
        {group?.kind ?? '...'} · {canPost ? 'you can broadcast' : 'read-only · only mods can post'}
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: 12 }}>
        {messages.length === 0 ? (
          <div className="surface-sub">No broadcasts yet.</div>
        ) : messages.map((m) => (
          <div key={m.messageHash} className="receipt-mini" style={{ marginBottom: 12, borderColor: 'var(--amber)' }}>
            <div className="t-meta env-sublabel">
              📢 {m.senderPubkey.slice(0, 12)}… · {new Date(m.sentAt).toLocaleString()}
            </div>
            <div style={{ marginTop: 6, whiteSpace: 'pre-wrap', fontSize: 14 }}>{m.payload.text ?? ''}</div>
          </div>
        ))}
      </div>
      {canPost && (
        <div style={{ borderTop: '1px solid var(--phos)', padding: 8, display: 'flex', gap: 8 }}>
          <textarea
            rows={2}
            value={composer}
            onChange={(e) => setComposer(e.target.value)}
            placeholder="Broadcast a message…"
            style={{ flex: 1, background: 'transparent', color: 'var(--phos)', border: '1px solid var(--phos)', padding: 6, fontFamily: 'var(--mono)' }}
          />
          <button className="cta" disabled={composer.trim().length === 0} onClick={() => { send().catch(() => { /* swallow */ }); }}>
            broadcast
          </button>
        </div>
      )}
      {error !== '' && (
        <div className="t-meta" style={{ color: 'var(--pink)', padding: '4px 12px' }}>{error}</div>
      )}
      <SurfaceStyles />
    </EnvFrame>
  );
}

function SocialGallery({ ctx, director }: SurfaceProps): JSX.Element {
  const groupId = useSelectedSocialGroupId();
  const [group, setGroup] = useState<SocialGroupRecord | undefined>(undefined);
  const [tiles, setTiles] = useState<SocialMessage[]>([]);
  const [opened, setOpened] = useState<SocialMessage | undefined>(undefined);

  useEffect(() => {
    if (groupId === '') return;
    (async () => {
      const mgr = getSocialGroupManager(ctx);
      const [g, ms] = await Promise.all([mgr.getGroup(groupId), mgr.listMessages({ groupId, limit: 200 })]);
      setGroup(g);
      // Gallery view: posts with media manifests OR with text-only as fallback.
      setTiles(ms.filter((m) => (m.payload.mediaManifestIds?.length ?? 0) > 0 || m.payload.text !== undefined));
    })().catch(() => { /* swallow */ });
  /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [groupId]);

  if (groupId === '') {
    return (
      <EnvFrame title="Gallery" glyph="▦" color="var(--pink)" director={director}>
        <div className="surface-sub" style={{ padding: 12 }}>No group selected.</div>
      </EnvFrame>
    );
  }

  return (
    <EnvFrame title={group?.name ?? 'Gallery'} glyph="▦" color="var(--pink)" director={director}>
      <div className="t-meta env-sublabel" style={{ padding: '4px 12px' }}>{tiles.length} item{tiles.length === 1 ? '' : 's'}</div>
      <div style={{ flex: 1, overflowY: 'auto', padding: 8, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 4 }}>
        {tiles.map((m) => {
          const hasMedia = (m.payload.mediaManifestIds?.length ?? 0) > 0;
          return (
            <div
              key={m.messageHash}
              role="button"
              tabIndex={0}
              onClick={() => setOpened(m)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setOpened(m); }}
              style={{
                aspectRatio: '1 / 1',
                border: '1px solid var(--phos)',
                background: hasMedia ? 'rgba(255, 127, 200, 0.06)' : 'rgba(127, 255, 159, 0.04)',
                cursor: 'pointer',
                padding: 6,
                fontFamily: 'var(--mono)',
                fontSize: 11,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
              }}
            >
              <div style={{ color: hasMedia ? 'var(--pink)' : 'var(--phos)' }}>
                {hasMedia ? `▦ ${m.payload.mediaManifestIds!.length} media` : 'text'}
              </div>
              <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' }}>
                {m.payload.text ?? ''}
              </div>
              <div className="t-meta env-sublabel" style={{ fontSize: 9 }}>
                {m.senderPubkey.slice(0, 6)}…
              </div>
            </div>
          );
        })}
      </div>
      {opened !== undefined && (
        <div
          onClick={() => setOpened(undefined)}
          style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 32, cursor: 'pointer', zIndex: 100 }}
        >
          <div className="receipt-mini" style={{ maxWidth: 500, background: 'var(--bg)', cursor: 'auto' }} onClick={(e) => e.stopPropagation()}>
            <div className="t-meta env-sublabel">
              {opened.senderPubkey.slice(0, 12)}… · {new Date(opened.sentAt).toLocaleString()}
            </div>
            <div style={{ marginTop: 8, whiteSpace: 'pre-wrap' }}>{opened.payload.text ?? ''}</div>
            {(opened.payload.mediaManifestIds?.length ?? 0) > 0 && (
              <div className="t-meta env-sublabel" style={{ marginTop: 8 }}>
                ▦ {opened.payload.mediaManifestIds!.length} media manifest(s) attached
              </div>
            )}
            <div style={{ marginTop: 12, textAlign: 'right' }}>
              <button className="pill" onClick={() => setOpened(undefined)}>close</button>
            </div>
          </div>
        </div>
      )}
      <SurfaceStyles />
    </EnvFrame>
  );
}

function SocialStories({ ctx, director }: SurfaceProps): JSX.Element {
  const groupId = useSelectedSocialGroupId();
  const [group, setGroup] = useState<SocialGroupRecord | undefined>(undefined);
  const [stories, setStories] = useState<SocialMessage[]>([]);
  const [activeIdx, setActiveIdx] = useState<number>(0);
  const [composer, setComposer] = useState<string>('');
  const [error, setError] = useState<string>('');

  const refresh = async (): Promise<void> => {
    if (groupId === '') return;
    const mgr = getSocialGroupManager(ctx);
    const [g, st] = await Promise.all([mgr.getGroup(groupId), mgr.listStories({ groupId })]);
    setGroup(g);
    setStories(st);
    if (activeIdx >= st.length) setActiveIdx(0);
  };

  useEffect(() => { refresh().catch(() => { /* swallow */ }); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [groupId]);

  const publish = async (): Promise<void> => {
    if (composer.trim().length === 0) return;
    try {
      await getSocialGroupManager(ctx).publishStory({ groupId, text: composer.trim() });
      setComposer('');
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  if (groupId === '') {
    return (
      <EnvFrame title="Stories" glyph="◌" color="var(--blue)" director={director}>
        <div className="surface-sub" style={{ padding: 12 }}>No group selected.</div>
      </EnvFrame>
    );
  }

  const active = stories[activeIdx];
  const remainingMs = (m: SocialMessage): number => {
    const ttlSec = m.payload.ttlSec ?? 0;
    return new Date(m.sentAt).getTime() + ttlSec * 1000 - Date.now();
  };
  const fmtRemaining = (ms: number): string => {
    if (ms <= 0) return 'expired';
    const h = Math.floor(ms / 3_600_000);
    const m = Math.floor((ms % 3_600_000) / 60_000);
    return h > 0 ? `${h}h ${m}m left` : `${m}m left`;
  };

  return (
    <EnvFrame title={group?.name ?? 'Stories'} glyph="◌" color="var(--blue)" director={director}>
      <div className="t-meta env-sublabel" style={{ padding: '4px 12px' }}>
        {stories.length} active stor{stories.length === 1 ? 'y' : 'ies'}
      </div>
      {/* Ring of dots — Instagram-style segmented progress */}
      <div style={{ display: 'flex', gap: 4, padding: '0 12px', overflowX: 'auto' }}>
        {stories.map((s, i) => (
          <button
            key={s.messageHash}
            className="pill"
            style={{
              minWidth: 60,
              fontSize: 10,
              background: i === activeIdx ? 'rgba(127, 159, 255, 0.15)' : 'transparent',
              borderColor: i === activeIdx ? 'var(--blue)' : undefined,
            }}
            onClick={() => setActiveIdx(i)}
          >
            {s.senderPubkey.slice(0, 6)}…
            <div style={{ fontSize: 9, opacity: 0.7 }}>{fmtRemaining(remainingMs(s))}</div>
          </button>
        ))}
      </div>
      <div style={{ flex: 1, padding: 12, overflowY: 'auto' }}>
        {active === undefined ? (
          <div className="surface-sub">No stories yet — post one below.</div>
        ) : (
          <div className="receipt-mini" style={{ borderColor: 'var(--blue)' }}>
            <div className="t-meta env-sublabel">
              ◌ {active.senderPubkey.slice(0, 12)}… · {new Date(active.sentAt).toLocaleString()} · {fmtRemaining(remainingMs(active))}
            </div>
            <div style={{ marginTop: 8, whiteSpace: 'pre-wrap', fontSize: 14 }}>{active.payload.text ?? ''}</div>
          </div>
        )}
      </div>
      <div style={{ borderTop: '1px solid var(--phos)', padding: 8, display: 'flex', gap: 8 }}>
        <textarea
          rows={2}
          value={composer}
          onChange={(e) => setComposer(e.target.value)}
          placeholder="Story expires in 24h…"
          style={{ flex: 1, background: 'transparent', color: 'var(--phos)', border: '1px solid var(--phos)', padding: 6, fontFamily: 'var(--mono)' }}
        />
        <button className="cta" disabled={composer.trim().length === 0} onClick={() => { publish().catch(() => { /* swallow */ }); }}>
          post story
        </button>
      </div>
      {error !== '' && (
        <div className="t-meta" style={{ color: 'var(--pink)', padding: '4px 12px' }}>{error}</div>
      )}
      <SurfaceStyles />
    </EnvFrame>
  );
}

// ─── Phase 27.4 — rich compose surface ─────────────────────────────────────

function SocialCompose({ ctx, director }: SurfaceProps): JSX.Element {
  const groupId = useSelectedSocialGroupId();
  const [text, setText] = useState<string>('');
  const [media, setMedia] = useState<string>('');      // comma-separated manifest ids
  const [linkUrl, setLinkUrl] = useState<string>('');
  const [pollLines, setPollLines] = useState<string>('');
  const [status, setStatus] = useState<'idle' | 'sending' | 'error'>('idle');
  const [error, setError] = useState<string>('');
  // Phase 55.b: pending-review pill. Populated once at mount + every time
  // the user navigates back here, so a draft they submitted lingers as a
  // visible reminder until it publishes / rejects / expires / withdraws.
  const [ownPending, setOwnPending] = useState<Array<import('../../storage/schema').PendingPostRecord>>([]);
  const [needsCommunityVote, setNeedsCommunityVote] = useState<boolean>(false);
  const refreshPending = useCallback(async (): Promise<void> => {
    try {
      const { getModerationManager } = await import('../../social/ModerationManager');
      const own = await getModerationManager(ctx).listOwnPending();
      setOwnPending(own);
    } catch { /* swallow — badge just stays empty */ }
  }, [ctx]);
  useEffect(() => {
    void refreshPending();
    // Phase 55.b: also detect whether the *current* group requires prepublish
    // vote so we can render the right call-to-action text on the publish
    // button + show an explanatory note.
    void (async () => {
      try {
        const group = await ctx.storage.db.social_groups.get(groupId);
        setNeedsCommunityVote(group?.moderation === 'community-vote-prepublish');
      } catch { setNeedsCommunityVote(false); }
    })();
  }, [ctx, groupId, refreshPending]);
  // Phase 41.1 (v8) + Phase 46.3 (v9): voice-note recording state.
  const [recordingState, setRecordingState] = useState<'idle' | 'recording' | 'recorded'>('idle');
  const [recordingSec, setRecordingSec] = useState<number>(0);
  const [voiceBlobUrl, setVoiceBlobUrl] = useState<string>('');
  const [voiceBlob, setVoiceBlob] = useState<Blob | null>(null);
  const [voiceMimeType, setVoiceMimeType] = useState<string>('');
  const voiceRecorderRef = useRef<{ recorder: import('../../vault/VoiceNoteRecorder').VoiceNoteRecorder | null; timer: ReturnType<typeof setInterval> | null }>({ recorder: null, timer: null });

  const startRecording = async (): Promise<void> => {
    try {
      const { VoiceNoteRecorder } = await import('../../vault/VoiceNoteRecorder');
      const rec = new VoiceNoteRecorder();
      await rec.requestPermission();
      rec.start();
      voiceRecorderRef.current.recorder = rec;
      setRecordingState('recording');
      setRecordingSec(0);
      const t = setInterval(() => setRecordingSec((s) => s + 1), 1000);
      voiceRecorderRef.current.timer = t;
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setStatus('error');
    }
  };

  const stopRecording = async (): Promise<void> => {
    const { recorder, timer } = voiceRecorderRef.current;
    if (recorder === null) return;
    if (timer !== null) clearInterval(timer);
    try {
      const result = await recorder.stop();
      const url = URL.createObjectURL(result.blob);
      setVoiceBlobUrl(url);
      setVoiceBlob(result.blob);
      setVoiceMimeType(result.mimeType);
      setRecordingState('recorded');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setStatus('error');
    } finally {
      voiceRecorderRef.current = { recorder: null, timer: null };
    }
  };

  const cancelRecording = async (): Promise<void> => {
    const { recorder, timer } = voiceRecorderRef.current;
    if (timer !== null) clearInterval(timer);
    if (recorder !== null) { await recorder.cancel().catch(() => { /* noop */ }); }
    voiceRecorderRef.current = { recorder: null, timer: null };
    if (voiceBlobUrl !== '') { try { URL.revokeObjectURL(voiceBlobUrl); } catch { /* noop */ } }
    setVoiceBlobUrl('');
    setVoiceBlob(null);
    setVoiceMimeType('');
    setRecordingState('idle');
    setRecordingSec(0);
  };

  const publish = async (): Promise<void> => {
    if (groupId === '') { setError('no group selected'); setStatus('error'); return; }
    setStatus('sending');
    const mediaIds = media.split(',').map((s) => s.trim()).filter((s) => s.length > 0);
    const pollOptions = pollLines.split('\n').map((s) => s.trim()).filter((s) => s.length > 0);
    try {
      if (needsCommunityVote) {
        // Phase 55.b: in community-vote-prepublish groups, top-level posts
        // must go through ModerationManager.submitForReview. We surface
        // the resulting pendingPostId via the badge instead of bridging
        // back to the feed (the draft isn't visible there yet).
        const { getModerationManager } = await import('../../social/ModerationManager');
        await getModerationManager(ctx).submitForReview({
          groupId,
          ...(text.trim().length > 0 ? { text: text.trim() } : {}),
          ...(mediaIds.length > 0 ? { mediaManifestIds: mediaIds } : {}),
          ...(linkUrl.trim().length > 0 ? { linkCardUrl: linkUrl.trim() } : {}),
          ...(pollOptions.length >= 2 ? { pollOptions } : {}),
        });
        // Reset compose state + refresh badge.
        setText(''); setMedia(''); setLinkUrl(''); setPollLines('');
        setStatus('idle');
        await refreshPending();
        return;
      }
      await getSocialGroupManager(ctx).publish({
        groupId,
        ...(text.trim().length > 0 ? { text: text.trim() } : {}),
        ...(mediaIds.length > 0 ? { mediaManifestIds: mediaIds } : {}),
        ...(linkUrl.trim().length > 0 ? { linkCardUrl: linkUrl.trim() } : {}),
        ...(pollOptions.length >= 2 ? { pollOptions } : {}),
        ...(voiceBlob !== null ? { voiceNoteBlob: voiceBlob, voiceNoteMimeType: voiceMimeType } : {}),
      });
      director.bridge('social-feed', 'back to feed');
    } catch (err) {
      setStatus('error');
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  // Phase 55.b: author-side withdraw. Updates the badge inline.
  const withdraw = async (pendingPostId: string): Promise<void> => {
    try {
      const { getModerationManager } = await import('../../social/ModerationManager');
      await getModerationManager(ctx).withdrawDraft(pendingPostId);
      await refreshPending();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setStatus('error');
    }
  };

  if (groupId === '') {
    return (
      <EnvFrame title="Compose" glyph="✎" color="var(--phos)" director={director}>
        <div className="surface-sub" style={{ padding: 12 }}>No group selected.</div>
      </EnvFrame>
    );
  }

  return (
    <EnvFrame title="Compose" glyph="✎" color="var(--phos)" director={director}>
      <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 10, overflowY: 'auto' }}>
        <label className="t-meta env-sublabel">
          text
          <textarea
            rows={4}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="What's on your mind?"
            style={{ display: 'block', width: '100%', background: 'transparent', color: 'var(--phos)', border: '1px solid var(--phos)', padding: 6, fontFamily: 'var(--mono)' }}
          />
        </label>
        <label className="t-meta env-sublabel">
          media manifest IDs (comma-separated, attach existing from Vault)
          <input
            value={media}
            onChange={(e) => setMedia(e.target.value)}
            placeholder="manifest-abc, manifest-def"
            style={{ display: 'block', width: '100%', background: 'transparent', color: 'var(--phos)', border: '1px solid var(--phos)', padding: 6 }}
          />
        </label>
        <label className="t-meta env-sublabel">
          link-card URL
          <input
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            placeholder="https://…"
            style={{ display: 'block', width: '100%', background: 'transparent', color: 'var(--phos)', border: '1px solid var(--phos)', padding: 6 }}
          />
        </label>
        <label className="t-meta env-sublabel">
          poll (one option per line; need 2+ to enable)
          <textarea
            rows={4}
            value={pollLines}
            onChange={(e) => setPollLines(e.target.value)}
            placeholder={'Option A\nOption B\nOption C'}
            style={{ display: 'block', width: '100%', background: 'transparent', color: 'var(--phos)', border: '1px solid var(--phos)', padding: 6, fontFamily: 'var(--mono)' }}
          />
        </label>
        {/* Phase 41.1 (v8) + 46.3 (v9): voice-note recorder pill. Wired to
            Vault — recorded notes attach to the post via voiceNoteBlob. */}
        <div className="t-meta env-sublabel">
          voice note
          <div style={{ marginTop: 4, display: 'flex', gap: 6, alignItems: 'center' }}>
            {recordingState === 'idle' && (
              <button className="pill" onClick={() => { startRecording().catch(() => { /* swallow */ }); }}>
                🎙 record
              </button>
            )}
            {recordingState === 'recording' && (
              <>
                <button className="pill" style={{ borderColor: 'var(--pink)' }} onClick={() => { stopRecording().catch(() => { /* swallow */ }); }}>
                  ⏹ stop ({recordingSec}s)
                </button>
                <button className="pill" onClick={() => { cancelRecording().catch(() => { /* swallow */ }); }}>
                  cancel
                </button>
              </>
            )}
            {recordingState === 'recorded' && voiceBlobUrl !== '' && (
              <>
                <audio controls src={voiceBlobUrl} style={{ flex: 1 }} />
                <button className="pill" onClick={() => { cancelRecording().catch(() => { /* swallow */ }); }}>
                  discard
                </button>
              </>
            )}
          </div>
        </div>
        {needsCommunityVote && (
          <div
            className="t-meta"
            style={{ color: 'var(--amber)', padding: 6, border: '1px solid var(--amber)' }}
            data-testid="social-compose-prepublish-notice"
          >
            ⚖ this group requires community vote — drafts publish only after quorum approves
          </div>
        )}
        <button
          className="cta"
          disabled={status === 'sending'}
          onClick={() => { publish().catch(() => { /* swallow */ }); }}
        >
          {status === 'sending'
            ? (needsCommunityVote ? 'submitting for review…' : 'publishing…')
            : (needsCommunityVote ? 'submit for review' : 'publish')}
        </button>
        {status === 'error' && (
          <div className="t-meta" style={{ color: 'var(--pink)' }}>{error}</div>
        )}
        {ownPending.length > 0 && (
          <div
            style={{ marginTop: 8, padding: 8, border: '1px solid var(--rule)' }}
            data-testid="social-compose-pending-badge"
          >
            <div className="t-meta env-sublabel" style={{ marginBottom: 6 }}>
              ⏳ pending review · {ownPending.length} draft{ownPending.length === 1 ? '' : 's'}
            </div>
            {ownPending.slice(0, 5).map((p) => {
              let preview = '';
              try {
                const parsed = JSON.parse(p.payloadJson) as { text?: string };
                preview = (parsed.text ?? '').slice(0, 80);
              } catch { /* leave empty */ }
              return (
                <div
                  key={p.id}
                  style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 4 }}
                >
                  <div className="t-meta" style={{ flex: 1, color: 'var(--ink-dim)', minWidth: 0 }}>
                    {preview !== '' ? preview : `(no text · ${p.id.slice(0, 8)}…)`} · expires {ago(p.expiresAt)}
                  </div>
                  <button
                    className="pill"
                    data-testid="social-compose-withdraw"
                    onClick={() => { withdraw(p.id).catch(() => { /* surfaced via error state */ }); }}
                  >
                    withdraw
                  </button>
                </div>
              );
            })}
            <div className="t-meta" style={{ color: 'var(--ink-dim)', marginTop: 4 }}>
              <a
                onClick={(e) => { e.preventDefault(); director.push('sass-prepublish-vote', 'review drafts'); }}
                href="#"
                style={{ color: 'var(--phos)' }}
              >
                → open prepublish-vote review
              </a>
            </div>
          </div>
        )}
      </div>
      <SurfaceStyles />
    </EnvFrame>
  );
}

// ─── Phase 36 (v7) — renderers for v6-registered surfaces ────────────────

/**
 * social-dm — 1:1 social chat layout. Reads peer pubkey from localStorage
 * (set by contact-picker or social-feed deep link).
 */
function SocialDm({ ctx, director }: SurfaceProps): JSX.Element {
  const peerInboxKey = (() => {
    try { return localStorage.getItem('alds-selected-dm-peer') ?? ''; } catch { return ''; }
  })();
  const [dmGroup, setDmGroup] = useState<SocialGroupRecord | undefined>(undefined);
  const [messages, setMessages] = useState<SocialMessage[]>([]);
  const [composer, setComposer] = useState<string>('');
  const [error, setError] = useState<string>('');
  const presenceMap = usePresenceMap(ctx);

  const refresh = async (): Promise<void> => {
    if (peerInboxKey === '') return;
    const mgr = getSocialGroupManager(ctx);
    const dm = await mgr.getOrCreateDmGroup(peerInboxKey);
    setDmGroup(dm);
    const ms = await mgr.listMessages({ groupId: dm.id });
    setMessages(ms);
    await mgr.markRead(dm.id).catch(() => { /* swallow */ });
  };

  useEffect(() => { refresh().catch(() => { /* swallow */ }); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [peerInboxKey]);

  const send = async (): Promise<void> => {
    if (composer.trim().length === 0 || dmGroup === undefined) return;
    try {
      await getSocialGroupManager(ctx).publish({ groupId: dmGroup.id, text: composer.trim() });
      setComposer('');
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  if (peerInboxKey === '') {
    return (
      <EnvFrame title="DM" glyph="✉" color="var(--phos)" director={director}>
        <div className="surface-sub" style={{ padding: 12 }}>
          No peer selected. Pick one from contacts.
        </div>
        <button className="pill" style={{ margin: 12 }} onClick={() => director.push('contact-picker', 'pick a peer')}>
          ☰ contacts
        </button>
      </EnvFrame>
    );
  }

  const callPeer = async (kind: 'audio' | 'video'): Promise<void> => {
    if (dmGroup === undefined) return;
    try {
      await initiateCallToPeer(ctx, director, {
        peerPubkey: peerInboxKey,
        threadId: dmGroup.id,
        kind,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <EnvFrame title={`DM · ${peerInboxKey.slice(0, 8)}…`} glyph="✉" color="var(--phos)" director={director}>
      <div
        style={{
          display: 'flex',
          gap: 8,
          padding: '6px 12px',
          borderBottom: '1px solid var(--phos-dim)',
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        <span className="t-meta env-sublabel" style={{ flex: 1, minWidth: 0 }}>
          <PresenceDot pubkey={peerInboxKey} presenceMap={presenceMap} />
          {peerInboxKey.slice(0, 16)}…
        </span>
        <button
          className="pill"
          aria-label="audio call this contact"
          data-testid="social-dm-call-audio"
          disabled={dmGroup === undefined}
          onClick={() => { callPeer('audio').catch(() => { /* surfaced via error state */ }); }}
        >
          ◉ call
        </button>
        <button
          className="pill"
          aria-label="video call this contact"
          data-testid="social-dm-call-video"
          disabled={dmGroup === undefined}
          onClick={() => { callPeer('video').catch(() => { /* surfaced via error state */ }); }}
        >
          ◆ video
        </button>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: 12 }}>
        {messages.length === 0 ? (
          <div className="surface-sub">No messages yet. Say hi.</div>
        ) : messages.map((m) => (
          <div key={m.messageHash} className="receipt-mini" style={{ marginBottom: 6 }}>
            <div className="t-meta env-sublabel">{m.senderPubkey.slice(0, 8)}… · {new Date(m.sentAt).toLocaleString()}</div>
            <div style={{ marginTop: 4, whiteSpace: 'pre-wrap' }}>{m.payload.text ?? ''}</div>
          </div>
        ))}
      </div>
      <div style={{ borderTop: '1px solid var(--phos)', padding: 8, display: 'flex', gap: 8 }}>
        <textarea
          rows={2}
          value={composer}
          onChange={(e) => setComposer(e.target.value)}
          placeholder="Type a message…"
          style={{ flex: 1, background: 'transparent', color: 'var(--phos)', border: '1px solid var(--phos)', padding: 6, fontFamily: 'var(--mono)' }}
        />
        <button className="cta" disabled={composer.trim().length === 0} onClick={() => { send().catch(() => { /* swallow */ }); }}>
          send
        </button>
      </div>
      {error !== '' && (
        <div className="t-meta" style={{ color: 'var(--pink)', padding: '4px 12px' }}>{error}</div>
      )}
      <SurfaceStyles />
    </EnvFrame>
  );
}

/**
 * contact-picker — searchable peer list bridging env-chat contacts +
 * social-subscriptions. Selecting a peer writes alds-selected-dm-peer to
 * localStorage and pushes social-dm.
 */
function ContactPicker({ ctx, director }: SurfaceProps): JSX.Element {
  const [query, setQuery] = useState<string>('');
  const [contacts, setContacts] = useState<Array<{ pubkey: string; alias?: string; source: 'chat' | 'social' }>>([]);
  const presenceMap = usePresenceMap(ctx);
  // Phase 54.b: "Online now" chip — when on, hide rows whose presence is gray
  // (no recent heartbeat OR explicitly 'invisible'). Available + busy both
  // count as online so a peer mid-call is still callable.
  const [onlineOnly, setOnlineOnly] = useState<boolean>(false);
  // Intent is set by the surface that pushed contact-picker (via the
  // localStorage handoff key). Default = social-dm for backwards compat
  // with the existing "☰ contacts" buttons.
  const intent: 'social-dm' | 'env-chat-thread' | 'call-target-audio' | 'call-target-video' = (() => {
    try {
      const raw = localStorage.getItem('alds-contact-picker-intent');
      if (raw === 'env-chat-thread' || raw === 'call-target-audio' || raw === 'call-target-video') return raw;
    } catch { /* noop */ }
    return 'social-dm';
  })();
  const isCallMode = intent === 'call-target-audio' || intent === 'call-target-video';

  useEffect(() => {
    (async () => {
      const chatRows = await ctx.storage.db.contacts.toArray();
      const subRows = await ctx.storage.db.social_subscriptions.toArray();
      const merged = new Map<string, { pubkey: string; alias?: string; source: 'chat' | 'social' }>();
      for (const c of chatRows) {
        merged.set(c.pubkey, { pubkey: c.pubkey, source: 'chat', ...(c.alias !== undefined ? { alias: c.alias } : {}) });
      }
      for (const s of subRows) {
        if (!merged.has(s.ownPubkey)) {
          merged.set(s.ownPubkey, { pubkey: s.ownPubkey, source: 'social' });
        }
      }
      setContacts([...merged.values()]);
    })().catch(() => { /* surface stays empty */ });
  }, [ctx]);

  const queryFiltered = query.length === 0
    ? contacts
    : contacts.filter((c) => c.pubkey.toLowerCase().includes(query.toLowerCase())
        || (c.alias !== undefined && c.alias.toLowerCase().includes(query.toLowerCase())));
  const filtered = onlineOnly
    ? queryFiltered.filter((c) => {
        const s = presenceMap.get(c.pubkey);
        return s === 'available' || s === 'busy';
      })
    : queryFiltered;

  // Default row tap: open DM (existing behavior). In call-target mode, tap
  // rings immediately at the intent's kind.
  const pick = (peer: string): void => {
    try { localStorage.removeItem('alds-contact-picker-intent'); } catch { /* noop */ }
    if (isCallMode) {
      const kind: 'audio' | 'video' = intent === 'call-target-video' ? 'video' : 'audio';
      // Synthetic threadId = `call-${peer}` — receivers correlate on callId
      // anyway; the wakeup notifier carries the real ring signal.
      void initiateCallToPeer(ctx, director, { peerPubkey: peer, threadId: `call-${peer}`, kind })
        .catch((err) => {
          // eslint-disable-next-line no-alert
          window.alert(`call failed: ${err instanceof Error ? err.message : String(err)}`);
        });
      return;
    }
    try { localStorage.setItem('alds-selected-dm-peer', peer); } catch { /* noop */ }
    director.bridge('social-dm', `dm ${peer.slice(0, 8)}…`);
  };

  // Quick-action buttons inside each row.
  const callQuick = (peer: string, kind: 'audio' | 'video'): void => {
    try { localStorage.removeItem('alds-contact-picker-intent'); } catch { /* noop */ }
    void initiateCallToPeer(ctx, director, { peerPubkey: peer, threadId: `call-${peer}`, kind })
      .catch((err) => {
        // eslint-disable-next-line no-alert
        window.alert(`call failed: ${err instanceof Error ? err.message : String(err)}`);
      });
  };

  const title = isCallMode
    ? (intent === 'call-target-video' ? 'Pick a contact to video-call' : 'Pick a contact to call')
    : 'Pick a contact';

  return (
    <EnvFrame title={title} glyph="☰" color="var(--phos)" director={director}>
      <div style={{ padding: 8 }}>
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Filter by pubkey or alias…"
          style={{ width: '100%', background: 'transparent', color: 'var(--phos)', border: '1px solid var(--phos)', padding: 6, fontFamily: 'var(--mono)' }}
        />
        <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
          <button
            className="pill"
            aria-pressed={onlineOnly}
            data-testid="contact-picker-online-now"
            onClick={() => setOnlineOnly((v) => !v)}
            style={onlineOnly
              ? { background: 'var(--phos)', color: 'var(--bg)' }
              : { background: 'transparent', color: 'var(--phos)' }}
          >
            ● Online now
          </button>
        </div>
      </div>
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {filtered.length === 0 ? (
          <div className="surface-sub" style={{ padding: 12 }}>No contacts match.</div>
        ) : filtered.map((c) => (
          <div
            key={c.pubkey}
            role="button"
            tabIndex={0}
            className="item-row"
            onClick={() => pick(c.pubkey)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') pick(c.pubkey); }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="item-main">
                <PresenceDot pubkey={c.pubkey} presenceMap={presenceMap} />
                {c.alias ?? `${c.pubkey.slice(0, 16)}…`}
              </div>
              <div className="item-meta">{c.pubkey.slice(0, 32)}… · {c.source}</div>
            </div>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <button
                className="pill"
                aria-label={`audio call ${c.alias ?? c.pubkey.slice(0, 8)}`}
                data-testid="contact-call-audio"
                onClick={(e) => { e.stopPropagation(); callQuick(c.pubkey, 'audio'); }}
              >
                ◉
              </button>
              <button
                className="pill"
                aria-label={`video call ${c.alias ?? c.pubkey.slice(0, 8)}`}
                data-testid="contact-call-video"
                onClick={(e) => { e.stopPropagation(); callQuick(c.pubkey, 'video'); }}
              >
                ◆
              </button>
              <div className="item-meta" aria-hidden="true">→</div>
            </div>
          </div>
        ))}
      </div>
      <SurfaceStyles />
    </EnvFrame>
  );
}

/** group-call-incoming — incoming group-call ring screen. */
function GroupCallIncoming({ ctx, director }: SurfaceProps): JSX.Element {
  const callId = (() => {
    try { return localStorage.getItem('alds-pending-group-call') ?? ''; } catch { return ''; }
  })();
  const accept = async (): Promise<void> => {
    if (callId === '') return;
    try {
      const { GroupCallManager } = await import('../../calls/GroupCallManager');
      await new GroupCallManager(ctx).joinCall(callId);
      try { localStorage.setItem('alds-active-group-call', callId); } catch { /* noop */ }
      director.bridge('group-call-active', 'joined');
    } catch { /* swallow */ }
  };
  const decline = (): void => {
    try { localStorage.removeItem('alds-pending-group-call'); } catch { /* noop */ }
    director.bridge('hub', '← hub');
  };
  return (
    <EnvFrame title="Incoming group call" glyph="◉" color="var(--blue)" director={director}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: 32, gap: 16 }}>
        <div className="t-meta env-sublabel">incoming · call {callId.slice(0, 8)}…</div>
        <div style={{ display: 'flex', gap: 16 }}>
          <button className="cta" onClick={() => { accept().catch(() => { /* swallow */ }); }}>accept</button>
          <button className="cta secondary" onClick={decline}>decline</button>
        </div>
      </div>
      <SurfaceStyles />
    </EnvFrame>
  );
}

/** group-call-active — participant list + mute/leave controls. */
function GroupCallActive({ ctx, director }: SurfaceProps): JSX.Element {
  const callId = (() => {
    try { return localStorage.getItem('alds-active-group-call') ?? ''; } catch { return ''; }
  })();
  const [participants, setParticipants] = useState<string[]>([]);
  const [audioMuted, setAudioMuted] = useState<boolean>(false);
  const [videoOn, setVideoOn] = useState<boolean>(false);

  const refresh = async (): Promise<void> => {
    if (callId === '') return;
    const call = await ctx.storage.db.group_calls.get(callId);
    setParticipants(call?.participantPubkeys ?? []);
  };
  useEffect(() => { refresh().catch(() => { /* swallow */ }); const t = setInterval(() => { refresh().catch(() => { /* swallow */ }); }, 3000); return () => clearInterval(t); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [callId]);

  const leave = async (): Promise<void> => {
    try {
      const { GroupCallManager } = await import('../../calls/GroupCallManager');
      await new GroupCallManager(ctx).leaveCall(callId);
    } catch { /* swallow */ }
    try { localStorage.removeItem('alds-active-group-call'); } catch { /* noop */ }
    director.bridge('hub', '← hub');
  };

  if (callId === '') {
    return (
      <EnvFrame title="Group call" glyph="◉" color="var(--blue)" director={director}>
        <div className="surface-sub" style={{ padding: 12 }}>No active call.</div>
      </EnvFrame>
    );
  }

  return (
    <EnvFrame title="Group call · active" glyph="◉" color="var(--blue)" director={director}>
      <div style={{ padding: 12 }}>
        <div className="t-meta env-sublabel">{participants.length} participant{participants.length === 1 ? '' : 's'}</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 6, marginTop: 8 }}>
          {participants.map((p) => (
            <div key={p} className="receipt-mini" style={{ aspectRatio: '1 / 1', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12 }}>
              {p.slice(0, 8)}…
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
          <button className="pill" onClick={() => setAudioMuted(!audioMuted)}>
            {audioMuted ? '🔇 muted' : '🎙 unmuted'}
          </button>
          <button className="pill" onClick={() => setVideoOn(!videoOn)}>
            {videoOn ? '📹 on' : '📷 off'}
          </button>
          <button className="pill" onClick={() => director.spawn('call-roster', 'roster')}>roster</button>
          <button className="cta" onClick={() => { leave().catch(() => { /* swallow */ }); }}>leave</button>
        </div>
      </div>
      <SurfaceStyles />
    </EnvFrame>
  );
}

/** call-roster — list view of participants in the active call. */
function CallRoster({ ctx, director }: SurfaceProps): JSX.Element {
  const callId = (() => {
    try { return localStorage.getItem('alds-active-group-call') ?? ''; } catch { return ''; }
  })();
  const [participants, setParticipants] = useState<string[]>([]);
  const presenceMap = usePresenceMap(ctx);
  useEffect(() => {
    if (callId === '') return;
    (async () => {
      const call = await ctx.storage.db.group_calls.get(callId);
      setParticipants(call?.participantPubkeys ?? []);
    })().catch(() => { /* swallow */ });
  /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [callId]);
  return (
    <EnvFrame title="Call roster" glyph="☰" color="var(--blue)" director={director}>
      <div style={{ padding: 12 }}>
        {participants.length === 0 ? (
          <div className="surface-sub">No participants.</div>
        ) : participants.map((p) => (
          <div key={p} className="item-row">
            <div className="item-main">
              <PresenceDot pubkey={p} presenceMap={presenceMap} />
              {p.slice(0, 24)}…
            </div>
            <div className="item-meta">{p === participants[0] ? 'host' : 'guest'}</div>
          </div>
        ))}
      </div>
      <SurfaceStyles />
    </EnvFrame>
  );
}

// ─── Phase 42 (v8) — plugin marketplace + audit-anchor renderers ────────

function PluginMarketplace({ ctx, director }: SurfaceProps): JSX.Element {
  type Row = { id: string; name?: string; version: string; status: 'installed' | 'installed-inactive' | 'available'; installedAt?: string };
  const [rows, setRows] = useState<Row[]>([]);
  const [status, setStatus] = useState<'idle' | 'busy' | 'error'>('idle');
  const [error, setError] = useState<string>('');

  const refresh = async (): Promise<void> => {
    try {
      const installed = await ctx.storage.db.plugins.toArray();
      const installedRows: Row[] = installed.map((p) => {
        // Pull a display name from the stored manifest JSON if available.
        let name: string | undefined;
        try {
          const parsed = JSON.parse(p.manifestJson) as { name?: string };
          if (typeof parsed.name === 'string') name = parsed.name;
        } catch { /* ignore — use id */ }
        return {
          id: p.id,
          version: p.version,
          status: p.status === 'active' ? 'installed' : 'installed-inactive',
          installedAt: p.installedAt,
          ...(name !== undefined ? { name } : {}),
        };
      });
      setRows(installedRows);
    } catch (err) {
      setStatus('error');
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  useEffect(() => { refresh().catch(() => { /* swallow */ }); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const toggle = async (id: string, current: Row['status']): Promise<void> => {
    setStatus('busy');
    try {
      const { PluginManager } = await import('../../plugins/PluginManager');
      const pm = new PluginManager(ctx);
      if (current === 'installed') await pm.deactivate(id);
      else if (current === 'installed-inactive') await pm.activate(id);
      await refresh();
      setStatus('idle');
    } catch (err) {
      setStatus('error');
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const uninstall = async (id: string): Promise<void> => {
    setStatus('busy');
    try {
      const { PluginManager } = await import('../../plugins/PluginManager');
      await new PluginManager(ctx).uninstall(id);
      await refresh();
      setStatus('idle');
    } catch (err) {
      setStatus('error');
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <EnvFrame title="Plugin marketplace" glyph="◇" color="var(--phos)" director={director}>
      <div style={{ padding: 12 }}>
        <div className="t-meta env-sublabel">
          {rows.length} installed · curated-available list fetched from operator (v8.x)
        </div>
        <div style={{ marginTop: 8 }}>
          {rows.length === 0 ? (
            <div className="surface-sub">No plugins installed. Install one via the operator's curated list.</div>
          ) : rows.map((r) => (
            <div key={r.id} className="receipt-mini" style={{ marginBottom: 6 }}>
              <div className="item-main">{r.name ?? r.id}</div>
              <div className="item-meta">
                {r.id} · v{r.version} · {r.status}
                {r.installedAt !== undefined ? ` · installed ${new Date(r.installedAt).toLocaleDateString()}` : ''}
              </div>
              <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                <button className="pill" disabled={status === 'busy'} onClick={() => { toggle(r.id, r.status).catch(() => { /* swallow */ }); }}>
                  {r.status === 'installed' ? 'deactivate' : 'activate'}
                </button>
                <button className="pill" disabled={status === 'busy'} onClick={() => { uninstall(r.id).catch(() => { /* swallow */ }); }}>
                  uninstall
                </button>
              </div>
            </div>
          ))}
        </div>
        {status === 'error' && (
          <div className="t-meta" style={{ color: 'var(--pink)', marginTop: 8 }}>{error}</div>
        )}
      </div>
      <SurfaceStyles />
    </EnvFrame>
  );
}

function AuditAnchor({ ctx, director }: SurfaceProps): JSX.Element {
  type Anchor = { id: string; chain: string; epoch: number; merkleRoot: string; anchoredAt: string; txHash: string };
  const [history, setHistory] = useState<Anchor[]>([]);
  const [latest, setLatest] = useState<{ merkleRoot: string; epoch: number; leafCount: number } | null>(null);
  const [status, setStatus] = useState<'idle' | 'computing' | 'error'>('idle');
  const [error, setError] = useState<string>('');

  const refresh = async (): Promise<void> => {
    const rows = await ctx.storage.db.anchor_log.orderBy('epoch').reverse().toArray();
    setHistory(rows as Anchor[]);
  };
  useEffect(() => { refresh().catch(() => { /* swallow */ }); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const compute = async (): Promise<void> => {
    setStatus('computing');
    try {
      const { GovernanceManager } = await import('../../governance/GovernanceManager');
      const gm = new GovernanceManager(ctx);
      const result = await gm.publishAuditAnchor({ chain: 'local' });
      setLatest(result);
      await refresh();
      setStatus('idle');
    } catch (err) {
      setStatus('error');
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const copy = (text: string): void => {
    try { void navigator.clipboard?.writeText(text); } catch { /* swallow */ }
  };

  return (
    <EnvFrame title="Audit anchor" glyph="⚓" color="var(--phos)" director={director}>
      <div style={{ padding: 12 }}>
        <div className="t-meta env-sublabel">
          Compute the Merkle root of the local audit log + publish on a public chain (operator does the on-chain tx).
        </div>
        <button className="cta" disabled={status === 'computing'} style={{ marginTop: 8 }}
          onClick={() => { compute().catch(() => { /* swallow */ }); }}>
          {status === 'computing' ? 'computing…' : "compute today's root"}
        </button>

        {latest !== null && (
          <div className="receipt-mini" style={{ marginTop: 12 }}>
            <div className="t-meta env-sublabel">latest · epoch {latest.epoch} · {latest.leafCount} entries</div>
            <div style={{ fontFamily: 'var(--mono)', wordBreak: 'break-all', fontSize: 11, marginTop: 4 }}>{latest.merkleRoot}</div>
            <button className="pill" style={{ marginTop: 6 }} onClick={() => copy(latest.merkleRoot)}>copy</button>
          </div>
        )}

        <div className="t-meta env-sublabel" style={{ marginTop: 16 }}>
          history · {history.length} entr{history.length === 1 ? 'y' : 'ies'}
        </div>
        <div style={{ marginTop: 4 }}>
          {history.length === 0 ? (
            <div className="surface-sub">No anchors computed yet.</div>
          ) : history.map((a) => (
            <div key={a.id} className="receipt-mini" style={{ marginBottom: 4 }}>
              <div className="item-meta">
                epoch {a.epoch} · {a.chain} · {new Date(a.anchoredAt).toLocaleString()}
                {a.txHash.length > 0 && ` · tx ${a.txHash.slice(0, 12)}…`}
              </div>
              <div style={{ fontFamily: 'var(--mono)', fontSize: 10, marginTop: 2, wordBreak: 'break-all' }}>{a.merkleRoot}</div>
            </div>
          ))}
        </div>

        {status === 'error' && (
          <div className="t-meta" style={{ color: 'var(--pink)', marginTop: 8 }}>{error}</div>
        )}
      </div>
      <SurfaceStyles />
    </EnvFrame>
  );
}

// ─── Phase 46.2 (v9) — voice-note player ──────────────────────────────

/**
 * VoiceNotePlayer — surfaces a voice-note manifest in the feed. Shows a
 * play button + size hint from manifests_cache. Actual chunk decoding +
 * playback requires the full manifest JSON which currently only lives on
 * the envelope payload (not in the summary cache row). v9.x will either
 * extend `manifests_cache` to carry the full manifest blob or hydrate it
 * from the envelope log on demand. For v9 today, we render the badge +
 * "playback coming in v9.x" placeholder.
 */
function VoiceNotePlayer({ ctx, manifestRef }: { ctx: AppContext; manifestRef: string; groupId: string }): JSX.Element {
  const manifestId = manifestRef.replace(/^(audio:|voice:)/, '');
  const [sizeLabel, setSizeLabel] = useState<string>('');

  useEffect(() => {
    (async () => {
      try {
        const row = await ctx.storage.db.manifests_cache.get(manifestId);
        if (row !== undefined) {
          const kb = Math.round(row.totalSize / 1024);
          setSizeLabel(kb > 0 ? `${kb} KB` : `${row.totalSize}B`);
        }
      } catch { /* swallow */ }
    })().catch(() => { /* swallow */ });
  /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [manifestId]);

  return (
    <div
      className="t-meta env-sublabel"
      style={{ marginTop: 6, padding: '4px 8px', border: '1px solid var(--phos)', borderRadius: 2, display: 'flex', alignItems: 'center', gap: 8 }}
    >
      <span style={{ color: 'var(--amber)' }}>🎙 voice note</span>
      {sizeLabel.length > 0 && <span>· {sizeLabel}</span>}
      <span style={{ opacity: 0.6, fontSize: 10 }}>· playback wiring v9.x</span>
    </div>
  );
}

// ─── Phase 47.2 (v9) — federated-search renderer ──────────────────────

function FederatedSearch({ ctx, director }: SurfaceProps): JSX.Element {
  type Hit = {
    id: string; queryId: string; responderNeighborhoodId: string;
    groupId: string; messageHash: string; snippet: string;
    matchedAt: string; receivedAt: string;
  };
  const [query, setQuery] = useState<string>('');
  const [queryId, setQueryId] = useState<string>('');
  const [hits, setHits] = useState<Hit[]>([]);
  const [status, setStatus] = useState<'idle' | 'searching' | 'error'>('idle');
  const [error, setError] = useState<string>('');
  const [sentTo, setSentTo] = useState<string[]>([]);

  const issue = async (): Promise<void> => {
    if (query.trim().length === 0) return;
    setStatus('searching');
    setError('');
    setHits([]);
    try {
      const { FederationManager } = await import('../../federation/FederationManager');
      const fm = new FederationManager(ctx);
      const result = await fm.federateSearch({ queryText: query.trim() });
      setQueryId(result.queryId);
      setSentTo(result.sentTo);
    } catch (err) {
      setStatus('error');
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  // Poll the results table every 1s while a query is active.
  useEffect(() => {
    if (queryId === '') return;
    let cancelled = false;
    const refresh = async (): Promise<void> => {
      const rows = await ctx.storage.db.federation_search_results
        .where('queryId').equals(queryId).toArray();
      if (cancelled) return;
      // Sort newest matchedAt first.
      rows.sort((a, b) => (a.matchedAt < b.matchedAt ? 1 : -1));
      setHits(rows as Hit[]);
    };
    refresh().catch(() => { /* swallow */ });
    const t = setInterval(() => { refresh().catch(() => { /* swallow */ }); }, 1000);
    return () => { cancelled = true; clearInterval(t); };
  /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [queryId]);

  return (
    <EnvFrame title="Federated search" glyph="⌕" color="var(--phos)" director={director}>
      <div style={{ padding: 12 }}>
        <div style={{ display: 'flex', gap: 6 }}>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { issue().catch(() => { /* swallow */ }); } }}
            placeholder="Search across federated neighborhoods…"
            style={{ flex: 1, background: 'transparent', color: 'var(--phos)', border: '1px solid var(--phos)', padding: 6, fontFamily: 'var(--mono)' }}
          />
          <button className="cta" disabled={query.trim().length === 0 || status === 'searching'}
            onClick={() => { issue().catch(() => { /* swallow */ }); }}>
            find
          </button>
        </div>
        {sentTo.length > 0 && (
          <div className="t-meta env-sublabel" style={{ marginTop: 8 }}>
            query {queryId.slice(0, 8)}… sent to {sentTo.length} neighborhood{sentTo.length === 1 ? '' : 's'}: {sentTo.join(', ')}
          </div>
        )}
        <div style={{ marginTop: 8 }}>
          {hits.length === 0 ? (
            <div className="surface-sub">
              {queryId === '' ? 'Issue a query.' : 'Awaiting responses…'}
            </div>
          ) : hits.map((h) => (
            <div key={h.id} className="receipt-mini" style={{ marginBottom: 6 }}>
              <div className="t-meta env-sublabel">
                @{h.responderNeighborhoodId} · {new Date(h.matchedAt).toLocaleString()}
              </div>
              <div style={{ marginTop: 4 }}>{h.snippet}</div>
            </div>
          ))}
        </div>
        {status === 'error' && (
          <div className="t-meta" style={{ color: 'var(--pink)', marginTop: 8 }}>{error}</div>
        )}
      </div>
      <SurfaceStyles />
    </EnvFrame>
  );
}

// ─── Phase U0 (v10+) — SASS landing surfaces ───────────────

/**
 * sass-chats — Signal/Telegram-shaped chats list. Pulls from the local
 * threads_index (regular threads + DMs + group conversations) and shows
 * the most recent activity. Tapping a row bridges to env-chat with the
 * thread selected.
 *
 * Scaffold (Phase U0.b) only — empty/loading state for fresh sessions.
 * Polish (preview text, unread badges, avatars) is U0.c+.
 */
function SASSChats({ ctx, director }: SurfaceProps): JSX.Element {
  const [rows, setRows] = useState<Array<{ threadId: string; kind: string; lastActivityAt: string; context: string; peerPubkey?: string }>>([]);
  const [loading, setLoading] = useState(true);
  const presenceMap = usePresenceMap(ctx);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const all = await ctx.storage.db.threads_index.toArray();
        if (cancelled) return;
        all.sort((a, b) => (b.lastActivityAt ?? '').localeCompare(a.lastActivityAt ?? ''));
        // Phase 54.b: enrich DM rows with the peer's inbox pubkey so the row
        // can render a PresenceDot. For DMs the threadId == social_groups.id;
        // group.members holds [self, peer] sorted, so we pick the one that
        // isn't us.
        let ownInboxHex = '';
        try {
          const own = await ctx.identity.pubkey({ environment: 'social', purpose: 'inbox' });
          ownInboxHex = Array.from(own).map((b) => b.toString(16).padStart(2, '0')).join('');
        } catch { /* leave empty — every row will look like "self" but that's fine */ }
        // DM group id format (per SocialGroupManager.getOrCreateDmGroup):
        //   `dm:${sorted(myInbox, peerInboxKey).join(':')}`
        // So we can recover the peer by parsing the id and dropping ourselves.
        const dmGroups = await ctx.storage.db.social_groups.where('kind').equals('dm').toArray();
        const peerByGroupId = new Map<string, string>();
        for (const g of dmGroups) {
          if (!g.id.startsWith('dm:')) continue;
          const [, a, b] = g.id.split(':');
          if (a === undefined || b === undefined) continue;
          const peer = a === ownInboxHex ? b : (b === ownInboxHex ? a : undefined);
          if (peer !== undefined) peerByGroupId.set(g.id, peer);
        }
        setRows(all.slice(0, 50).map((r) => {
          const k = (r as { kind?: string }).kind ?? 'thread';
          const peer = k === 'dm' ? peerByGroupId.get(r.threadId) : undefined;
          return {
            threadId: r.threadId,
            kind: k,
            lastActivityAt: r.lastActivityAt ?? '',
            context: typeof r.context === 'string' ? r.context : JSON.stringify(r.context),
            ...(peer !== undefined ? { peerPubkey: peer } : {}),
          };
        }));
      } catch {
        // Empty threads_index in a fresh session — leave rows empty.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [ctx]);

  return (
    <EnvFrame title="Chats" glyph="✦" color="var(--phos)" director={director}>
      <div style={{ padding: '4px 0' }}>
        <div className="t-meta env-sublabel" style={{ marginBottom: 12 }}>
          {loading ? 'loading conversations…' : rows.length === 0 ? 'no conversations yet' : `${rows.length} conversation${rows.length === 1 ? '' : 's'}`}
        </div>
        {rows.length === 0 && !loading && (
          <div className="empty-state schematic-frame" style={{ padding: 16 }}>
            <div className="t-serif" style={{ fontSize: 16, marginBottom: 6 }}>Your conversations are private here</div>
            <div className="surface-sub" style={{ marginBottom: 10 }}>
              Every chat is end-to-end encrypted with MLS — even the operator can&apos;t read your messages.
              Threads show up here when you send a DM, accept an incident invite, or join a group chat.
            </div>
            <div className="t-meta" style={{ color: 'var(--ink-dim)', marginBottom: 12 }}>
              No one is messaging you yet. Start the first conversation.
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {/* U2.6 — Chats compose lands on contact-picker FIRST so the
               * user chooses a recipient, then a new thread can be created
               * with that contact, then compose-message gets the thread
               * context it needs. Previously this pushed compose-message
               * directly which is an in-thread reply surface with no
               * recipient picker. */}
              <button className="cta" onClick={() => director.push('contact-picker', 'pick a recipient to message')}>
                ✎ start a new conversation
              </button>
            </div>
          </div>
        )}
        {rows.map((r) => (
          <div
            key={r.threadId}
            role="button"
            tabIndex={0}
            className="chat-row"
            onClick={() => {
              try { localStorage.setItem('alds-selected-thread', r.threadId); } catch { /* noop */ }
              director.bridge('env-chat', `thread ${r.threadId.slice(0, 8)}`);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                try { localStorage.setItem('alds-selected-thread', r.threadId); } catch { /* noop */ }
                director.bridge('env-chat', `thread ${r.threadId.slice(0, 8)}`);
              }
            }}
          >
            <div className="chat-row-glyph">{r.kind === 'dm' ? '◐' : '✦'}</div>
            <div className="chat-row-body">
              <div className="chat-row-title">
                {r.peerPubkey !== undefined && (
                  <PresenceDot pubkey={r.peerPubkey} presenceMap={presenceMap} />
                )}
                {r.threadId.slice(0, 12)}…
              </div>
              <div className="chat-row-meta t-meta">
                {r.kind} · {r.lastActivityAt ? new Date(r.lastActivityAt).toLocaleString() : '—'}
              </div>
            </div>
          </div>
        ))}
      </div>
      <style>{sassChatsStyles}</style>
      <SurfaceStyles />
    </EnvFrame>
  );
}

const sassChatsStyles = `
.chat-row {
  display: flex;
  gap: 12px;
  align-items: center;
  padding: 10px 8px;
  border-bottom: 1px solid var(--rule);
  cursor: pointer;
  transition: background 80ms ease;
}
.chat-row:hover, .chat-row:focus {
  background: rgba(120, 220, 140, 0.06);
  outline: none;
}
.chat-row-glyph {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--phos);
  font-size: 18px;
}
.chat-row-body { flex: 1; min-width: 0; }
.chat-row-title { font-size: 14px; color: var(--ink); }
.chat-row-meta { font-size: 10px; color: var(--ink-dim); margin-top: 2px; }
`;

/**
 * sass-more — drawer of secondary entry points. Every surface that
 * isn't reachable from Chats/Feed/Dispatch tabs lives here, organized
 * by category. Settings, Audit, Federation, Recovery, Plugins, etc.
 */
/**
 * U4 — Theme switcher widget. Reads / writes
 * `localStorage['alds-theme']` and sets `<html data-theme=...>` so the
 * CSS variable cascade picks up the new palette. Phosphor (default) is
 * the cyberpunk green-on-black; Warm is the civic-mutual-aid amber-on-
 * slate palette intended for non-technical users.
 */
function ThemeSwitcher(): JSX.Element {
  const [theme, setTheme] = useState<'phosphor' | 'warm'>(() => {
    try {
      const t = localStorage.getItem('alds-theme');
      return t === 'warm' ? 'warm' : 'phosphor';
    } catch { return 'phosphor'; }
  });
  const applyTheme = (next: 'phosphor' | 'warm') => {
    setTheme(next);
    try {
      localStorage.setItem('alds-theme', next);
      document.documentElement.setAttribute('data-theme', next);
    } catch { /* noop */ }
  };
  return (
    <div className="more-row" style={{ alignItems: 'center' }}>
      <div className="more-row-body">
        <div className="more-row-title">Theme</div>
        <div className="more-row-desc">Switch between phosphor (default) and warm.</div>
      </div>
      <div style={{ display: 'flex', gap: 4 }}>
        <button
          className={theme === 'phosphor' ? 'pill' : 'pill'}
          style={theme === 'phosphor' ? { borderColor: 'var(--phos)', color: 'var(--phos)' } : {}}
          onClick={() => applyTheme('phosphor')}
        >phosphor</button>
        <button
          className={theme === 'warm' ? 'pill' : 'pill'}
          style={theme === 'warm' ? { borderColor: 'var(--phos)', color: 'var(--phos)' } : {}}
          onClick={() => applyTheme('warm')}
        >warm</button>
      </div>
    </div>
  );
}

function SASSMore({ director }: SurfaceProps): JSX.Element {
  type Link = { surfaceId: string; label: string; desc: string; gesture: 'bridge' | 'push' };
  const sections: { title: string; links: Link[] }[] = [
    {
      title: 'Identity & Network',
      links: [
        { surfaceId: 'network-config',     label: 'Network mode',       desc: 'local-only / mesh, transport prefs', gesture: 'push' },
        { surfaceId: 'key-storage-config', label: 'Key storage',        desc: 'where root keys live + attestation', gesture: 'push' },
        { surfaceId: 'mesh-invite-generate', label: 'Invite a neighbor', desc: 'generate a mesh invite token',       gesture: 'push' },
        { surfaceId: 'mesh-invite-redeem',   label: 'Redeem an invite',  desc: 'paste a token from a neighbor',      gesture: 'push' },
      ],
    },
    {
      title: 'Audit & Trust',
      links: [
        { surfaceId: 'sass-audit-verify', label: 'Anchor verifier',  desc: 'verify the operator hasn\'t tampered with the audit log', gesture: 'push' },
        { surfaceId: 'audit-anchor',      label: 'Publish anchor',   desc: 'compute today\'s merkle root + publish on a public chain', gesture: 'push' },
        { surfaceId: 'export-bundle',    label: 'Export account',    desc: 'encrypted bundle for another device',          gesture: 'push' },
        { surfaceId: 'import-bundle',    label: 'Import account',    desc: 'restore from an exported bundle',              gesture: 'push' },
        { surfaceId: 'treaty-builder',   label: 'Federation treaty', desc: 'inter-neighborhood agreements',                gesture: 'push' },
      ],
    },
    {
      title: 'Discovery',
      links: [
        { surfaceId: 'sass-shell-chooser', label: 'Choose your shell',     desc: 'adult / kids / teens skin + home swap (Phase 73 full)', gesture: 'push' },
        { surfaceId: 'sass-surface-map',   label: 'All surfaces',          desc: 'every feature ALDS ships, grouped + searchable (Phase 73 interim)', gesture: 'push' },
        { surfaceId: 'news-paper',         label: 'News paper',            desc: 'curated local headlines + publish form (Phase 69.A)', gesture: 'push' },
        { surfaceId: 'federated-search',   label: 'Search the federation', desc: 'cross-neighborhood content search',  gesture: 'bridge' },
        { surfaceId: 'plugin-marketplace', label: 'Plugins',               desc: 'jurisdiction plugins + extensions',   gesture: 'push' },
        { surfaceId: 'sneakernet',         label: 'Sneakernet bundle',     desc: 'offline package for QR/USB transfer', gesture: 'push' },
      ],
    },
    {
      title: 'Communication',
      links: [
        { surfaceId: 'env-voice',        label: 'Calls',        desc: 'voice + video hub: recent + contacts + start',  gesture: 'bridge' },
        { surfaceId: 'call-history',     label: 'Call history', desc: 'past 1:1 and group calls',                       gesture: 'push' },
        { surfaceId: 'contact-picker',   label: 'Contacts',     desc: 'your contact list',                              gesture: 'push' },
      ],
    },
    {
      title: 'Moderation & resilience',
      links: [
        { surfaceId: 'sass-mascot',          label: 'Personal mascot', desc: 'L1 content filter + kid-mode lock + filter log (Phase 71 partial)', gesture: 'push' },
        { surfaceId: 'sass-prepublish-vote', label: 'Pending posts',   desc: 'vote on drafts awaiting publish (Phase 55)', gesture: 'push' },
        { surfaceId: 'sass-ble-status',      label: 'BLE mesh',        desc: 'offline queue + nearby peers (Phase 56)',    gesture: 'push' },
        { surfaceId: 'sass-sms-subscribers', label: 'SMS subscribers', desc: 'roster of SMS-only neighbors (Phase 57)',    gesture: 'push' },
      ],
    },
    {
      title: 'Recovery',
      links: [
        { surfaceId: 'sass-key-recovery-setup',   label: 'Set up social recovery', desc: 'split your seed across M-of-N trusted peers (Phase 53)', gesture: 'push' },
        { surfaceId: 'sass-key-recovery-restore', label: 'Recover identity',       desc: 'reconstruct from peers when you lose a device (Phase 53)', gesture: 'push' },
      ],
    },
  ];

  return (
    <EnvFrame title="More" glyph="≡" color="var(--ink)" director={director}>
      <div style={{ padding: '4px 0' }}>
        <div style={{ marginBottom: 18 }}>
          <div className="t-meta env-sublabel" style={{ marginBottom: 8 }}>Appearance</div>
          <ThemeSwitcher />
        </div>
        {sections.map((s) => (
          <div key={s.title} style={{ marginBottom: 18 }}>
            <div className="t-meta env-sublabel" style={{ marginBottom: 8 }}>{s.title}</div>
            {s.links.map((l) => (
              <div
                key={l.surfaceId}
                role="button"
                tabIndex={0}
                className="more-row"
                onClick={() => (l.gesture === 'bridge' ? director.bridge(l.surfaceId, l.label) : director.push(l.surfaceId, l.label))}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') (l.gesture === 'bridge' ? director.bridge(l.surfaceId, l.label) : director.push(l.surfaceId, l.label)); }}
              >
                <div className="more-row-body">
                  <div className="more-row-title">{l.label}</div>
                  <div className="more-row-desc">{l.desc}</div>
                </div>
                <div className="more-row-arrow">›</div>
              </div>
            ))}
          </div>
        ))}
      </div>
      <style>{sassMoreStyles}</style>
      <SurfaceStyles />
    </EnvFrame>
  );
}

/**
 * sass-audit-verify — Phase 52 close-out UI. Runs the three-layer
 * AuditVerifier against the local anchor_log and surfaces results in
 * a user-facing panel: clean count, mismatches with likely cause, and
 * (future) peer-attestation quorum.
 *
 * The More-tab link "Anchor verifier" was previously misdirected to
 * `audit-anchor` (the Phase 39 publish-side surface). This is the real
 * verify-side that the link promised.
 */
function SASSAuditVerify({ ctx, director }: SurfaceProps): JSX.Element {
  type Mismatch = import('../../governance/AuditVerifier').AuditMismatch;
  const [result, setResult] = useState<{
    anchorsChecked: number;
    anchorsVerified: number;
    mismatches: Mismatch[];
    verifiedAt: string;
  } | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const runVerify = async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const { AuditVerifier } = await import('../../governance/AuditVerifier');
      const verifier = new AuditVerifier(ctx);
      const r = await verifier.verifyAnchors();
      setResult(r);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void runVerify(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [ctx]);

  const status = (() => {
    if (loading) return { color: 'var(--ink-dim)', text: 'verifying…' };
    if (error !== null) return { color: 'var(--pink)', text: 'verifier error' };
    if (result === null) return { color: 'var(--ink-dim)', text: 'unknown' };
    if (result.anchorsChecked === 0) return { color: 'var(--ink-dim)', text: 'no anchors yet' };
    if (result.mismatches.length > 0) return { color: 'var(--amber)', text: 'TAMPER DETECTED' };
    return { color: 'var(--phos)', text: 'all clean' };
  })();

  return (
    <EnvFrame title="Anchor verifier" glyph="✓" color="var(--phos)" director={director}>
      <div style={{ padding: '4px 0' }}>
        <p className="surface-sub" style={{ marginBottom: 12 }}>
          Recomputes the merkle root of your local audit log and compares
          to each published anchor. If the operator tampered with the
          audit log without re-publishing, the recomputed root diverges
          and this panel raises an alarm.
        </p>

        <div className="verify-status schematic-frame" style={{ padding: 12, marginBottom: 12 }}>
          <div className="t-meta env-sublabel" style={{ marginBottom: 6 }}>status</div>
          <div className="t-serif" style={{ fontSize: 18, color: status.color }}>
            {status.text}
          </div>
          {result !== null && !loading && (
            <div className="t-meta" style={{ marginTop: 8, color: 'var(--ink-dim)' }}>
              {result.anchorsVerified}/{result.anchorsChecked} anchors verified clean ·{' '}
              last run {new Date(result.verifiedAt).toLocaleTimeString()}
            </div>
          )}
          {error !== null && (
            <div className="t-meta" style={{ marginTop: 8, color: 'var(--pink)' }}>{error}</div>
          )}
        </div>

        <button className="cta" onClick={() => void runVerify()} disabled={loading} style={{ marginBottom: 16 }}>
          {loading ? 'verifying…' : '↻ re-verify all anchors'}
        </button>

        {result !== null && result.mismatches.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <div className="t-meta env-sublabel" style={{ marginBottom: 8, color: 'var(--amber)' }}>
              ⚠ {result.mismatches.length} mismatch{result.mismatches.length === 1 ? '' : 'es'}
            </div>
            {result.mismatches.map((m) => (
              <div key={m.anchor.id} className="schematic-frame" style={{ padding: 10, marginBottom: 8 }}>
                <div className="t-meta" style={{ color: 'var(--amber)', marginBottom: 4 }}>
                  {m.anchor.id} · {m.likelyCause.replace(/-/g, ' ')}
                </div>
                <div className="t-meta" style={{ color: 'var(--ink-dim)' }}>
                  expected: <code>{m.expectedRoot.slice(0, 16)}…</code>
                </div>
                <div className="t-meta" style={{ color: 'var(--ink-dim)' }}>
                  observed: <code>{m.observedRoot.slice(0, 16) || '(empty)'}…</code>
                </div>
                <div className="t-meta" style={{ color: 'var(--ink-dim)' }}>
                  observed leaf count: {m.observedLeafCount}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="t-meta env-sublabel" style={{ marginBottom: 8, marginTop: 16 }}>verification layers</div>
        <div className="schematic-frame" style={{ padding: 12, fontSize: 11, lineHeight: 1.7, color: 'var(--ink-dim)' }}>
          <div><strong style={{ color: 'var(--phos)' }}>1. Local consistency</strong> — recomputes root from local audit_log, compares to anchor_log entries. Active above.</div>
          <div><strong style={{ color: 'var(--phos)' }}>2. Pinned content</strong> — fetches the anchor doc from its CID; checks the pinned merkleRoot. Needs operator&apos;s pin client.</div>
          <div><strong style={{ color: 'var(--phos)' }}>3. Peer attestations</strong> — quorum across peers&apos; signed observations of (epoch, cid). Needs gossip wiring (lands with v10 Phase 54).</div>
        </div>
      </div>
      <SurfaceStyles />
    </EnvFrame>
  );
}

const sassMoreStyles = `
.more-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 8px;
  border-bottom: 1px solid var(--rule);
  cursor: pointer;
  transition: background 80ms ease;
}
.more-row:hover, .more-row:focus { background: rgba(120, 220, 140, 0.06); outline: none; }
.more-row-body { flex: 1; min-width: 0; }
.more-row-title { font-size: 14px; color: var(--ink); }
.more-row-desc { font-size: 11px; color: var(--ink-dim); margin-top: 2px; }
.more-row-arrow { color: var(--ink-dim); font-size: 18px; }
`;

// ════════════════════════════════════════════════════════════════════
// v10 Phase 55-57 — UI surfaces
// ════════════════════════════════════════════════════════════════════

/**
 * sass-prepublish-vote — Phase 55. Lists pending drafts for a chosen
 * community-vote-prepublish group + shows each with tally + countdown +
 * approve/reject buttons. Tries to default the group to the first
 * subscription whose moderation policy is `community-vote-prepublish`.
 */
function SASSPrepublishVote({ ctx, director }: SurfaceProps): JSX.Element {
  type PendingRow = import('../../storage/schema').PendingPostRecord;
  type GroupRow = import('../../storage/schema').SocialGroupRecord;
  const [groups, setGroups] = useState<GroupRow[]>([]);
  const [groupId, setGroupId] = useState<string | null>(null);
  const [rows, setRows] = useState<PendingRow[]>([]);
  const [tallies, setTallies] = useState<Record<string, { approve: number; reject: number; quorum: number; expired: boolean }>>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Phase 55.b: cache own pubkey so we can show the "withdraw" action
  // on drafts the user themself authored.
  const [ownPubkey, setOwnPubkey] = useState<string>('');
  useEffect(() => {
    void (async () => {
      try {
        const own = await ctx.identity.pubkey({ environment: 'social', purpose: 'inbox' });
        setOwnPubkey(Array.from(own).map((b) => b.toString(16).padStart(2, '0')).join(''));
      } catch { /* leave empty */ }
    })();
  }, [ctx]);

  const loadGroups = async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const allGroups = await ctx.storage.db.social_groups.toArray();
      const eligible = allGroups.filter((g) => g.moderation === 'community-vote-prepublish' && g.archivedAt === undefined);
      setGroups(eligible);
      if (eligible.length > 0 && groupId === null) setGroupId(eligible[0]!.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  const loadPending = async (gid: string | null): Promise<void> => {
    if (gid === null) { setRows([]); setTallies({}); return; }
    try {
      const { ModerationManager } = await import('../../social/ModerationManager');
      const mod = new ModerationManager(ctx);
      const pending = await mod.listPending({ groupId: gid, status: 'pending' });
      setRows(pending);
      const tmap: Record<string, { approve: number; reject: number; quorum: number; expired: boolean }> = {};
      for (const r of pending) {
        const t = await mod.tally(r.id);
        tmap[r.id] = { approve: t.approveCount, reject: t.rejectCount, quorum: t.quorum, expired: t.expired };
      }
      setTallies(tmap);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  useEffect(() => { void loadGroups(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [ctx]);
  useEffect(() => { void loadPending(groupId); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [groupId]);

  const cast = async (pendingPostId: string, vote: 'approve' | 'reject'): Promise<void> => {
    setBusy(pendingPostId);
    setError(null);
    try {
      const { getModerationManager } = await import('../../social/ModerationManager');
      const mod = getModerationManager(ctx);
      await mod.castVote({ pendingPostId, vote });
      await mod.tryFinalize(pendingPostId);
      await loadPending(groupId);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  // Phase 55.b: author-side withdraw from the review surface.
  const withdraw = async (pendingPostId: string): Promise<void> => {
    setBusy(pendingPostId);
    setError(null);
    try {
      const { getModerationManager } = await import('../../social/ModerationManager');
      await getModerationManager(ctx).withdrawDraft(pendingPostId);
      await loadPending(groupId);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <EnvFrame title="Pending posts" glyph="⏳" color="var(--amber)" director={director}>
      <div style={{ padding: '4px 0' }}>
        <p className="surface-sub" style={{ marginBottom: 12 }}>
          Drafts awaiting community quorum before publish. Only groups with
          moderation set to <code>community-vote-prepublish</code> appear here.
        </p>

        {loading && <div className="t-meta" style={{ color: 'var(--ink-dim)' }}>loading…</div>}
        {error !== null && (
          <div className="t-meta" style={{ color: 'var(--pink)', marginBottom: 8 }}>{error}</div>
        )}

        {!loading && groups.length === 0 && (
          <div className="schematic-frame" style={{ padding: 12, color: 'var(--ink-dim)' }}>
            No groups are set to community-vote-prepublish yet. Configure one
            from <code>social-group-config</code> to start.
          </div>
        )}

        {groups.length > 0 && (
          <div style={{ marginBottom: 12 }}>
            <div className="t-meta env-sublabel" style={{ marginBottom: 6 }}>group</div>
            <select
              value={groupId ?? ''}
              onChange={(e) => setGroupId(e.target.value || null)}
              style={{ width: '100%', padding: 8, background: 'var(--bg)', color: 'var(--ink)', border: '1px solid var(--rule)' }}
            >
              {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
            </select>
          </div>
        )}

        {groupId !== null && rows.length === 0 && (
          <div className="schematic-frame" style={{ padding: 12, color: 'var(--ink-dim)' }}>
            no pending drafts for this group.
          </div>
        )}

        {rows.map((r) => {
          const t = tallies[r.id] ?? { approve: 0, reject: 0, quorum: r.quorum, expired: false };
          const payload = (() => { try { return JSON.parse(r.payloadJson); } catch { return {}; } })();
          const remainingMs = new Date(r.expiresAt).getTime() - Date.now();
          const remainingMin = Math.max(0, Math.floor(remainingMs / 60_000));
          const text = (payload as { text?: string }).text ?? '(no text)';
          return (
            <div key={r.id} className="schematic-frame" style={{ padding: 12, marginBottom: 12 }}>
              <div className="t-meta" style={{ color: 'var(--ink-dim)', marginBottom: 6 }}>
                draft · expires in {remainingMin} min{remainingMin === 1 ? '' : 's'}
                {t.expired ? ' (overdue)' : ''}
              </div>
              <div style={{ marginBottom: 10, color: 'var(--ink)' }}>{text}</div>
              <div className="t-meta" style={{ marginBottom: 8, color: 'var(--ink-dim)' }}>
                <span style={{ color: 'var(--phos)' }}>{t.approve}/{t.quorum} approve</span>
                {' · '}
                <span style={{ color: 'var(--pink)' }}>{t.reject} reject</span>
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button
                  className="cta"
                  disabled={busy === r.id}
                  onClick={() => void cast(r.id, 'approve')}
                >
                  {busy === r.id ? '…' : '✓ approve'}
                </button>
                <button
                  className="cta secondary"
                  disabled={busy === r.id}
                  onClick={() => void cast(r.id, 'reject')}
                >
                  ✗ reject
                </button>
                {ownPubkey !== '' && r.authorPubkey === ownPubkey && (
                  <button
                    className="cta secondary"
                    style={{ borderColor: 'var(--amber)', color: 'var(--amber)' }}
                    disabled={busy === r.id}
                    data-testid="prepublish-vote-withdraw"
                    onClick={() => void withdraw(r.id)}
                  >
                    ⤺ withdraw
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <SurfaceStyles />
    </EnvFrame>
  );
}

/**
 * sass-ble-status — Phase 56. Surface for the BLE outbox + peer book.
 * Lets the user manually trigger flushOnRecovery (after the radio comes
 * back, but operators may also want to force a drain).
 */
function SASSBleStatus({ ctx, director }: SurfaceProps): JSX.Element {
  type OutboxRow = import('../../storage/schema').BleOutboxRecord;
  type PeerRow = import('../../storage/schema').BlePeerRecord;
  const [pendingRows, setPendingRows] = useState<OutboxRow[]>([]);
  const [flushedCount, setFlushedCount] = useState<number>(0);
  const [peers, setPeers] = useState<PeerRow[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [busy, setBusy] = useState<boolean>(false);
  const [lastResult, setLastResult] = useState<string | null>(null);

  const load = async (): Promise<void> => {
    setLoading(true);
    try {
      const all = await ctx.storage.db.ble_outbox.toArray();
      setPendingRows(all.filter((r) => r.flushedAt === undefined));
      setFlushedCount(all.filter((r) => r.flushedAt !== undefined).length);
      const peerRows = await ctx.storage.db.ble_peer_book.toArray();
      peerRows.sort((a, b) => b.lastSeenAt.localeCompare(a.lastSeenAt));
      setPeers(peerRows);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [ctx]);

  const flush = async (): Promise<void> => {
    setBusy(true);
    setLastResult(null);
    try {
      const { BleQueueManager } = await import('../../transport/BleQueueManager');
      const q = new BleQueueManager(ctx);
      const result = await q.flushOnRecovery();
      setLastResult(`${result.drained}/${result.attempted} drained · ${result.failed} failed · ${result.remaining} remaining`);
      await load();
    } catch (e) {
      setLastResult(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <EnvFrame title="BLE mesh" glyph="◢" color="var(--blue)" director={director}>
      <div style={{ padding: '4px 0' }}>
        <p className="surface-sub" style={{ marginBottom: 12 }}>
          Bluetooth-only envelopes queued for sync. When the internet
          comes back, the queue drains automatically — but you can force
          a flush below. (Phase 56 ships the substrate; a real BLE
          radio lands in v11 once mobile-native plugins mature.)
        </p>

        <div className="schematic-frame" style={{ padding: 12, marginBottom: 12 }}>
          <div className="t-meta env-sublabel" style={{ marginBottom: 6 }}>queue</div>
          {loading ? (
            <div className="t-meta" style={{ color: 'var(--ink-dim)' }}>loading…</div>
          ) : (
            <div className="t-serif" style={{ fontSize: 18, color: pendingRows.length > 0 ? 'var(--amber)' : 'var(--phos)' }}>
              {pendingRows.length} pending · {flushedCount} flushed
            </div>
          )}
          <button
            className="cta"
            onClick={() => void flush()}
            disabled={busy || pendingRows.length === 0}
            style={{ marginTop: 10 }}
          >
            {busy ? 'flushing…' : '↻ flush queue now'}
          </button>
          {lastResult !== null && (
            <div className="t-meta" style={{ marginTop: 8, color: 'var(--ink-dim)' }}>{lastResult}</div>
          )}
        </div>

        <div className="t-meta env-sublabel" style={{ marginBottom: 8 }}>nearby peers</div>
        {peers.length === 0 ? (
          <div className="schematic-frame" style={{ padding: 12, color: 'var(--ink-dim)' }}>
            No BLE peers observed yet. (No radio in v10 — peers populate via
            <code> _testRecordPeer</code> until mobile-native lands.)
          </div>
        ) : (
          peers.map((p) => (
            <div key={p.pubkey} className="schematic-frame" style={{ padding: 10, marginBottom: 8 }}>
              <div className="t-meta" style={{ color: 'var(--ink)' }}>
                {p.pubkey.slice(0, 10)}… <span style={{ color: 'var(--ink-dim)' }}>· {p.deviceId}</span>
              </div>
              <div className="t-meta" style={{ color: 'var(--ink-dim)', marginTop: 4 }}>
                rssi {p.rssi} dBm · {p.hops} hop{p.hops === 1 ? '' : 's'} ·{' '}
                {new Date(p.lastSeenAt).toLocaleTimeString()}
              </div>
            </div>
          ))
        )}
      </div>
      <SurfaceStyles />
    </EnvFrame>
  );
}

/**
 * sass-sms-subscribers — Phase 57. Operator-side roster of SMS-only
 * subscribers. Lets the operator register a new subscriber + opt-out
 * existing ones. Outbound dispatch + inbound parsing happen via
 * SmsGatewayManager when wired to a real SmsProvider (Twilio / Telnyx /
 * Asterisk); the UI just manages the roster.
 */
function SASSSmsSubscribers({ ctx, director }: SurfaceProps): JSX.Element {
  type SubRow = import('../../storage/schema').SmsSubscriberRecord;
  const [rows, setRows] = useState<SubRow[]>([]);
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [pairedPubkey, setPairedPubkey] = useState<string>('');
  const [neighborhoodId, setNeighborhoodId] = useState<string>('');
  const [label, setLabel] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [busy, setBusy] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const load = async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const all = await ctx.storage.db.sms_subscribers.toArray();
      all.sort((a, b) => b.addedAt.localeCompare(a.addedAt));
      setRows(all);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [ctx]);

  const register = async (): Promise<void> => {
    if (phoneNumber === '' || pairedPubkey === '' || neighborhoodId === '') {
      setError('phone, pubkey, and neighborhood are required');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const { SmsGatewayManager, MockSmsProvider } = await import('../../sms/SmsGatewayManager');
      const gateway = new SmsGatewayManager(ctx, new MockSmsProvider());
      await gateway.registerSubscriber({
        phoneNumber,
        pairedAldsPubkey: pairedPubkey,
        neighborhoodId,
        ...(label !== '' && { label }),
      });
      setPhoneNumber(''); setPairedPubkey(''); setLabel('');
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const optOut = async (phone: string): Promise<void> => {
    setBusy(true);
    try {
      const { SmsGatewayManager, MockSmsProvider } = await import('../../sms/SmsGatewayManager');
      const gateway = new SmsGatewayManager(ctx, new MockSmsProvider());
      await gateway.optOutSubscriber(phone);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <EnvFrame title="SMS subscribers" glyph="📱" color="var(--phos)" director={director}>
      <div style={{ padding: '4px 0' }}>
        <p className="surface-sub" style={{ marginBottom: 12 }}>
          Bridge SMS-only neighbors into ALDS. Each subscriber pairs a phone
          number with an ALDS identity; high-priority dispatches + mentions
          forward as plain SMS. <strong style={{ color: 'var(--amber)' }}>The carrier and the operator see the SMS body.</strong>
        </p>

        <div className="schematic-frame" style={{ padding: 12, marginBottom: 16 }}>
          <div className="t-meta env-sublabel" style={{ marginBottom: 8 }}>register a new subscriber</div>
          <input
            placeholder="+15551234567"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            style={{ width: '100%', padding: 6, marginBottom: 8, background: 'var(--bg)', color: 'var(--ink)', border: '1px solid var(--rule)' }}
          />
          <input
            placeholder="paired ALDS pubkey (hex)"
            value={pairedPubkey}
            onChange={(e) => setPairedPubkey(e.target.value)}
            style={{ width: '100%', padding: 6, marginBottom: 8, background: 'var(--bg)', color: 'var(--ink)', border: '1px solid var(--rule)' }}
          />
          <input
            placeholder="neighborhoodId"
            value={neighborhoodId}
            onChange={(e) => setNeighborhoodId(e.target.value)}
            style={{ width: '100%', padding: 6, marginBottom: 8, background: 'var(--bg)', color: 'var(--ink)', border: '1px solid var(--rule)' }}
          />
          <input
            placeholder="label (optional, e.g. Grandma)"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            style={{ width: '100%', padding: 6, marginBottom: 8, background: 'var(--bg)', color: 'var(--ink)', border: '1px solid var(--rule)' }}
          />
          <button className="cta" onClick={() => void register()} disabled={busy}>
            {busy ? '…' : '+ register'}
          </button>
          {error !== null && (
            <div className="t-meta" style={{ marginTop: 8, color: 'var(--pink)' }}>{error}</div>
          )}
        </div>

        <div className="t-meta env-sublabel" style={{ marginBottom: 8 }}>
          subscribers · {rows.length}
        </div>
        {loading ? (
          <div className="t-meta" style={{ color: 'var(--ink-dim)' }}>loading…</div>
        ) : rows.length === 0 ? (
          <div className="schematic-frame" style={{ padding: 12, color: 'var(--ink-dim)' }}>
            no subscribers yet.
          </div>
        ) : (
          rows.map((s) => (
            <div key={s.phoneNumber} className="schematic-frame" style={{ padding: 10, marginBottom: 8 }}>
              <div style={{ color: 'var(--ink)' }}>
                {s.label !== undefined && <strong>{s.label} · </strong>}{s.phoneNumber}
              </div>
              <div className="t-meta" style={{ color: 'var(--ink-dim)', marginTop: 4 }}>
                reply code <code>{s.replyCode}</code> · {s.neighborhoodId} ·{' '}
                added {new Date(s.addedAt).toLocaleDateString()}
                {s.optedOutAt !== undefined && (
                  <span style={{ color: 'var(--amber)' }}> · opted-out</span>
                )}
              </div>
              <div className="t-meta" style={{ color: 'var(--ink-dim)', marginTop: 4 }}>
                paired: <code>{s.pairedAldsPubkey.slice(0, 16)}…</code>
              </div>
              {s.optedOutAt === undefined && (
                <button
                  className="cta secondary"
                  onClick={() => void optOut(s.phoneNumber)}
                  disabled={busy}
                  style={{ marginTop: 8 }}
                >
                  opt out
                </button>
              )}
            </div>
          ))
        )}
      </div>
      <SurfaceStyles />
    </EnvFrame>
  );
}

// ─── Phase 59 (v11) — Phase 54.b PresenceDot component + hook ──────────

/**
 * usePresenceMap — polls presence_status every 30s and returns a Map
 * keyed by peer pubkey. Used by PresenceDot to colorize peer rows.
 *
 * Phase 54.b (v11): heartbeats now carry the sender's social:inbox-context
 * pubkey in addition to the hub-context signer key. The map exposes BOTH
 * keys → the same status, so caller surfaces holding either pubkey shape
 * (env-chat hub pubkey OR social:inbox peerInboxKey) resolve to the right
 * dot. Falls back gracefully for older peers whose heartbeats predate the
 * dual-pubkey carry.
 */
function usePresenceMap(ctx: AppContext): Map<string, 'available' | 'busy' | 'invisible'> {
  const [map, setMap] = useState<Map<string, 'available' | 'busy' | 'invisible'>>(new Map());

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setInterval> | null = null;

    const refresh = async (): Promise<void> => {
      try {
        const rows = await ctx.storage.db.presence_status.toArray();
        const now = Date.now();
        const next = new Map<string, 'available' | 'busy' | 'invisible'>();
        for (const r of rows) {
          // 90s staleness window matches PresenceManager.STALENESS_WINDOW_MS.
          if (now - new Date(r.lastHeartbeatAt).getTime() > 90_000) continue;
          next.set(r.pubkey, r.status);
          if (r.inboxPubkey !== undefined && r.inboxPubkey !== r.pubkey) {
            next.set(r.inboxPubkey, r.status);
          }
        }
        if (!cancelled) setMap(next);
      } catch { /* swallow — surface stays gray */ }
    };

    void refresh();
    timer = setInterval(() => { void refresh(); }, 30_000);
    return () => {
      cancelled = true;
      if (timer !== null) clearInterval(timer);
    };
  }, [ctx]);

  return map;
}

/**
 * PresenceDot — colored circle indicator next to a peer pubkey.
 * Green = available, amber = busy, gray = offline/unknown.
 */
function PresenceDot({
  pubkey,
  presenceMap,
  size = 8,
}: {
  pubkey: string;
  presenceMap: Map<string, 'available' | 'busy' | 'invisible'>;
  size?: number;
}): JSX.Element {
  const status = presenceMap.get(pubkey);
  const color = status === 'available' ? 'var(--phos)'
    : status === 'busy'      ? 'var(--amber)'
    : 'var(--ink-dim)';
  const label = status === 'available' ? 'online' : status === 'busy' ? 'busy' : 'offline';
  return (
    <span
      aria-label={`status: ${label}`}
      title={label}
      style={{
        display: 'inline-block',
        width: size,
        height: size,
        borderRadius: '50%',
        background: color,
        marginRight: 6,
        verticalAlign: 'middle',
      }}
    />
  );
}

// ─── Phase 59 (v11) — Phase 53.b key recovery wizard surfaces ──────────

/**
 * sass-key-recovery-setup — pick M-of-N threshold + peers + send shares.
 *
 * Uses existing 1:1 chat threads as the delivery channel. The user picks
 * ≥ threshold contacts that have an active thread; KeyRecoveryManager
 * splits their seed into N shares and dispatches each via ThreadManager
 * sendMessage. Successful shares get rows in peer_shares_given.
 */
function SASSKeyRecoverySetup({ ctx, director }: SurfaceProps): JSX.Element {
  type ThreadRow = import('../../storage/schema').ThreadIndexRecord;
  type GivenRow = import('../../storage/schema').PeerShareGivenRecord;

  const [threads, setThreads] = useState<ThreadRow[]>([]);
  const [selectedThreadIds, setSelectedThreadIds] = useState<Set<string>>(new Set());
  // Phase 53.b: per-thread "I confirmed this peer's pubkey out-of-band"
  // checkbox. Submit is gated on every selected peer being in this set so
  // a silent MITM swap of the cached counterparty pubkey can't slip a share.
  const [verifiedThreadIds, setVerifiedThreadIds] = useState<Set<string>>(new Set());
  const [threshold, setThreshold] = useState<number>(3);
  const [seedHex, setSeedHex] = useState<string>('');
  const [busy, setBusy] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ recoveryConfigId: string; sharesIssued: number } | null>(null);
  const [history, setHistory] = useState<GivenRow[]>([]);
  // Phase 53.b: peer pubkey resolution cache, indexed by threadId. Drives
  // the formatted fingerprint shown next to each row + the verified-checkbox
  // gating. Populated alongside `threads` so the fingerprint is visible
  // before the user clicks anything.
  const [peerPubkeyByThread, setPeerPubkeyByThread] = useState<Map<string, string>>(new Map());

  const refresh = async (): Promise<void> => {
    try {
      const allThreads = await ctx.storage.db.threads_index.toArray();
      // Only 1:1 threads (2 members) — multi-party threads aren't meaningful
      // recovery channels because shares need to land with a specific peer.
      const active = allThreads
        .filter((t) => !t.archived && t.memberCount === 2)
        .sort((a, b) => b.lastActivityAt.localeCompare(a.lastActivityAt));
      setThreads(active);
      // Phase 53.b: resolve each thread's counterparty pubkey from message_cache
      // up front so we can show fingerprints in the row without waiting for
      // submit. Falls back gracefully when there's no message history yet.
      const ownPubkeyBytes = await ctx.identity.pubkey({ environment: 'chat', purpose: 'identity' });
      const ownPubkeyHex = Array.from(ownPubkeyBytes).map((b) => b.toString(16).padStart(2, '0')).join('');
      const pubkeyByThread = new Map<string, string>();
      for (const t of active) {
        const msgs = await ctx.storage.db.message_cache
          .where('threadId').equals(t.threadId).toArray();
        const peerMsg = msgs.find((m) => m.senderPubkey !== ownPubkeyHex);
        if (peerMsg?.senderPubkey !== undefined) {
          pubkeyByThread.set(t.threadId, peerMsg.senderPubkey);
        }
      }
      setPeerPubkeyByThread(pubkeyByThread);
      const given = await ctx.storage.db.peer_shares_given.toArray();
      setHistory(given.sort((a, b) => b.sentAt.localeCompare(a.sentAt)));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  useEffect(() => { void refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [ctx]);

  const toggleThread = (threadId: string): void => {
    setSelectedThreadIds((prev) => {
      const next = new Set(prev);
      if (next.has(threadId)) next.delete(threadId); else next.add(threadId);
      return next;
    });
  };

  const submit = async (): Promise<void> => {
    setError(null);
    setResult(null);
    if (selectedThreadIds.size < threshold) {
      setError(`Pick at least ${threshold} peers (you picked ${selectedThreadIds.size}).`);
      return;
    }
    if (threshold < 2) {
      setError('Threshold must be ≥ 2 to provide social-recovery semantics.');
      return;
    }
    // Phase 53.b: every selected peer with a known pubkey must be verified.
    // Threads without a resolved pubkey (no message history) bypass the
    // check — `submit` already substitutes a `thread:<id>` placeholder for
    // those, so there's no real fingerprint to compare.
    const unverified: string[] = [];
    for (const tid of selectedThreadIds) {
      if (peerPubkeyByThread.has(tid) && !verifiedThreadIds.has(tid)) {
        unverified.push(tid);
      }
    }
    if (unverified.length > 0) {
      setError(`Confirm out-of-band that the fingerprint for each selected peer matches before sending shares (${unverified.length} still unconfirmed).`);
      return;
    }
    const trimmed = seedHex.trim().replace(/[^0-9a-fA-F]/g, '');
    if (trimmed.length < 32) {
      setError('Seed hex too short (need ≥ 16 bytes / 32 hex chars).');
      return;
    }
    const seed = new Uint8Array(trimmed.length / 2);
    for (let i = 0; i < seed.length; i++) seed[i] = parseInt(trimmed.slice(i * 2, i * 2 + 2), 16);

    setBusy(true);
    try {
      // For each selected thread, use the most recent counterparty sender
      // from message_cache as the peer pubkey. Falls back to a synthetic
      // `thread:<id>` placeholder so setupRecovery's bookkeeping fields
      // still index correctly even if message history is empty.
      const peers: Array<{ pubkey: string; label?: string; threadId: string }> = [];
      const ownPubkeyBytes = await ctx.identity.pubkey({ environment: 'chat', purpose: 'identity' });
      const ownPubkeyHex = Array.from(ownPubkeyBytes).map((b) => b.toString(16).padStart(2, '0')).join('');
      for (const threadId of selectedThreadIds) {
        const t = threads.find((x) => x.threadId === threadId);
        if (t === undefined) continue;
        const msgs = await ctx.storage.db.message_cache
          .where('threadId').equals(threadId).toArray();
        const peerMsg = msgs.find((m) => m.senderPubkey !== ownPubkeyHex);
        const peerPubkey = peerMsg?.senderPubkey ?? `thread:${threadId}`;
        peers.push({
          pubkey: peerPubkey,
          ...(t.name !== '' ? { label: t.name } : {}),
          threadId,
        });
      }
      if (peers.length < threshold) {
        setError(`Only resolved ${peers.length} eligible peers from selected threads (need ${threshold}).`);
        setBusy(false);
        return;
      }
      const { KeyRecoveryManager } = await import('../../recovery/KeyRecoveryManager');
      const mgr = new KeyRecoveryManager(ctx);
      const r = await mgr.setupRecovery({ seed, peers, threshold });
      setResult({ recoveryConfigId: r.recoveryConfigId, sharesIssued: r.sharesIssued });
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <EnvFrame title="Set up social recovery" glyph="⚷" color="var(--phos)" director={director}>
      <div style={{ padding: '4px 0' }}>
        <p className="surface-sub" style={{ marginBottom: 12 }}>
          Split your identity seed across ≥ {threshold} trusted peers using Shamir secret-sharing. If you lose your device, any {threshold} of them can help you recover. No single peer can reconstruct alone.
        </p>

        <div style={{ marginBottom: 14 }}>
          <div className="t-meta env-sublabel" style={{ marginBottom: 6 }}>threshold (M)</div>
          <input
            type="number"
            min={2}
            max={Math.max(2, selectedThreadIds.size)}
            value={threshold}
            onChange={(e) => setThreshold(Math.max(2, parseInt(e.target.value, 10) || 2))}
            style={{ width: 100, padding: 6, background: 'var(--bg)', color: 'var(--ink)', border: '1px solid var(--rule)', fontFamily: 'var(--mono)' }}
          />
          <span className="t-meta" style={{ marginLeft: 8, color: 'var(--ink-dim)' }}>
            of {selectedThreadIds.size} selected peers (N)
          </span>
        </div>

        <div style={{ marginBottom: 14 }}>
          <div className="t-meta env-sublabel" style={{ marginBottom: 6 }}>seed (hex)</div>
          <input
            type="password"
            placeholder="paste your identity seed in hex (≥ 32 hex chars)"
            value={seedHex}
            onChange={(e) => setSeedHex(e.target.value)}
            style={{ width: '100%', padding: 6, background: 'var(--bg)', color: 'var(--ink)', border: '1px solid var(--rule)', fontFamily: 'var(--mono)' }}
          />
          <div className="t-meta" style={{ color: 'var(--ink-dim)', marginTop: 4 }}>
            Get this from Inspector → reveal seed (mobile: backup banner → reveal). Hex only; nothing is logged or sent in the clear.
          </div>
        </div>

        <div style={{ marginBottom: 12 }}>
          <div className="t-meta env-sublabel" style={{ marginBottom: 6 }}>
            select peers ({selectedThreadIds.size} / {threads.length} threads)
          </div>
          {threads.length === 0 && (
            <div className="schematic-frame" style={{ padding: 12, color: 'var(--ink-dim)' }}>
              No active threads. Invite a peer first via Hub → Invite a neighbor.
            </div>
          )}
          {threads.map((t) => {
            const peer = peerPubkeyByThread.get(t.threadId);
            const fingerprint = peer !== undefined ? formatFingerprint(peer) : null;
            const selected = selectedThreadIds.has(t.threadId);
            const verified = verifiedThreadIds.has(t.threadId);
            return (
              <div
                key={t.threadId}
                className="item-row"
                style={{ flexDirection: 'column', alignItems: 'stretch', cursor: 'default', ['--env-color' as string]: 'var(--phos)' } as React.CSSProperties}
              >
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={selected}
                    onChange={() => toggleThread(t.threadId)}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="item-main">{t.name !== '' ? t.name : `thread ${t.threadId.slice(0, 8)}…`}</div>
                    <div className="item-meta">
                      {t.threadId.slice(0, 16)}… · last active {ago(t.lastActivityAt)}
                    </div>
                  </div>
                </label>
                {selected && fingerprint !== null && (
                  <div
                    style={{
                      marginTop: 6,
                      marginLeft: 26,
                      padding: 8,
                      border: '1px solid var(--rule)',
                      background: 'var(--bg-elev, transparent)',
                    }}
                  >
                    <div className="t-meta" style={{ color: 'var(--ink-dim)', marginBottom: 4 }}>
                      Verify peer fingerprint out-of-band (call, in-person, signed note)
                    </div>
                    <div className="t-mono" style={{ fontFamily: 'var(--mono)', letterSpacing: 1 }} data-testid={`recovery-fingerprint-${t.threadId.slice(0, 8)}`}>
                      {fingerprint}
                    </div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6 }}>
                      <input
                        type="checkbox"
                        checked={verified}
                        data-testid={`recovery-verified-${t.threadId.slice(0, 8)}`}
                        onChange={() => {
                          setVerifiedThreadIds((prev) => {
                            const next = new Set(prev);
                            if (next.has(t.threadId)) next.delete(t.threadId);
                            else next.add(t.threadId);
                            return next;
                          });
                        }}
                      />
                      <span className="t-meta">✓ I confirmed this peer's fingerprint out-of-band</span>
                    </label>
                  </div>
                )}
                {selected && fingerprint === null && (
                  <div
                    style={{ marginTop: 6, marginLeft: 26, padding: 8, border: '1px dashed var(--rule)' }}
                    className="t-meta"
                  >
                    No message history yet — this thread will use a placeholder peer id.
                    Open the thread and exchange one message before sending a share so
                    the real fingerprint can be verified.
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <button
            className="cta"
            disabled={busy || threads.length === 0}
            onClick={() => void submit()}
            data-testid="key-recovery-setup-submit"
          >
            {busy ? 'sending shares…' : `send ${selectedThreadIds.size} shares`}
          </button>
          <button
            className="cta secondary"
            onClick={() => director.push('sass-key-recovery-restore', 'restore from peers')}
          >
            restore (lost device)
          </button>
        </div>

        {error !== null && (
          <div className="receipt-mini" style={{ borderColor: 'var(--pink)', marginTop: 12 }}>
            <div className="t-meta" style={{ color: 'var(--pink)' }}>{error}</div>
          </div>
        )}
        {result !== null && (
          <div className="receipt-mini" style={{ borderColor: 'var(--phos)', marginTop: 12 }}>
            <div className="t-meta" style={{ color: 'var(--phos)' }}>
              ✓ {result.sharesIssued} share{result.sharesIssued === 1 ? '' : 's'} sent · config <code>{result.recoveryConfigId.slice(0, 12)}…</code>
            </div>
          </div>
        )}

        {history.length > 0 && (
          <div style={{ marginTop: 18 }}>
            <div className="t-meta env-sublabel" style={{ marginBottom: 6 }}>previously sent</div>
            {history.slice(0, 10).map((h) => (
              <div key={h.id} className="t-meta" style={{ color: 'var(--ink-dim)', marginBottom: 4 }}>
                {h.peerLabel ?? h.peerPubkey.slice(0, 12) + '…'} · share x={h.shareX} / {h.totalShares} · {ago(h.sentAt)}
              </div>
            ))}
          </div>
        )}
      </div>
      <SurfaceStyles />
    </EnvFrame>
  );
}

/**
 * sass-key-recovery-restore — list incoming shares we hold + reconstruct.
 *
 * Shows shares grouped by the original requester's pubkey. When the count
 * meets the threshold for any requester, the reconstruct button assembles
 * the seed and displays it for the user to copy to their new device.
 */
function SASSKeyRecoveryRestore({ ctx, director }: SurfaceProps): JSX.Element {
  type HeldRow = import('../../storage/schema').PeerShareHeldRecord;
  const [heldByRequester, setHeldByRequester] = useState<Map<string, HeldRow[]>>(new Map());
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [reconstructedSeed, setReconstructedSeed] = useState<string | null>(null);
  // Phase 53.b: auto-trigger reconstruction the first time the threshold is
  // met for a requester. Tracked per-requester so a manual reset (e.g.
  // pressing reconstruct again with different shares) is still possible.
  const [autoReconstructed, setAutoReconstructed] = useState<Set<string>>(new Set());

  const refresh = async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const all = await ctx.storage.db.peer_shares_held.toArray();
      const byReq = new Map<string, HeldRow[]>();
      for (const row of all) {
        const arr = byReq.get(row.requesterPubkey) ?? [];
        arr.push(row);
        byReq.set(row.requesterPubkey, arr);
      }
      setHeldByRequester(byReq);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [ctx]);

  // Phase 53.b: poll for new shares every 10s + auto-trigger reconstruction
  // when threshold first becomes satisfied. Stops re-running for a requester
  // after the first auto-trigger so manual reconstruct stays in user control.
  useEffect(() => {
    const interval = setInterval(() => { void refresh(); }, 10_000);
    return () => clearInterval(interval);
  /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [ctx]);

  const reconstruct = async (rows: HeldRow[]): Promise<void> => {
    setError(null);
    setReconstructedSeed(null);
    if (rows.length === 0) return;
    const threshold = rows[0]!.threshold;
    if (rows.length < threshold) {
      setError(`Need ${threshold} shares to reconstruct; you have ${rows.length}.`);
      return;
    }
    try {
      const { KeyRecoveryManager } = await import('../../recovery/KeyRecoveryManager');
      const { deserializeShare } = await import('../../recovery/Shamir');
      const mgr = new KeyRecoveryManager(ctx);
      const shares = rows.slice(0, threshold).map((r) => deserializeShare(r.encryptedShare));
      const seed = mgr.assembleRecovery({ shares, threshold });
      const hex = Array.from(seed).map((b) => b.toString(16).padStart(2, '0')).join('');
      setReconstructedSeed(hex);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  // Phase 53.b: when threshold is first met for a requester, fire reconstruct
  // once. The autoReconstructed gate prevents re-firing on every poll tick
  // and stops auto-runs from racing the user's own manual reconstruct.
  useEffect(() => {
    for (const [requesterPubkey, rows] of heldByRequester.entries()) {
      if (autoReconstructed.has(requesterPubkey)) continue;
      if (rows.length === 0) continue;
      const threshold = rows[0]!.threshold;
      if (rows.length < threshold) continue;
      setAutoReconstructed((prev) => {
        const next = new Set(prev);
        next.add(requesterPubkey);
        return next;
      });
      void reconstruct(rows);
      break;  // one auto-reconstruct per render — user can re-trigger others manually
    }
  /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [heldByRequester]);

  return (
    <EnvFrame title="Recover identity" glyph="⚷" color="var(--phos)" director={director}>
      <div style={{ padding: '4px 0' }}>
        <p className="surface-sub" style={{ marginBottom: 12 }}>
          Shares others hold on your behalf appear here once they release them. Once the threshold is met, the reconstruct button assembles your seed.
        </p>

        {loading && <div className="t-meta" style={{ color: 'var(--ink-dim)' }}>loading…</div>}
        {error !== null && (
          <div className="receipt-mini" style={{ borderColor: 'var(--pink)', marginBottom: 12 }}>
            <div className="t-meta" style={{ color: 'var(--pink)' }}>{error}</div>
          </div>
        )}

        {!loading && heldByRequester.size === 0 && (
          <div className="schematic-frame" style={{ padding: 12, color: 'var(--ink-dim)' }}>
            No shares held yet. When peers release shares to you, they'll appear here grouped by the original setup config.
          </div>
        )}

        {Array.from(heldByRequester.entries()).map(([requesterPubkey, rows]) => {
          const threshold = rows[0]?.threshold ?? 0;
          const total = rows[0]?.totalShares ?? 0;
          const haveEnough = rows.length >= threshold;
          return (
            <div key={requesterPubkey} className="schematic-frame" style={{ padding: 12, marginBottom: 12 }}>
              <div className="t-meta" style={{ color: 'var(--ink-dim)', marginBottom: 6 }}>
                from <code>{requesterPubkey.slice(0, 16)}…</code>
              </div>
              <div style={{ marginBottom: 8, color: 'var(--ink)' }}>
                {rows.length} / {threshold} shares received (of {total} total)
              </div>
              <button
                className="cta"
                disabled={!haveEnough}
                onClick={() => void reconstruct(rows)}
                data-testid="key-recovery-reconstruct"
              >
                {haveEnough ? '⚷ reconstruct seed' : `waiting for ${threshold - rows.length} more`}
              </button>
              {autoReconstructed.has(requesterPubkey) && (
                <div
                  className="t-meta"
                  style={{ marginTop: 6, color: 'var(--phos)' }}
                  data-testid="key-recovery-auto-triggered"
                >
                  ⚡ threshold met — auto-reconstructed (seed shown below)
                </div>
              )}
            </div>
          );
        })}

        {reconstructedSeed !== null && (
          <div className="schematic-frame" style={{ padding: 12, marginTop: 16, borderColor: 'var(--phos)' }}>
            <div className="t-meta env-sublabel" style={{ marginBottom: 6, color: 'var(--phos)' }}>reconstructed seed (hex)</div>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 12, wordBreak: 'break-all', color: 'var(--ink)', userSelect: 'all' }}>
              {reconstructedSeed}
            </div>
            <div className="t-meta" style={{ color: 'var(--ink-dim)', marginTop: 6 }}>
              Copy this to a secure place + immediately set up new social recovery from your new device.
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
          <button className="cta secondary" onClick={() => director.pop('back')}>back</button>
        </div>
      </div>
      <SurfaceStyles />
    </EnvFrame>
  );
}

// ─── Phase 70 — Personal AI helper chat surface ────────────────────────

function PersonalAIChat({ ctx, director }: SurfaceProps): JSX.Element {
  const helper = getPersonalHelper(ctx);
  const [state, setState] = useState<HelperState>(() => helper.state());
  const [messages, setMessages] = useState<readonly PersonalChatMessage[]>(() => helper.messages());
  const [draft, setDraft] = useState<string>('');
  const [streamingTail, setStreamingTail] = useState<string>('');

  useEffect(() => helper.onState(setState), [helper]);
  useEffect(() => helper.onMessages(setMessages), [helper]);

  const isBusy = state.kind === 'loading' || state.kind === 'generating';

  const send = async () => {
    const trimmed = draft.trim();
    if (trimmed === '' || isBusy) return;
    setDraft('');
    setStreamingTail('');
    try {
      await helper.chat(trimmed, (chunk) => setStreamingTail((prev) => prev + chunk));
    } catch {
      /* error state already set by helper */
    } finally {
      setStreamingTail('');
    }
  };

  const tryLoad = () => {
    helper.ensureReady().catch(() => { /* error state set internally */ });
  };

  return (
    <EnvFrame title="your helper" glyph="✦" color="var(--phos)" director={director}>
      <div className="t-meta env-sublabel">
        on-device ai · phase 70 · {helper.config().modelId}
      </div>

      <div className="ai-chat-state" data-testid="ai-chat-state" data-state-kind={state.kind}>
        {state.kind === 'idle' && (
          <div className="schematic-frame" style={{ padding: 12 }}>
            <div className="t-meta" style={{ marginBottom: 8 }}>helper not loaded</div>
            <div style={{ marginBottom: 10, fontSize: 13 }}>
              Downloads ~2.4 GB of model weights on first use. Runs entirely on this device after that — no prompts leave your browser.
            </div>
            <button className="cta" onClick={tryLoad} data-testid="ai-chat-load">
              ⬇ download + start
            </button>
          </div>
        )}
        {state.kind === 'loading' && (
          <div className="schematic-frame" style={{ padding: 12 }}>
            <div className="t-meta" style={{ marginBottom: 8 }}>
              loading · {Math.round(state.progress * 100)}%
            </div>
            <div className="ai-progress-track">
              <div className="ai-progress-fill" style={{ width: `${Math.round(state.progress * 100)}%` }} />
            </div>
            <div className="t-meta dim" style={{ marginTop: 6 }}>{state.status}</div>
          </div>
        )}
        {state.kind === 'error' && (
          <div className="schematic-frame" style={{ padding: 12, borderColor: 'var(--amber)' }}>
            <div className="t-meta" style={{ color: 'var(--amber)', marginBottom: 6 }}>error</div>
            <div style={{ fontSize: 12, marginBottom: 10 }}>{state.message}</div>
            <button className="cta secondary" onClick={tryLoad} data-testid="ai-chat-retry">
              retry
            </button>
          </div>
        )}
        {state.kind === 'unsupported' && (
          <div className="schematic-frame" style={{ padding: 12, borderColor: 'var(--ink-dim)' }}>
            <div className="t-meta" style={{ marginBottom: 6 }}>unsupported browser</div>
            <div style={{ fontSize: 12 }}>{state.reason}</div>
          </div>
        )}
      </div>

      {(state.kind === 'ready' || state.kind === 'generating') && (
        <>
          <div className="ai-chat-log" data-testid="ai-chat-log">
            {messages.length === 0 && streamingTail === '' && (
              <div className="t-meta dim">say hello to start a conversation</div>
            )}
            {messages.map((m, i) => (
              <div key={`${i}-${m.ts}`} className={`ai-msg ai-msg-${m.role}`} data-role={m.role}>
                <div className="t-meta dim ai-msg-role">{m.role}</div>
                <div className="ai-msg-body">{m.content}</div>
              </div>
            ))}
            {streamingTail !== '' && (
              <div className="ai-msg ai-msg-assistant ai-msg-streaming" data-testid="ai-msg-streaming">
                <div className="t-meta dim ai-msg-role">assistant · ◌</div>
                <div className="ai-msg-body">{streamingTail}</div>
              </div>
            )}
          </div>

          <div className="ai-chat-input">
            <textarea
              className="ai-chat-textarea"
              data-testid="ai-chat-textarea"
              placeholder="ask anything…"
              value={draft}
              disabled={isBusy}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  void send();
                }
              }}
              rows={2}
            />
            <button
              className="cta"
              data-testid="ai-chat-send"
              onClick={() => void send()}
              disabled={isBusy || draft.trim() === ''}
            >
              send
            </button>
          </div>
        </>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
        <button className="cta secondary" onClick={() => director.pop('back')}>back</button>
        {(state.kind === 'ready' || state.kind === 'generating') && (
          <button
            className="cta secondary"
            onClick={() => helper.clearMessages()}
            data-testid="ai-chat-clear"
          >
            clear chat
          </button>
        )}
      </div>

      <style>{`
        .ai-chat-state { margin-top: 12px; }
        .ai-progress-track {
          height: 6px; background: var(--bg-3, rgba(255,255,255,0.06));
          border: 1px solid var(--rule); overflow: hidden;
        }
        .ai-progress-fill {
          height: 100%; background: var(--phos);
          transition: width 200ms linear;
        }
        .ai-chat-log {
          margin-top: 14px;
          max-height: 50vh;
          overflow-y: auto;
          display: flex;
          flex-direction: column;
          gap: 10px;
          padding-right: 4px;
        }
        .ai-msg {
          padding: 10px 12px;
          border: 1px solid var(--rule);
          background: var(--bg-2);
        }
        .ai-msg-user { border-left: 2px solid var(--phos); }
        .ai-msg-assistant { border-left: 2px solid var(--amber); }
        .ai-msg-streaming { opacity: 0.85; }
        .ai-msg-role {
          margin-bottom: 4px;
          font-size: 9px;
          letter-spacing: 0.25em;
          text-transform: uppercase;
        }
        .ai-msg-body { white-space: pre-wrap; font-size: 13px; line-height: 1.45; }
        .ai-chat-input {
          display: flex;
          gap: 8px;
          margin-top: 12px;
          align-items: stretch;
        }
        .ai-chat-textarea {
          flex: 1;
          padding: 10px 12px;
          font-family: var(--mono);
          font-size: 12px;
          background: var(--bg-2);
          color: var(--ink);
          border: 1px solid var(--rule);
          resize: vertical;
        }
        .ai-chat-textarea:focus { outline: 1px solid var(--phos); }
      `}</style>
      <SurfaceStyles />
    </EnvFrame>
  );
}

// ─── Phase 73 interim — surface discovery drawer ────────────────────────

/**
 * Human-readable category labels for each surface id. Keeps the
 * discovery drawer readable when ids like "sass-ble-status" don't
 * map to obvious purposes. Unknown ids fall through to a humanized
 * version of their id (split on -, title-case).
 */
const SURFACE_LABELS: Readonly<Record<string, { label: string; group: string; hint?: string }>> = {
  // Dispatch family
  'hub':                 { label: 'Role picker',              group: 'Dispatch',  hint: 'caller / dispatcher / responder / supervisor' },
  'env-intake':          { label: 'Caller intake',            group: 'Dispatch',  hint: 'file an incident anonymously' },
  'env-board':           { label: 'Incident board',           group: 'Dispatch',  hint: 'dispatcher\'s queue' },
  'env-roster':          { label: 'Responder roster',         group: 'Dispatch',  hint: 'my assignments + shift toggle' },
  'env-audit':           { label: 'Audit view',               group: 'Dispatch',  hint: 'supervisor + public transparency' },
  'env-chat':            { label: 'Incident thread',          group: 'Dispatch',  hint: 'per-incident coordination chat' },
  'incident-report':     { label: 'Report incident',          group: 'Dispatch' },
  'triage':              { label: 'Triage',                   group: 'Dispatch' },
  'assign-responder':    { label: 'Assign responder',         group: 'Dispatch' },
  'responder-accept':    { label: 'Accept assignment',        group: 'Dispatch' },
  'responder-status':    { label: 'Responder status',         group: 'Dispatch' },
  'request-backup':      { label: 'Request backup',           group: 'Dispatch' },
  'share-incident-out':  { label: 'Share incident out',       group: 'Dispatch' },
  'attach-evidence':     { label: 'Attach evidence',          group: 'Dispatch' },

  // Chats family
  'sass-chats':          { label: 'Chats',                    group: 'Chats',     hint: 'all DMs + threads' },
  'contact-picker':      { label: 'Pick a contact',           group: 'Chats' },
  'compose-message':     { label: 'New message',              group: 'Chats' },
  'social-dm':           { label: 'Direct message',           group: 'Chats' },

  // Calls (under Chats tab)
  'env-voice':           { label: 'Voice + calls',            group: 'Calls',     hint: 'voice/video hub' },
  'establish-call':      { label: 'Place a call',             group: 'Calls' },
  'call-incoming':       { label: 'Incoming call',            group: 'Calls' },
  'call-active':         { label: 'Active call',              group: 'Calls' },
  'call-history':        { label: 'Call history',             group: 'Calls' },
  'media-controls':      { label: 'Media controls',           group: 'Calls' },
  'group-call-incoming': { label: 'Group call incoming',      group: 'Calls' },
  'group-call-active':   { label: 'Group call active',        group: 'Calls' },
  'call-roster':         { label: 'Call roster',              group: 'Calls' },

  // Feed family
  'env-social':          { label: 'Feed',                     group: 'Feed',      hint: 'social-feed hub' },
  'social-feed':         { label: 'Timeline',                 group: 'Feed' },
  'social-channel':      { label: 'Channel',                  group: 'Feed' },
  'social-broadcast':    { label: 'Broadcast',                group: 'Feed' },
  'social-stories':      { label: 'Stories',                  group: 'Feed' },
  'social-compose':      { label: 'Compose post',             group: 'Feed' },
  'social-gallery':      { label: 'Gallery',                  group: 'Feed' },
  'social-group-config': { label: 'Social group config',      group: 'Feed' },
  'bulletin-config':     { label: 'Bulletin config',          group: 'Feed' },
  'bulletin-published':  { label: 'Published bulletins',      group: 'Feed' },

  // More family
  'sass-more':           { label: 'More',                     group: 'More',      hint: 'settings + admin' },
  'sass-surface-map':    { label: 'All surfaces (this view)', group: 'More',      hint: 'where you are now' },
  'sass-shell-chooser':  { label: 'Choose your shell',        group: 'More',      hint: 'adult / kids / teens reskin' },
  'network-config':      { label: 'Network config',           group: 'More' },
  'key-storage-config':  { label: 'Key storage',              group: 'More' },
  'plugin-marketplace':  { label: 'Plugin marketplace',       group: 'More' },
  'plugin-picker':       { label: 'Plugin picker',            group: 'More' },
  'audit-anchor':        { label: 'Audit anchor',             group: 'More' },
  'sass-audit-verify':   { label: 'Verify audit chain',       group: 'More' },
  'export-bundle':       { label: 'Export account',           group: 'More' },
  'import-bundle':       { label: 'Import account',           group: 'More' },
  'treaty-builder':      { label: 'Federation treaty',        group: 'More' },
  'federated-search':    { label: 'Federated search',         group: 'More' },
  'env-vault':           { label: 'Personal vault',           group: 'More',      hint: 'photos / voice notes / files' },

  // Mesh / sneakernet (under More)
  'mesh-invite-generate': { label: 'Generate mesh invite',    group: 'Mesh' },
  'mesh-invite-redeem':   { label: 'Redeem mesh invite',      group: 'Mesh' },
  'sneakernet':           { label: 'Sneakernet handoff',      group: 'Mesh' },
  'qr-handoff':           { label: 'QR handoff',              group: 'Mesh' },

  // SASS extras (under More)
  'sass-prepublish-vote':      { label: 'Prepublish vote queue',  group: 'Moderation' },
  'sass-ble-status':           { label: 'Bluetooth mesh status',  group: 'Moderation' },
  'sass-sms-subscribers':      { label: 'SMS subscribers',        group: 'Moderation' },
  'sass-key-recovery-setup':   { label: 'Set up key recovery',    group: 'Recovery' },
  'sass-key-recovery-restore': { label: 'Restore key recovery',   group: 'Recovery' },

  // AI (FAB-accessible)
  'personal-ai-chat':    { label: 'Personal AI helper',       group: 'Helper',    hint: 'on-device WebLLM · also reachable via the ✦ helper button' },

  // News (Phase 69.A — also reachable via the top-of-screen ticker)
  'news-paper':          { label: 'News paper',               group: 'News',      hint: 'curated local headlines · operator publish form · phase 69.A' },
};

const GROUP_ORDER: readonly string[] = ['Dispatch', 'Chats', 'Calls', 'Feed', 'News', 'More', 'Mesh', 'Moderation', 'Recovery', 'Helper', 'Other'];

function humanizeId(id: string): string {
  return id.replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function SASSSurfaceMap({ ctx, director }: SurfaceProps): JSX.Element {
  const [filter, setFilter] = useState<string>('');
  const all = ctx.surfaces.all();
  const fold = filter.trim().toLowerCase();
  const groups = new Map<string, Array<{ id: string; label: string; hint: string | undefined }>>();
  for (const def of all) {
    const meta = SURFACE_LABELS[def.id];
    const group = meta?.group ?? 'Other';
    const label = meta?.label ?? humanizeId(def.id);
    const hint = meta?.hint;
    if (fold !== '' && !label.toLowerCase().includes(fold) && !def.id.toLowerCase().includes(fold) && !(hint ?? '').toLowerCase().includes(fold)) {
      continue;
    }
    let row = groups.get(group);
    if (row === undefined) { row = []; groups.set(group, row); }
    row.push({ id: def.id, label, hint });
  }
  const orderedGroups = [...GROUP_ORDER, ...[...groups.keys()].filter((g) => !GROUP_ORDER.includes(g))];
  const visible = orderedGroups.filter((g) => groups.has(g) && (groups.get(g)?.length ?? 0) > 0);
  const total = all.length;
  const matched = [...groups.values()].reduce((n, rows) => n + rows.length, 0);

  return (
    <EnvFrame title="all surfaces" glyph="≡" color="var(--phos)" director={director}>
      <div className="t-meta env-sublabel">
        phase 73 interim · discovery drawer · {matched}/{total} surfaces
      </div>

      <div style={{ margin: '12px 0' }}>
        <input
          type="text"
          className="surface-map-search"
          placeholder="search surfaces…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          data-testid="surface-map-search"
        />
      </div>

      {visible.length === 0 && (
        <div className="t-meta dim">no surfaces match "{filter}"</div>
      )}

      {visible.map((group) => {
        const rows = groups.get(group) ?? [];
        return (
          <section key={group} className="surface-map-group">
            <div className="t-meta surface-map-group-title">{group} · {rows.length}</div>
            <ul className="surface-map-list">
              {rows.map((row) => (
                <li key={row.id} className="surface-map-row">
                  <button
                    className="surface-map-bridge"
                    data-testid={`surface-map-bridge-${row.id}`}
                    onClick={() => director.bridge(row.id, `surface-map → ${row.id}`)}
                  >
                    <span className="surface-map-label">{row.label}</span>
                    <span className="surface-map-id">{row.id}</span>
                    {row.hint !== undefined && (
                      <span className="surface-map-hint">{row.hint}</span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          </section>
        );
      })}

      <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
        <button className="cta secondary" onClick={() => director.pop('back')}>back</button>
      </div>

      <style>{`
        .surface-map-search {
          width: 100%;
          padding: 8px 10px;
          background: var(--bg-2);
          color: var(--ink);
          border: 1px solid var(--rule);
          font-family: var(--mono);
          font-size: 12px;
        }
        .surface-map-search:focus { outline: 1px solid var(--phos); }
        .surface-map-group { margin-top: 18px; }
        .surface-map-group-title {
          color: var(--phos);
          letter-spacing: 0.25em;
          text-transform: uppercase;
          margin-bottom: 8px;
        }
        .surface-map-list {
          list-style: none;
          padding: 0;
          margin: 0;
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
          gap: 6px;
        }
        .surface-map-row { margin: 0; padding: 0; }
        .surface-map-bridge {
          width: 100%;
          text-align: left;
          padding: 8px 10px;
          background: var(--bg-2);
          color: var(--ink);
          border: 1px solid var(--rule);
          cursor: pointer;
          display: flex;
          flex-direction: column;
          gap: 2px;
        }
        .surface-map-bridge:hover { border-color: var(--phos); }
        .surface-map-label { font-size: 13px; }
        .surface-map-id {
          font-family: var(--mono);
          font-size: 10px;
          color: var(--ink-dim);
          letter-spacing: 0.04em;
        }
        .surface-map-hint { font-size: 11px; color: var(--ink-dim); margin-top: 2px; }
      `}</style>
      <SurfaceStyles />
    </EnvFrame>
  );
}

// ─── Phase 71 partial — personal moderation mascot drawer ───────────────

const MASCOT_CATEGORY_LABELS: Readonly<Record<MascotCategory, { label: string; desc: string }>> = {
  violence:        { label: 'Violence',        desc: 'fight / weapons / attacks' },
  sexual:          { label: 'Sexual',          desc: 'explicit / nsfw content' },
  doxing:          { label: 'Doxing',          desc: 'addresses, phones, SSNs' },
  hate:            { label: 'Hate speech',     desc: 'slurs / genocidal language' },
  'kid-safety':    { label: 'Kid safety',      desc: 'grooming patterns + school grade references' },
  'location-leak': { label: 'Location leak',   desc: '"I live at" patterns + street addresses' },
  'scam-pattern':  { label: 'Scam pattern',    desc: 'wire / crypto / urgent-payment language' },
};

function SASSMascot({ ctx, director }: SurfaceProps): JSX.Element {
  const mascot = getMascotManager(ctx);
  const [settings, setSettings] = useState<MascotSettings>(() => mascot.settings());
  const [log, setLog] = useState<readonly FilterLogEntry[]>(() => mascot.log());
  const [state, setStateVal] = useState<MascotState>(() => mascot.state());
  const [pin, setPin] = useState('');
  const [pinError, setPinError] = useState<string | null>(null);
  const [tryText, setTryText] = useState('');

  useEffect(() => mascot.onSettings(setSettings), [mascot]);
  useEffect(() => mascot.onLog(setLog), [mascot]);
  useEffect(() => mascot.onState(setStateVal), [mascot]);

  const kidLocked = settings.kidMode.enabled;
  const today = new Date().toISOString().slice(0, 10);
  const todayCount = log.filter((e) => e.ts.slice(0, 10) === today).length;

  const setAction = (cat: MascotCategory, action: MascotAction) => {
    try {
      mascot.setCategoryAction(cat, action);
    } catch (err) {
      setPinError(err instanceof Error ? err.message : String(err));
    }
  };

  const enableKid = async () => {
    setPinError(null);
    try {
      await mascot.enableKidMode(pin);
      setPin('');
    } catch (err) {
      setPinError(err instanceof Error ? err.message : String(err));
    }
  };

  const disableKid = async () => {
    setPinError(null);
    const ok = await mascot.disableKidMode(pin);
    if (!ok) setPinError('PIN does not match');
    else setPin('');
  };

  const tryEvaluate = () => {
    if (tryText.trim() === '') return;
    const hash = `try-${Date.now()}`;
    mascot.evaluate(tryText, { contentHash: hash });
    setTryText('');
  };

  return (
    <EnvFrame title="mascot" glyph="◉" color="var(--phos)" director={director}>
      <div className="t-meta env-sublabel">
        phase 71 partial · keyword-based L1 filter · state: {state.kind}
      </div>

      <div className="schematic-frame" style={{ padding: 12, marginTop: 12 }}>
        <div className="t-meta" style={{ marginBottom: 6 }}>today's activity</div>
        <div style={{ fontSize: 13 }}>
          <strong>{todayCount}</strong> filter event{todayCount === 1 ? '' : 's'} on {today}
          {' · '}
          <strong>{log.length}</strong> total in rolling buffer
        </div>
      </div>

      <section style={{ marginTop: 18 }}>
        <div className="t-meta env-sublabel" style={{ marginBottom: 8 }}>
          per-category action {kidLocked ? '· LOCKED by kid-mode' : ''}
        </div>
        <ul className="mascot-cat-list">
          {MASCOT_CATEGORIES.map((cat) => {
            const meta = MASCOT_CATEGORY_LABELS[cat];
            const current = settings.perCategory[cat];
            return (
              <li key={cat} className="mascot-cat-row" data-testid={`mascot-cat-${cat}`}>
                <div className="mascot-cat-meta">
                  <div className="mascot-cat-label">{meta.label}</div>
                  <div className="mascot-cat-desc">{meta.desc}</div>
                  <div className="mascot-cat-keywords">{defaultKeywordsFor(cat).slice(0, 3).join(' · ')}…</div>
                </div>
                <div className="mascot-cat-actions">
                  {(['pass', 'blur', 'hide'] as MascotAction[]).map((a) => (
                    <button
                      key={a}
                      className={`mascot-action mascot-action-${a} ${current === a ? 'mascot-action-on' : ''}`}
                      data-testid={`mascot-action-${cat}-${a}`}
                      disabled={kidLocked}
                      onClick={() => setAction(cat, a)}
                    >
                      {a}
                    </button>
                  ))}
                </div>
              </li>
            );
          })}
        </ul>
      </section>

      <section style={{ marginTop: 18 }}>
        <div className="t-meta env-sublabel" style={{ marginBottom: 8 }}>
          kid-mode lock {kidLocked ? '· ENABLED' : ''}
        </div>
        <div className="schematic-frame" style={{ padding: 12 }}>
          {!kidLocked && (
            <>
              <div style={{ fontSize: 12, marginBottom: 8 }}>
                Lock all categories to <strong>hide</strong>. Unlock requires the PIN you set here.
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  type="password"
                  className="surface-map-search"
                  placeholder="set a PIN (4+ chars)"
                  value={pin}
                  onChange={(e) => setPin(e.target.value)}
                  data-testid="mascot-kid-pin"
                  style={{ flex: 1 }}
                />
                <button className="cta" onClick={() => void enableKid()} data-testid="mascot-kid-enable">
                  ⚷ enable kid-mode
                </button>
              </div>
            </>
          )}
          {kidLocked && (
            <>
              <div style={{ fontSize: 12, marginBottom: 8, color: 'var(--amber)' }}>
                Kid-mode is on. All categories are forced to <strong>hide</strong>. Enter PIN to unlock.
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  type="password"
                  className="surface-map-search"
                  placeholder="enter PIN"
                  value={pin}
                  onChange={(e) => setPin(e.target.value)}
                  data-testid="mascot-kid-pin"
                  style={{ flex: 1 }}
                />
                <button className="cta secondary" onClick={() => void disableKid()} data-testid="mascot-kid-disable">
                  ⚷ unlock
                </button>
              </div>
            </>
          )}
          {pinError !== null && (
            <div className="t-meta" style={{ color: 'var(--amber)', marginTop: 8 }}>
              {pinError}
            </div>
          )}
        </div>
      </section>

      <section style={{ marginTop: 18 }}>
        <div className="t-meta env-sublabel" style={{ marginBottom: 8 }}>
          try it out
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <textarea
            className="ai-chat-textarea"
            placeholder="paste some text to see what the mascot would flag…"
            value={tryText}
            onChange={(e) => setTryText(e.target.value)}
            data-testid="mascot-try-text"
            rows={2}
            style={{ flex: 1 }}
          />
          <button
            className="cta"
            onClick={tryEvaluate}
            data-testid="mascot-try-eval"
            disabled={tryText.trim() === ''}
          >
            evaluate
          </button>
        </div>
      </section>

      <section style={{ marginTop: 18 }}>
        <div className="t-meta env-sublabel" style={{ marginBottom: 8 }}>
          what got filtered ({log.length})
        </div>
        {log.length === 0 ? (
          <div className="t-meta dim">nothing yet</div>
        ) : (
          <ul className="mascot-log-list" data-testid="mascot-log">
            {[...log].slice(-12).reverse().map((entry, i) => (
              <li key={`${entry.ts}-${i}`} className="mascot-log-row">
                <span className={`mascot-log-action mascot-log-action-${entry.action}`}>{entry.action}</span>
                <span className="mascot-log-category">{entry.category}</span>
                <span className="mascot-log-terms">{entry.matchedTerms.join(', ')}</span>
                <span className="mascot-log-ts">{entry.ts.slice(11, 19)}Z</span>
              </li>
            ))}
          </ul>
        )}
        {log.length > 0 && (
          <button
            className="cta secondary"
            onClick={() => mascot.clearLog()}
            data-testid="mascot-log-clear"
            style={{ marginTop: 8 }}
          >
            clear log
          </button>
        )}
      </section>

      <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
        <button
          className="cta secondary"
          onClick={() => { mascot.dismiss(); director.pop('back'); }}
        >
          back
        </button>
      </div>

      <style>{`
        .mascot-cat-list { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 6px; }
        .mascot-cat-row {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 8px 10px;
          background: var(--bg-2);
          border: 1px solid var(--rule);
        }
        .mascot-cat-meta { flex: 1; min-width: 0; }
        .mascot-cat-label { font-size: 13px; }
        .mascot-cat-desc { font-size: 11px; color: var(--ink-dim); margin-top: 2px; }
        .mascot-cat-keywords {
          font-family: var(--mono);
          font-size: 10px;
          color: var(--ink-dim);
          margin-top: 2px;
          opacity: 0.7;
        }
        .mascot-cat-actions { display: flex; gap: 4px; flex-shrink: 0; }
        .mascot-action {
          padding: 4px 8px;
          font-size: 10px;
          font-family: var(--mono);
          background: var(--bg-2);
          color: var(--ink-dim);
          border: 1px solid var(--rule);
          cursor: pointer;
          letter-spacing: 0.18em;
          text-transform: uppercase;
        }
        .mascot-action:disabled { opacity: 0.4; cursor: not-allowed; }
        .mascot-action-on { color: var(--phos); border-color: var(--phos); }
        .mascot-action-hide.mascot-action-on { color: var(--amber); border-color: var(--amber); }
        .mascot-log-list { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 4px; }
        .mascot-log-row {
          display: grid;
          grid-template-columns: 50px 110px 1fr 60px;
          gap: 8px;
          padding: 4px 6px;
          font-size: 11px;
          background: var(--bg-2);
          border: 1px solid var(--rule);
        }
        .mascot-log-action {
          font-family: var(--mono); text-transform: uppercase; font-size: 9px;
          letter-spacing: 0.18em; padding: 1px 4px; align-self: center;
        }
        .mascot-log-action-hide { color: var(--amber); }
        .mascot-log-action-blur { color: var(--phos); }
        .mascot-log-action-pass { color: var(--ink-dim); }
        .mascot-log-category { font-family: var(--mono); align-self: center; }
        .mascot-log-terms { color: var(--ink-dim); align-self: center; overflow: hidden; text-overflow: ellipsis; }
        .mascot-log-ts { font-family: var(--mono); color: var(--ink-dim); font-size: 9px; align-self: center; text-align: right; }
      `}</style>
      <SurfaceStyles />
    </EnvFrame>
  );
}

// ─── Phase 69.A — news-paper digital newspaper surface ──────────────────

function NewsPaper({ ctx, director }: SurfaceProps): JSX.Element {
  const manager = getNewsHeadlinesManager(ctx);
  const [activeRows, setActiveRows] = useState<readonly NewsHeadlineRecord[]>([]);
  const [allRows, setAllRows] = useState<readonly NewsHeadlineRecord[]>([]);
  const [filter, setFilter] = useState<'active' | 'all'>('active');

  // Publish-form state
  const [title, setTitle] = useState('');
  const [source, setSource] = useState('');
  const [url, setUrl] = useState('');
  const [summary, setSummary] = useState('');
  const [publishErr, setPublishErr] = useState<string | null>(null);

  useEffect(() => manager.onActiveChange(setActiveRows), [manager]);
  useEffect(() => manager.onAllChange(setAllRows), [manager]);

  const rows = filter === 'active' ? activeRows : allRows;
  const grouped = groupByDay(rows);

  const publish = async () => {
    setPublishErr(null);
    try {
      const input: { title: string; source: string; url?: string; summary?: string } = {
        title,
        source,
      };
      if (url.trim() !== '') input.url = url;
      if (summary.trim() !== '') input.summary = summary;
      await manager.publish(input);
      setTitle('');
      setSource('');
      setUrl('');
      setSummary('');
    } catch (err) {
      setPublishErr(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <EnvFrame title="news paper" glyph="▣" color="var(--phos)" director={director}>
      <div className="t-meta env-sublabel">
        phase 69.A · local headlines · {activeRows.length} active · {allRows.length} total
      </div>

      <section style={{ marginTop: 14 }}>
        <div className="t-meta env-sublabel" style={{ marginBottom: 8 }}>publish a headline</div>
        <div className="schematic-frame newspaper-publish">
          <input
            className="newspaper-input"
            type="text"
            placeholder="headline (required)"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            data-testid="news-publish-title"
          />
          <div className="newspaper-publish-row">
            <input
              className="newspaper-input"
              type="text"
              placeholder="source (e.g. AP, Hyperlocal Gazette)"
              value={source}
              onChange={(e) => setSource(e.target.value)}
              data-testid="news-publish-source"
              style={{ flex: 1 }}
            />
            <input
              className="newspaper-input"
              type="url"
              placeholder="https://… (optional)"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              data-testid="news-publish-url"
              style={{ flex: 2 }}
            />
          </div>
          <textarea
            className="newspaper-input"
            placeholder="one-line summary (optional)"
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
            data-testid="news-publish-summary"
            rows={2}
          />
          <div className="newspaper-publish-row">
            <button
              className="cta"
              onClick={() => void publish()}
              disabled={title.trim() === '' || source.trim() === ''}
              data-testid="news-publish-submit"
            >
              ▣ publish
            </button>
            {publishErr !== null && (
              <span className="t-meta" style={{ color: 'var(--amber)', alignSelf: 'center' }}>
                {publishErr}
              </span>
            )}
          </div>
        </div>
      </section>

      <section style={{ marginTop: 18 }}>
        <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
          <button
            className={`pill ${filter === 'active' ? 'pill-on' : ''}`}
            onClick={() => setFilter('active')}
            data-testid="news-filter-active"
          >
            active ({activeRows.length})
          </button>
          <button
            className={`pill ${filter === 'all' ? 'pill-on' : ''}`}
            onClick={() => setFilter('all')}
            data-testid="news-filter-all"
          >
            all ({allRows.length})
          </button>
        </div>

        {rows.length === 0 && (
          <div className="t-meta dim">no headlines yet — publish one above</div>
        )}

        {grouped.map(([day, dayRows]) => (
          <div key={day} className="newspaper-day">
            <div className="newspaper-day-rule">{day}</div>
            <div className="newspaper-grid">
              {dayRows.map((row) => (
                <article
                  key={row.id}
                  className={`newspaper-card ${row.dismissedAt !== null ? 'newspaper-card-dismissed' : ''}`}
                  data-testid={`news-card-${row.id}`}
                >
                  <div className="newspaper-card-source">{row.source}</div>
                  <div className="newspaper-card-title">
                    {row.url !== undefined ? (
                      <a href={row.url} target="_blank" rel="noopener noreferrer">{row.title}</a>
                    ) : row.title}
                  </div>
                  {row.summary !== undefined && (
                    <div className="newspaper-card-summary">{row.summary}</div>
                  )}
                  <div className="newspaper-card-meta">
                    <span>{row.publishedAt.slice(11, 16)}Z</span>
                    {row.dismissedAt === null ? (
                      <button
                        className="newspaper-act"
                        onClick={() => void manager.dismiss(row.id)}
                        data-testid={`news-dismiss-${row.id}`}
                      >
                        dismiss
                      </button>
                    ) : (
                      <button
                        className="newspaper-act"
                        onClick={() => void manager.restore(row.id)}
                        data-testid={`news-restore-${row.id}`}
                      >
                        restore
                      </button>
                    )}
                    <button
                      className="newspaper-act newspaper-act-warn"
                      onClick={() => void manager.remove(row.id)}
                      data-testid={`news-remove-${row.id}`}
                    >
                      remove
                    </button>
                  </div>
                </article>
              ))}
            </div>
          </div>
        ))}
      </section>

      <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
        <button className="cta secondary" onClick={() => director.pop('back')}>back</button>
      </div>

      <style>{`
        .newspaper-publish {
          padding: 12px;
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        .newspaper-publish-row {
          display: flex;
          gap: 8px;
          align-items: stretch;
        }
        .newspaper-input {
          padding: 8px 10px;
          font-family: var(--mono);
          font-size: 12px;
          background: var(--bg-2);
          color: var(--ink);
          border: 1px solid var(--rule);
          resize: vertical;
        }
        .newspaper-input:focus { outline: 1px solid var(--phos); }
        .newspaper-day-rule {
          font-family: var(--mono);
          font-size: 10px;
          color: var(--phos);
          letter-spacing: 0.22em;
          text-transform: uppercase;
          border-bottom: 1px solid var(--rule);
          padding-bottom: 4px;
          margin-bottom: 10px;
        }
        .newspaper-day { margin-bottom: 18px; }
        .newspaper-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
          gap: 10px;
        }
        .newspaper-card {
          padding: 12px 14px;
          background: var(--bg-2);
          border: 1px solid var(--rule);
          display: flex;
          flex-direction: column;
          gap: 6px;
        }
        .newspaper-card-dismissed { opacity: 0.55; }
        .newspaper-card-source {
          font-family: var(--mono);
          font-size: 10px;
          letter-spacing: 0.22em;
          text-transform: uppercase;
          color: var(--phos);
        }
        .newspaper-card-title {
          font-family: var(--serif, var(--font-serif, serif));
          font-size: 16px;
          line-height: 1.3;
        }
        .newspaper-card-title a { color: inherit; text-decoration: none; border-bottom: 1px dotted var(--phos); }
        .newspaper-card-title a:hover { color: var(--phos); }
        .newspaper-card-summary {
          font-size: 12px;
          color: var(--ink-dim);
          line-height: 1.4;
        }
        .newspaper-card-meta {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 10px;
          color: var(--ink-dim);
          font-family: var(--mono);
          margin-top: 4px;
        }
        .newspaper-act {
          background: none;
          border: 1px solid var(--rule);
          color: var(--ink-dim);
          padding: 2px 6px;
          font-family: var(--mono);
          font-size: 9px;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          cursor: pointer;
        }
        .newspaper-act:hover { color: var(--phos); border-color: var(--phos); }
        .newspaper-act-warn:hover { color: var(--amber); border-color: var(--amber); }
        .pill {
          padding: 4px 10px;
          font-family: var(--mono);
          font-size: 10px;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          background: var(--bg-2);
          color: var(--ink-dim);
          border: 1px solid var(--rule);
          cursor: pointer;
        }
        .pill-on { color: var(--phos); border-color: var(--phos); }
      `}</style>
      <SurfaceStyles />
    </EnvFrame>
  );
}

function groupByDay(rows: readonly NewsHeadlineRecord[]): Array<[string, NewsHeadlineRecord[]]> {
  const map = new Map<string, NewsHeadlineRecord[]>();
  for (const row of rows) {
    const day = row.publishedAt.slice(0, 10);
    let list = map.get(day);
    if (list === undefined) { list = []; map.set(day, list); }
    list.push(row);
  }
  return [...map.entries()].sort((a, b) => (a[0] < b[0] ? 1 : -1));
}

